'use client'

import { MediaNodeResizeHandle, useMediaNodeResize } from './media-node-resize'
import { Tooltip } from 'antd'
import { MediaButton, MediaDropdown, MediaModal } from './ui'
import {
  cloneElement,
  isValidElement,
  useEffect,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEventHandler,
  type PointerEvent,
  type ReactNode,
} from 'react'
import { canConnectMediaModalities } from './media-flow-domain'
import {
  buildCanvasComponentSurfaces,
  type MediaCanvasNodeOption,
  MEDIA_BUILTIN_CREATABLE_OPTIONS as CREATABLE_NODE_OPTIONS,
  MEDIA_BUILTIN_NODE_OPTIONS as NODE_OPTIONS,
} from './media-canvas-components'
import type {
  MediaCanvasController,
  MediaCanvasNode,
  MediaCanvasPanel,
  MediaCanvasPoint,
  MediaCanvasViewport,
  MediaConnectionFailure,
  MediaConnectionResult,
  MediaCreateIntent,
  MediaNodeRuntime,
} from './media-canvas-controller'
import { isPrivateMediaResourceAccessURL, type MediaCanvasComponentSummary, type MediaModality, type MediaProjectScope } from './media-user-contract'
import type { MediaNodeCreationMethod, MediaTelemetry } from './media-telemetry'
import { AUDIO_NODE_CARD_SIZE, MediaNodeStyles, mediaNodeFrameGeometry } from './media-node-components'
import { MediaWorkspaceTitle } from './media-workspace-title'
import { MediaElementGroupHeader } from './components/MediaElementGroupHeader'
import { MediaComponentIcon, MediaCreationIcon } from './media-icons'
import { MediaCanvasMiniMap } from './media-canvas-minimap'
import type { MediaCanvasGroup, MediaCanvasNodeBoundsMap } from './media-canvas-groups'
import { MediaCanvasContextMenu, MediaCanvasGroupFrame, MediaCanvasGroupStyles, resizeMediaCanvasGroup, type MediaGroupResizeHandle } from './media-canvas-group-components'

import { MEDIA_CANVAS_MIN_ZOOM as MIN_ZOOM, MEDIA_CANVAS_MAX_ZOOM as MAX_ZOOM } from './media-canvas-limits'
import { useMediaCanvasPreferences } from './media-canvas-preferences'
import { mediaCanvasDragPositions, mediaCanvasWheelViewport } from './media-canvas-interaction'

import { MEDIA_UPLOAD_ACCEPT, MEDIA_UPLOAD_FORMAT_HINT, isSupportedMediaDropFile as isSupportedDropFile } from './media-upload'

const DROP_FILE_HINT = `请拖入 ${MEDIA_UPLOAD_FORMAT_HINT}`

// The smallest 10px node labels reach 6px before raster scaling takes over.
const MIN_LAYOUT_ZOOM = 0.6
const MIN_SIDEBAR_WIDTH = 300
const MAX_SIDEBAR_WIDTH = 530
const NODE_WIDTH = 600
const NODE_HEIGHT = 500
const PORT_SCREEN_HIT_SIZE = 36
const PORT_HOVER_HALF_WIDTH_SCREEN = 32
const PORT_HOVER_HALF_HEIGHT_SCREEN = 56
/** Screen-pixel snap radius around the target card border or input button. */
const PORT_CONNECT_RADIUS_SCREEN = 10
const FIT_PADDING = 80
/** How long a rejected drag-to-connect hint stays on screen. */
const CONNECTION_HINT_VISIBLE_MS = 4500
const FALLBACK_VIEWPORT_SIZE: MediaCanvasViewportSize = { width: 1200, height: 720 }
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const CONNECTION_PORT_CURSOR = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="20" height="20"><path fill="#ffffff" d="M512 42.666667a469.333333 469.333333 0 1 1 0 938.666666 469.333333 469.333333 0 0 1 0-938.666666z m-42.666667 87.637333A384.170667 384.170667 0 0 0 130.304 469.333333H234.666667a42.666667 42.666667 0 0 1 0 85.333334H130.304A384.170667 384.170667 0 0 0 469.333333 893.696V789.333333a42.666667 42.666667 0 0 1 85.333334 0v104.362667A384.170667 384.170667 0 0 0 893.696 554.666667H768a42.666667 42.666667 0 0 1 0-85.333334h125.696A384.170667 384.170667 0 0 0 554.666667 130.304V234.666667a42.666667 42.666667 0 0 1-85.333334 0V130.304z"/></svg>')}") 10 10, crosshair`
const EDGE_CUT_CURSOR = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="32" height="32"><path fill="#ffffff" d="M896 149.333333a61.013333 61.013333 0 0 0-85.333333-4.693333l-323.626667 322.133333-94.506667-94.506666a149.333333 149.333333 0 1 0-51.2 39.68L441.813333 512 341.333333 612.053333a149.333333 149.333333 0 1 0 50.773334 39.68l94.506666-94.506666L810.666667 880.426667a61.013333 61.013333 0 0 0 85.333333-4.693334L533.333333 512z m-548.266667 198.4a99.626667 99.626667 0 1 1 0-140.8 99.626667 99.626667 0 0 1 0 140.8z m0 469.333334a99.626667 99.626667 0 1 1 0-140.8 99.626667 99.626667 0 0 1 0 140.8z"/></svg>')}") 16 16, crosshair`

// Product decision 2026-08-29: every creation surface (empty-canvas shortcuts,
// double-click/keyboard selector, output-drag drop selector) offers all four
// modalities by default. NODE_OPTIONS and friends (imported from
// media-canvas-components) are the built-in presentation contract for
// persisted nodes and the fallback catalog; the workspace merges the
// administrator-managed component catalog over them at render time.
const SELECTOR_HEIGHT = 362

const PANEL_OPTIONS: readonly { readonly panel: Exclude<MediaCanvasPanel, 'none'>; readonly label: string }[] = [
  { panel: 'elements', label: '画布' },
  { panel: 'assets', label: '资产' },
  { panel: 'history', label: '历史' },
]

export type MediaCanvasZoomTier = 'full' | 'medium' | 'simplified' | 'overview'

export interface MediaCanvasViewportSize {
  readonly width: number
  readonly height: number
}

export interface MediaCanvasNodePresentation {
  readonly component?: MediaCanvasNodeOption
  readonly zoomTier: MediaCanvasZoomTier
  readonly promptInputFixedScale: boolean
  readonly selectionChromeHidden?: boolean
  readonly zoomAllowsInteraction: boolean
  readonly editable: boolean
  readonly selected: boolean
  readonly editing: boolean
  readonly runtime?: MediaNodeRuntime
  readonly requestEdit: () => void
  readonly closeEditor: () => void
  readonly requestCenter?: () => void
  readonly locateReference?: (sourceNodeId: string) => void
  readonly fileDropHover?: boolean
}

export interface MediaCanvasProjectSwitcherItem {
  readonly uuid: string
  readonly name: string
  readonly scope: MediaProjectScope
}

/**
 * The route layer owns the ACL-filtered project query and navigation. The
 * canvas only receives this narrow, already-authorized display model so it
 * remains reusable for static/embedded workspace callers.
 */
export interface MediaCanvasProjectSwitcher {
  readonly currentProjectUuid: string
  readonly currentProject?: MediaCanvasProjectSwitcherItem
  readonly projects: readonly MediaCanvasProjectSwitcherItem[]
  readonly status: 'loading' | 'ready' | 'error'
  readonly onReturnToHome: () => void
  readonly onOpenProject: (projectUuid: string) => void
  readonly onRetry?: () => void
  readonly onCreateProject?: () => Promise<void>
}

export interface MediaCanvasWorkspaceProps {
  readonly referenceTargetNodeId?: string
  readonly onExitReferenceSelection?: () => void
  readonly onReferenceConnected?: (targetNodeId: string) => void
  readonly preferenceNamespace?: string
  readonly controller: MediaCanvasController
  /**
   * Administrator-managed canvas component catalog (super-admin「组件管理」).
   * `undefined` keeps every built-in creation surface verbatim; entries merge
   * copy/icon/order over the defaults and `disabled` rows hide that modality's
   * creation surfaces. Persisted nodes always keep rendering.
   */
  readonly nodeComponents?: readonly MediaCanvasComponentSummary[]
  readonly onRenameProject?: (name: string) => Promise<boolean>
  readonly onSendToAgent?: (nodeId: string) => void
  readonly onSaveNodeAsset?: (nodeId: string) => void
  readonly projectName?: string
  readonly projectSwitcher?: MediaCanvasProjectSwitcher
  readonly saveStatus?: '已保存' | '保存中' | '离线编辑' | '保存失败' | '有冲突'
  readonly readOnly?: boolean
  readonly viewportSize?: MediaCanvasViewportSize
  readonly panelContent?: Readonly<Partial<Record<Exclude<MediaCanvasPanel, 'none'>, ReactNode>>>
  readonly renderNode?: (node: MediaCanvasNode, presentation: MediaCanvasNodePresentation) => ReactNode
  /** View-only URLs stay outside the persisted canvas document. */
  readonly getElementPreviewUrl?: (node: MediaCanvasNode) => string | undefined
  readonly onCreateIntent?: (intent: MediaCreateIntent) => void
  readonly onDropFiles?: (files: readonly File[], point: MediaCanvasPoint, targetNodeId?: string) => void
  /**
   * A project workspace may temporarily be read-only while it reacquires an
   * edit lease. Supplying this callback marks that state as recoverable; the
   * requested empty-canvas action is replayed only after the parent renders
   * the canvas editable again.
   */
  readonly onRestoreEditing?: () => void | Promise<void>
  readonly telemetry?: MediaTelemetry
}

type PendingEditAction =
  | { readonly kind: 'create'; readonly modality: MediaModality; readonly point: MediaCanvasPoint; readonly method: MediaNodeCreationMethod }
  | { readonly kind: 'selector'; readonly intent: MediaCreateIntent }

interface CanvasPointerLike {
  readonly clientX: number
  readonly clientY: number
  readonly currentTarget: { getBoundingClientRect(): { readonly left: number; readonly top: number } }
}

interface PendingOutputDrag {
  readonly sourceNodeId: string
  readonly sourcePort: 'input' | 'output'
  readonly pointerId?: number
  readonly captureTarget?: PointerCaptureTarget
  readonly cardBounds: Map<string, NodeCardBounds>
}

interface OutputDragPreview {
  readonly sourcePort: 'input' | 'output'
  readonly sourceNodeId: string
  readonly point: MediaCanvasPoint
  readonly hoverNodeId?: string
}

interface PointerCaptureTarget {
  setPointerCapture?(pointerId: number): void
  hasPointerCapture?(pointerId: number): boolean
  releasePointerCapture?(pointerId: number): void
}

interface PanGesture {
  readonly pointerId: number
  readonly mode: 'middle' | 'space' | 'right' | 'left'
  moved: boolean
  readonly startClientX: number
  readonly startClientY: number
  readonly startViewport: MediaCanvasViewport
  readonly captureTarget: PointerCaptureTarget
}

interface NodeDragGesture {
  readonly pointerId: number
  readonly nodeId: string
  readonly startClientX: number
  readonly startClientY: number
  readonly startPositions: Readonly<Record<string, MediaCanvasPoint>>
  readonly startGroupPositions: Readonly<Record<string, MediaCanvasPoint>>
  readonly reassignNodeIds: readonly string[]
  readonly bounds: MediaCanvasNodeBoundsMap
  readonly zoom: number
  readonly captureTarget: PointerCaptureTarget
}

type NodeDragPreview = Readonly<Record<string, MediaCanvasPoint>>
interface GroupResizeGesture {
  readonly pointerId: number
  readonly group: MediaCanvasGroup
  readonly handle: MediaGroupResizeHandle
  readonly startClient: MediaCanvasPoint
  readonly zoom: number
  readonly captureTarget: PointerCaptureTarget
}

interface SelectionBoxGesture {
  readonly pointerId: number
  readonly start: MediaCanvasPoint
  readonly initialSelection: readonly string[]
  readonly initialGroupSelection: readonly string[]
  readonly additive: boolean
  readonly captureTarget: PointerCaptureTarget
}

interface SelectionBox {
  readonly left: number
  readonly top: number
  readonly width: number
  readonly height: number
}

type GridStyle = CSSProperties & {
  readonly '--mf-grid-base': string
  readonly '--mf-grid-dot-radius': string
  readonly '--mf-grid-dot-edge': string
  readonly '--mf-grid-dot-opacity': number
  readonly '--mf-grid-x': string
  readonly '--mf-grid-y': string
}

type MediaCanvasNodeStyle = CSSProperties & {
  readonly '--mf-resize-width'?: string
  readonly '--mf-resize-height'?: string
  readonly '--mf-node-wrapper-width': string
  readonly '--mf-connect-rotate-x': string
  readonly '--mf-connect-rotate-y': string
  readonly '--mf-node-content-width': string
  readonly '--mf-node-port-y': string
  readonly '--mf-port-hit-size': string
  readonly '--mf-canvas-inverse-zoom': number
  readonly '--mf-canvas-layout-zoom': number
  readonly '--mf-canvas-inverse-layout-zoom': number
  readonly '--mf-canvas-inverse-transform-zoom': number
}

type NodeGeometrySource = Pick<MediaCanvasNode, 'modality' | 'size'> & { readonly data?: Pick<MediaCanvasNode['data'], 'parameters'> }

function nodeContentWidth(node: NodeGeometrySource): number {
  return node.size?.width ?? (node.modality === 'text' ? 320 : node.modality === 'audio' ? AUDIO_NODE_CARD_SIZE : mediaNodeFrameGeometry(node.modality, node.data?.parameters ?? {})?.width ?? 440)
}

function nodePortY(node: NodeGeometrySource): number {
  const frame = mediaNodeFrameGeometry(node.modality, node.data?.parameters ?? {})
  return 28 + (node.size ? node.size.height / 2 : node.modality === 'text' ? 90 : node.modality === 'audio' ? AUDIO_NODE_CARD_SIZE / 2 : frame ? frame.height / 2 : 124)
}

function nodePortX(node: Pick<MediaCanvasNode, 'modality' | 'position' | 'size'> & { readonly data?: Pick<MediaCanvasNode['data'], 'parameters'> }, port: 'input' | 'output'): number {
  const contentWidth = nodeContentWidth(node)
  const contentLeft = node.position.x + (Math.max(NODE_WIDTH, contentWidth) - contentWidth) / 2
  return port === 'input' ? contentLeft - 22 : contentLeft + contentWidth + 22
}

export function mediaCanvasNodePortCenter(node: Pick<MediaCanvasNode, 'modality' | 'position' | 'size'> & { readonly data?: Pick<MediaCanvasNode['data'], 'parameters'> }, port: 'input' | 'output'): MediaCanvasPoint {
  return { x: nodePortX(node, port), y: node.position.y + nodePortY(node) }
}

function ConnectionPortIcon() {
  return <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="20" height="20" aria-hidden="true" focusable="false">
    <path className="mf-workspace-port__default-icon" fill="white" d="M512 958.016611c-119.648434 0-232.1288-46.367961-316.736783-130.559656-84.640667-84.255342-131.263217-196.255772-131.263217-315.455235 0-119.168499 46.624271-231.199892 131.232254-315.424271 84.607983-84.191695 197.088348-130.559656 316.736783-130.559656s232.1288 46.367961 316.704099 130.559656c84.67163 84.224378 131.263217 196.255772 131.263217 315.391587 0.032684 119.199462-46.591587 231.232576-131.263217 315.455235C744.1288 911.615966 631.648434 958.016611 512 958.016611zM512 129.983389c-102.623626 0-199.071738 39.743475-271.583282 111.936783-72.480581 72.12794-112.416718 168.063432-112.416718 270.079828s39.903454 197.951888 112.384034 270.047144c72.511544 72.191587 168.959656 111.936783 271.583282 111.936783 102.592662 0 199.071738-39.743475 271.583282-111.936783 72.480581-72.160624 112.416718-168.063432 112.384034-270.079828 0-102.016396-39.903454-197.919204-112.384034-270.016181C711.071738 169.759548 614.592662 129.983389 512 129.983389z" />
    <path className="mf-workspace-port__default-icon" fill="white" d="M736.00086 480.00086 544.00086 480.00086 544.00086 288.00086c0-17.664722-14.336138-32.00086-32.00086-32.00086s-32.00086 14.336138-32.00086 32.00086l0 192L288.00086 480.00086c-17.664722 0-32.00086 14.336138-32.00086 32.00086s14.336138 32.00086 32.00086 32.00086l192 0 0 192c0 17.695686 14.336138 32.00086 32.00086 32.00086s32.00086-14.303454 32.00086-32.00086L544.00258 544.00086l192 0c17.695686 0 32.00086-14.336138 32.00086-32.00086S753.696546 480.00086 736.00086 480.00086z" />
    <path className="mf-workspace-port__hover-icon" fill="white" d="M512 42.666667a469.333333 469.333333 0 1 1 0 938.666666 469.333333 469.333333 0 0 1 0-938.666666z m-42.666667 87.637333A384.170667 384.170667 0 0 0 130.304 469.333333H234.666667a42.666667 42.666667 0 0 1 0 85.333334H130.304A384.170667 384.170667 0 0 0 469.333333 893.696V789.333333a42.666667 42.666667 0 0 1 85.333334 0v104.362667A384.170667 384.170667 0 0 0 893.696 554.666667H768a42.666667 42.666667 0 0 1 0-85.333334h125.696A384.170667 384.170667 0 0 0 554.666667 130.304V234.666667a42.666667 42.666667 0 0 1-85.333334 0V130.304z" />
  </svg>
}

function nodeBorderAnchor(node: Pick<MediaCanvasNode, 'modality' | 'position' | 'size'> & { readonly data?: Pick<MediaCanvasNode['data'], 'parameters'> }, port: 'input' | 'output'): MediaCanvasPoint {
  const center = mediaCanvasNodePortCenter(node, port)
  return { x: center.x + (port === 'input' ? 22 : -22), y: center.y }
}

type NodeCardBounds = { readonly left: number; readonly top: number; readonly width: number; readonly height: number; readonly cornerRadius?: number }

function defaultNodeCardBounds(node: Pick<MediaCanvasNode, 'modality' | 'position' | 'size'> & { readonly data?: Pick<MediaCanvasNode['data'], 'parameters'> }): NodeCardBounds {
  const frame = mediaNodeFrameGeometry(node.modality, node.data?.parameters ?? {})
  return {
    left: node.position.x + (Math.max(NODE_WIDTH, nodeContentWidth(node)) - nodeContentWidth(node)) / 2,
    top: node.position.y + 28,
    width: nodeContentWidth(node),
    height: node.size?.height ?? (node.modality === 'text' ? 180 : node.modality === 'audio' ? AUDIO_NODE_CARD_SIZE : frame?.height ?? 248),
    cornerRadius: 8,
  }
}

function distanceToNodeCard(point: MediaCanvasPoint, bounds: NodeCardBounds): number {
  // Signed distance to the rendered rounded card, clamped to zero inside it.
  const corner = Math.min(bounds.cornerRadius ?? 25, bounds.width / 2, bounds.height / 2)
  const dx = Math.abs(point.x - bounds.left - bounds.width / 2) - (bounds.width / 2 - corner)
  const dy = Math.abs(point.y - bounds.top - bounds.height / 2) - (bounds.height / 2 - corner)
  return Math.max(0, Math.hypot(Math.max(dx, 0), Math.max(dy, 0)) + Math.min(Math.max(dx, dy), 0) - corner)
}

export function mediaConnectionEdgePath(source: MediaCanvasPoint, target: MediaCanvasPoint): string {
  const control = Math.max(80, Math.abs(target.x - source.x) * 0.45)
  return `M ${source.x} ${source.y} C ${source.x + control} ${source.y}, ${target.x - control} ${target.y}, ${target.x} ${target.y}`
}

/** Nearest card within the zoom-adjusted border radius, or its input button. */
export function findMediaCanvasConnectionTargetNodeId(options: {
  readonly nodes: readonly Pick<MediaCanvasNode, 'id' | 'modality' | 'position'>[]
  readonly point: MediaCanvasPoint
  readonly sourceNodeId: string
  readonly radius: number
  readonly getCardBounds?: (nodeId: string) => NodeCardBounds | undefined
  readonly targetPort?: 'input' | 'output'
}): string | undefined {
  if (!(options.radius > 0) || !Number.isFinite(options.radius)) return undefined
  let nearest: { readonly id: string; readonly distance: number } | undefined
  for (const node of options.nodes) {
    if (node.id === options.sourceNodeId) continue
    const center = mediaCanvasNodePortCenter(node, options.targetPort ?? 'input')
    const distance = Math.hypot(options.point.x - center.x, options.point.y - center.y)
    const bounds = options.getCardBounds?.(node.id) ?? defaultNodeCardBounds(node)
    if (distance > options.radius && distanceToNodeCard(options.point, bounds) > options.radius) continue
    if (!nearest || distance < nearest.distance) nearest = { id: node.id, distance }
  }
  return nearest?.id
}

/** Safe, user-facing copy for a rejected drag-to-connect drop; never leaks internals. */
export function mediaConnectionFailureHint(sourceName: string, reason: MediaConnectionFailure): string {
  switch (reason) {
    case 'duplicate_connection': return '这两个节点之间已有参考连线'
    case 'modality_incompatible': return `该节点不接受「${sourceName}」这类参考输入`
    case 'target_capability': return '该节点的参考输入数量已达上限'
    case 'cycle': return '不能建立环状参考连线'
    default: return '暂时无法建立这条参考连线，请稍后重试'
  }
}

export function getMediaCanvasZoomTier(zoom: number): MediaCanvasZoomTier {
  if (zoom >= 1) return 'full'
  if (zoom >= 0.6) return 'medium'
  if (zoom >= 0.25) return 'simplified'
  return 'overview'
}

export function isMediaCanvasEditableAtZoom(_zoom: number): boolean {
  return true
}

function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(zoom * 100) / 100))
}

function validViewportSize(value: MediaCanvasViewportSize | undefined): value is MediaCanvasViewportSize {
  return Boolean(value && Number.isFinite(value.width) && value.width > 0 && Number.isFinite(value.height) && value.height > 0)
}

export function fitMediaCanvasViewport(
  nodes: readonly Pick<MediaCanvasNode, 'position' | 'size'>[],
  requestedSize: MediaCanvasViewportSize,
  groups: readonly MediaCanvasGroup[] = [],
): MediaCanvasViewport {
  const size = validViewportSize(requestedSize) ? requestedSize : { width: 1200, height: 720 }
  if (nodes.length === 0 && groups.length === 0) return { x: 0, y: 0, zoom: 1 }
  const minX = Math.min(...nodes.map((node) => node.position.x), ...groups.map((group) => group.position.x))
  const minY = Math.min(...nodes.map((node) => node.position.y), ...groups.map((group) => group.position.y))
  const maxX = Math.max(...nodes.map((node) => node.position.x + Math.max(NODE_WIDTH, node.size?.width ?? 0)), ...groups.map((group) => group.position.x + group.width))
  const maxY = Math.max(...nodes.map((node) => node.position.y + Math.max(NODE_HEIGHT, (node.size?.height ?? 0) + 28)), ...groups.map((group) => group.position.y + group.height))
  const contentWidth = Math.max(NODE_WIDTH, maxX - minX)
  const contentHeight = Math.max(NODE_HEIGHT, maxY - minY)
  const availableWidth = Math.max(1, size.width - FIT_PADDING * 2)
  const availableHeight = Math.max(1, size.height - FIT_PADDING * 2)
  const zoom = clampZoom(Math.min(availableWidth / contentWidth, availableHeight / contentHeight))
  return {
    x: (size.width - contentWidth * zoom) / 2 - minX * zoom,
    y: (size.height - contentHeight * zoom) / 2 - minY * zoom,
    zoom,
  }
}

function canvasPointFromPointer(event: CanvasPointerLike, viewport: MediaCanvasViewport): MediaCanvasPoint {
  const rect = event.currentTarget.getBoundingClientRect()
  return {
    x: (event.clientX - rect.left - viewport.x) / viewport.zoom,
    y: (event.clientY - rect.top - viewport.y) / viewport.zoom,
  }
}

function screenPointFromPointer(event: CanvasPointerLike): MediaCanvasPoint {
  const rect = event.currentTarget.getBoundingClientRect()
  return { x: event.clientX - rect.left, y: event.clientY - rect.top }
}

function normalizedSelectionBox(start: MediaCanvasPoint, end: MediaCanvasPoint): SelectionBox {
  return {
    left: Math.min(start.x, end.x),
    top: Math.min(start.y, end.y),
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  }
}

function selectionBoxIntersectsNode(box: SelectionBox, bounds: NodeCardBounds, viewport: MediaCanvasViewport): boolean {
  const left = viewport.x + bounds.left * viewport.zoom
  const top = viewport.y + bounds.top * viewport.zoom
  return left <= box.left + box.width && top <= box.top + box.height
    && left + bounds.width * viewport.zoom >= box.left
    && top + bounds.height * viewport.zoom >= box.top
}

function selectionBoxContainsNode(box: SelectionBox, bounds: NodeCardBounds, viewport: MediaCanvasViewport): boolean {
  const left = viewport.x + bounds.left * viewport.zoom
  const top = viewport.y + bounds.top * viewport.zoom
  return left >= box.left && top >= box.top
    && left + bounds.width * viewport.zoom <= box.left + box.width
    && top + bounds.height * viewport.zoom <= box.top + box.height
}

function viewportCenter(viewport: MediaCanvasViewport, size: MediaCanvasViewportSize): MediaCanvasPoint {
  return {
    x: (size.width / 2 - viewport.x) / viewport.zoom,
    y: (size.height / 2 - viewport.y) / viewport.zoom,
  }
}

function keyboardTarget(target: EventTarget): 'input' | 'textarea' | 'select' | 'contenteditable' | 'other' {
  const element = target as { readonly tagName?: string; readonly isContentEditable?: boolean }
  if (element.isContentEditable) return 'contenteditable'
  const name = element.tagName?.toLowerCase()
  if (name === 'input' || name === 'textarea' || name === 'select') return name
  return 'other'
}

function isCanvasShortcutProtectedTarget(target: EventTarget): boolean {
  const element = target as {
    readonly tagName?: string
    readonly isContentEditable?: boolean
    closest?: (selector: string) => Element | null
  }
  if (element.isContentEditable) return true
  const name = element.tagName?.toLowerCase()
  if (name === 'input' || name === 'textarea' || name === 'select' || name === 'button' || name === 'a'
    || name === 'audio' || name === 'video'
    || name === 'form' || name === 'label' || name === 'fieldset') return true
  return typeof element.closest === 'function' && Boolean(element.closest([
    'input', 'textarea', 'select', 'button', 'a', 'audio', 'video', '[controls]', 'form', 'label', 'fieldset',
    '[contenteditable="true"]', '[contenteditable=""]',
    '[role="button"]', '[role="link"]', '[role="checkbox"]', '[role="radio"]',
    '[role="switch"]', '[role="slider"]', '[role="textbox"]', '[role="combobox"]',
    '[role="menuitem"]', '[role="option"]', '[role="tab"]',
  ].join(',')))
}

function isSelectedTextNodeScrollTarget(target: EventTarget): boolean {
  const element = target as { closest?: (selector: string) => Element | null }
  return typeof element.closest === 'function'
    && Boolean(element.closest('[data-mf-workspace-node][data-selected="true"] .mf-result--text'))
}

function nodeDragProtectedTarget(target: EventTarget): boolean {
  const element = target as {
    readonly tagName?: string
    readonly isContentEditable?: boolean
    closest?: (selector: string) => Element | null
  }
  if (element.isContentEditable) return true
  const name = element.tagName?.toLowerCase()
  if (name === 'input' || name === 'textarea' || name === 'select' || name === 'button' || name === 'a'
    || name === 'audio'
    || name === 'form' || name === 'label' || name === 'fieldset') return true
  return typeof element.closest === 'function' && Boolean(element.closest([
    'input', 'textarea', 'select', 'button', 'a', 'audio', '[controls]', 'form', 'label', 'fieldset',
    '[contenteditable="true"]', '[contenteditable=""]',
    '[role="button"]', '[role="link"]', '[role="checkbox"]', '[role="radio"]',
    '[role="switch"]', '[role="slider"]', '[role="textbox"]', '[role="combobox"]',
    '[role="menuitem"]', '[role="option"]', '[role="tab"]',
  ].join(',')))
}

function eventHitsCanvasControl(target: EventTarget): boolean {
  const element = target as { closest?: (selector: string) => Element | null }
  return typeof element.closest === 'function'
    && Boolean(element.closest('[data-mf-workspace-node], [data-mf-node-selector], [data-mf-group], [data-mf-context-menu]'))
}

function runtimeStatusLabel(runtime?: MediaNodeRuntime): string {
  if (!runtime?.task_status) return '未运行'
  const labels: Readonly<Record<string, string>> = {
    created: '生成中',
    reserving: '生成中',
    queued: '生成中',
    submitting: '生成中',
    running: '生成中',
    succeeded: '已完成',
    failed: '生成失败',
    timed_out: '已超时',
    cancelled: '已取消',
    rejected: '已拒绝',
  }
  return labels[runtime.task_status] ?? '状态未知'
}

function modalityLabel(modality: MediaModality): string {
  return modality === 'file' ? 'PDF 文件' : NODE_OPTIONS.find((option) => option.modality === modality)?.label ?? '媒体'
}

function subscribeReducedMotion(listener: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
  const query = window.matchMedia(REDUCED_MOTION_QUERY)
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', listener)
    return () => query.removeEventListener('change', listener)
  }
  query.addListener(listener)
  return () => query.removeListener(listener)
}

function reducedMotionSnapshot(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true
  return window.matchMedia(REDUCED_MOTION_QUERY).matches
}

function selectedOutput(node: MediaCanvasNode): MediaCanvasNode['data']['outputs'][number] | undefined {
  const requested = node.data.selected_output_version
  if (requested !== undefined) return node.data.outputs.find((output) => output.output_version === requested)
  return [...node.data.outputs].sort((left, right) => right.output_version - left.output_version)[0]
}

function releaseCapturedPointer(target: PointerCaptureTarget | undefined, pointerId: number | undefined): void {
  if (!target || pointerId === undefined || typeof target.releasePointerCapture !== 'function') return
  try {
    if (typeof target.hasPointerCapture !== 'function' || target.hasPointerCapture(pointerId)) target.releasePointerCapture(pointerId)
  } catch {
    // A lost capture is already a completed pointer lifecycle.
  }
}

function DefaultWorkspaceNode({ node, presentation, onEdit }: {
  readonly node: MediaCanvasNode
  readonly presentation: MediaCanvasNodePresentation
  readonly onEdit: () => void
}) {
  const status = runtimeStatusLabel(presentation.runtime)
  const component = presentation.component ?? NODE_OPTIONS.find((option) => option.modality === node.modality)!
  return <div className="mf-workspace-node__card" data-mf-node-card={presentation.zoomTier}>
    <header className="mf-workspace-node__header">
      <span className="mf-workspace-node__type" aria-hidden="true"><MediaComponentIcon icon={component.icon} /></span>
      <strong>{node.name}</strong>
      <span className="mf-workspace-node__status" role="status" aria-live="polite">{status}</span>
    </header>
    {presentation.zoomTier === 'full' || presentation.zoomTier === 'medium'
      ? <div className="mf-workspace-node__preview" aria-hidden="true"><MediaComponentIcon icon={component.icon} size={32} /></div>
      : null}
    {presentation.zoomTier === 'full'
      ? <p className="mf-workspace-node__hint">{component.description}</p>
      : null}
    <MediaButton type="button" className="mf-workspace-node__edit" aria-label={`编辑${node.name}`} disabled={!presentation.editable} onClick={onEdit}>编辑节点</MediaButton>
  </div>
}

function ElementThumbnail({ modality, icon, sourceUrl }: {
  readonly modality: MediaModality
  readonly icon: MediaCanvasNodeOption['icon']
  readonly sourceUrl?: string
}) {
  const [failedUrl, setFailedUrl] = useState<string>()
  const url = sourceUrl && (sourceUrl.startsWith('blob:') || isPrivateMediaResourceAccessURL(sourceUrl)) ? sourceUrl : undefined
  const showMedia = url && url !== failedUrl
  return <span className="mf-workspace__element-thumbnail" aria-hidden="true" data-mf-element-thumbnail={modality}>
    {showMedia && modality === 'image'
      ? <img key={url} className="mf-workspace__element-media" src={url} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setFailedUrl(url)} />
      : showMedia && modality === 'video'
        ? <video key={url} className="mf-workspace__element-media" src={url} muted playsInline preload="auto" tabIndex={-1} onLoadedMetadata={(event) => { event.currentTarget.currentTime = 0 }} onError={() => setFailedUrl(url)} />
        : <span className="mf-workspace__element-icon"><MediaComponentIcon icon={icon} size={18} /></span>}
  </span>
}

function WorkspaceStyles() {
  return <style data-mf-workspace-style dangerouslySetInnerHTML={{ __html: `
    :root { --mf-workspace-cursor: url("data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='24'%20height='24'%20viewBox='0%200%201024%201024'%3E%3Cpath%20fill='%23ffffff'%20d='M516.096%20649.216l-151.04%20202.752c-27.648%2036.864-86.016%2019.968-89.088-26.112L231.424%20205.824a49.4592%2049.4592%200%200%201%2077.312-44.544l514.56%20348.672c37.888%2025.6%2023.552%2084.992-22.016%2090.624l-250.88%2029.184c-13.824%201.536-26.112%208.704-33.792%2019.456z'/%3E%3Cpath%20fill='%23ffffff'%20d='M325.632%20897.024c-7.168%200-14.336-1.024-21.504-3.072a73.728%2073.728%200%200%201-53.76-66.56L205.824%20207.36a75.1616%2075.1616%200%200%201%20117.248-67.584l514.56%20348.672c26.112%2017.92%2038.4%2049.152%2030.72%2080.384s-32.768%2053.248-64.512%2056.832l-250.88%2029.184c-6.656%200.512-12.288%204.096-16.384%209.728s-151.04%20202.752-151.04%20202.752c-14.336%2019.456-36.864%2030.208-59.904%2030.208zM281.088%20177.664c-5.632%200-10.24%202.048-12.288%203.072-3.072%201.536-12.8%208.704-11.776%2022.528l44.544%20620.032c1.024%2015.36%2013.312%2020.48%2017.408%2021.504%204.096%201.024%2016.896%203.584%2026.112-8.704l151.04-202.752c12.288-16.384%2031.232-27.648%2051.712-29.696l250.88-29.184c15.36-2.048%2019.456-14.336%2020.48-17.92%201.024-4.096%203.072-16.896-9.728-25.6L294.4%20182.272a23.6032%2023.6032%200%200%200-13.312-4.096z'/%3E%3C/svg%3E") 5 3, default; }
    .mf-workspace { --mf-accent:#8666ff; --mf-toolbar-height:36px; --mf-toolbar-bottom:24px; position:relative; isolation:isolate; min-height:0; height:calc(100vh - 70px); height:calc(100dvh - 70px); overflow:hidden; color:#f2f0f7; background:#050608; font-family:Inter,"PingFang SC","Microsoft YaHei",sans-serif; }
    .mf-workspace *, .mf-workspace *::before, .mf-workspace *::after { box-sizing:border-box; }
    .mf-workspace, .mf-workspace * { -webkit-user-select:none; user-select:none; }
    .mf-workspace :is(input, textarea, [contenteditable="true"], [contenteditable=""], [contenteditable="plaintext-only"]), .mf-workspace :is([contenteditable="true"], [contenteditable=""], [contenteditable="plaintext-only"]) * { -webkit-user-select:text; user-select:text; }
    .mf-workspace__skip { position:absolute; z-index:40; left:12px; top:8px; transform:translateY(-160%); border-radius:7px; padding:8px 11px; color:#17131d; background:#f4f1f8; font-size:12px; }
    .mf-workspace__skip:nth-of-type(2) { left:132px; }
    .mf-workspace__skip:focus { transform:none; outline:2px solid #fff; outline-offset:2px; }
    .mf-reference-picker { position:absolute; z-index:80; top:20px; left:50%; transform:translateX(-50%); display:flex; align-items:center; gap:12px; max-width:calc(100% - 32px); box-sizing:border-box; padding:10px 14px; border-radius:16px; background:#246bff; color:#fff; box-shadow:0 8px 30px #0004; font-size:14px; }
    .mf-workspace[data-mf-sidebar-expanded="true"] .mf-reference-picker { left:calc(50% + var(--mf-sidebar-width)/2); max-width:calc(100% - var(--mf-sidebar-width) - 24px); }
    .mf-reference-picker__icon { display:grid; place-items:center; flex:none; width:36px; height:36px; border-radius:10px; background:#ffffff26; }
    .mf-reference-picker > span[role="status"] { min-width:0; }
    .mf-reference-picker button { --mf-workspace-cursor:pointer; flex:none; border:0; border-radius:9px; padding:8px 12px; color:inherit; background:#ffffff26; cursor:pointer; font:inherit; white-space:nowrap; }
    .mf-reference-picker button:hover { background:#ffffff40; }
    .mf-reference-picker button:focus-visible { outline:2px solid #fff; outline-offset:2px; }
    .mf-reference-picker button[aria-label="退出参考选择"] { padding:3px 8px; background:transparent; font-size:26px; }
    .mf-canvas[data-mf-reference-selecting="true"] .mf-workspace-node, .mf-canvas[data-mf-reference-selecting="true"] .mf-workspace-node * { --mf-workspace-cursor:crosshair!important; cursor:crosshair!important; }
    .mf-workspace-node[data-mf-reference-candidate="true"]:hover .mf-node__content-card { outline:2px solid #4c8aff; outline-offset:3px; }
    .mf-canvas { position:absolute; inset:0; overflow:hidden; outline:none; background-color:#050608; cursor:default; touch-action:none; }
    .mf-canvas[data-mf-space-pan="true"], .mf-canvas[data-mf-space-pan="true"] * { --mf-workspace-cursor:grab!important; cursor:grab!important; user-select:none; }
    .mf-canvas[data-mf-panning="true"], .mf-canvas[data-mf-panning="true"] * { --mf-workspace-cursor:grabbing!important; cursor:grabbing!important; user-select:none; }
    .mf-canvas[data-mf-selecting="true"] { cursor:crosshair; user-select:none; }
    .mf-canvas[data-mf-grid-visible="true"] { background-image:radial-gradient(circle,rgba(255,255,255,var(--mf-grid-dot-opacity)) var(--mf-grid-dot-radius),transparent var(--mf-grid-dot-edge)); background-size:var(--mf-grid-base) var(--mf-grid-base); background-position:var(--mf-grid-x) var(--mf-grid-y); }
    .mf-canvas__drop-hint { position:absolute; z-index:30; inset:12px; display:grid; place-items:center; border:2px dashed #a58cff; border-radius:16px; color:#eee8ff; background:rgba(64,45,105,.25); font-size:16px; pointer-events:none; }
    .mf-canvas__scene { position:absolute; inset:0; transform-origin:0 0; pointer-events:none; }
    .mf-workspace-node { position:absolute; isolation:isolate; width:600px; min-height:210px; outline:none; pointer-events:none; }
    .mf-workspace-node :is(.mf-node__header,.mf-node__content-card,.mf-node__action-rail,.mf-node__prompt-anchor,.mf-node > .mf-status,.mf-workspace-node__card,.mf-workspace-port,.mf-workspace-node__focus) { pointer-events:auto; }
    .mf-workspace-node[data-selected="true"], .mf-workspace-node:focus-within { z-index:20; }
    .mf-workspace-node[data-mf-node-dragging="true"] { z-index:21; cursor:grabbing; user-select:none; }
    .mf-workspace-node__card { min-height:210px; border:1px solid rgba(255,255,255,.2); border-radius:14px; background:#232323; box-shadow:0 18px 48px rgba(0,0,0,.35); overflow:hidden; }
    .mf-workspace-node[data-selected="true"] .mf-workspace-node__card { border-color:var(--mf-accent); box-shadow:0 0 0 2px rgba(134,102,255,.25),0 18px 48px rgba(0,0,0,.35); }
    .mf-workspace-node__header { display:flex; align-items:center; gap:9px; min-height:48px; padding:0 14px; border-bottom:1px solid rgba(255,255,255,.08); }
    .mf-workspace-node__header strong { min-width:0; flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:14px; }
    .mf-workspace-node__type { display:grid; place-items:center; width:22px; height:22px; border-radius:6px; color:#d8ceff; background:rgba(134,102,255,.15); }
    .mf-workspace-node__status { font-size:11px; color:#aaa5b4; }
    .mf-workspace-node__preview { display:grid; place-items:center; height:112px; margin:12px; border-radius:10px; color:#85808c; background:#232323; font-size:28px; }
    .mf-workspace-node__hint { margin:0 14px 12px; color:#97929e; font-size:12px; }
    .mf-workspace-node__edit { margin:0 14px 14px; border:0; border-radius:8px; padding:7px 10px; color:#e9e5f2; background:#353238; cursor:pointer; }
    .mf-workspace-node__edit:disabled { opacity:.42; cursor:not-allowed; }
    .mf-workspace-node[data-mf-node-zoom-interactive="false"] { min-height:94px; }
    .mf-workspace-node[data-mf-node-zoom-interactive="false"] .mf-workspace-node__card { min-height:94px; }
    .mf-workspace-node[data-mf-node-zoom-interactive="false"] .mf-workspace-node__edit { display:none; }
    .mf-workspace-port { position:absolute; z-index:12; top:var(--mf-node-port-y); display:grid; place-items:center; width:20px; height:20px; margin-top:-10px; border:0; padding:0; color:#d7d2dc; background:transparent; cursor:crosshair; }
    /* Keep the side hit region fixed as the icon follows the pointer, and stop at the card edge. */
    .mf-workspace-port::before { content:""; position:absolute; left:calc(10px - 22px); top:50%; width:calc(22px + 32px * var(--mf-canvas-inverse-zoom)); height:max(calc(112px * var(--mf-canvas-inverse-zoom)), calc(2 * (var(--mf-node-port-y) - 28px) + 48px)); transform:translate(calc(-1 * var(--mf-port-offset-x, 0px)), calc(-50% - var(--mf-port-offset-y, 0px))); background:transparent; }
    .mf-workspace-port--input::before { left:auto; right:calc(10px - 22px); }
    .mf-workspace-port svg { --mf-workspace-cursor:${CONNECTION_PORT_CURSOR}; display:block; position:relative; width:20px; height:20px; border-radius:999px; background:transparent; pointer-events:auto; cursor:${CONNECTION_PORT_CURSOR}; }
    .mf-workspace-port__hover-icon { display:none; }
    .mf-workspace-port:hover .mf-workspace-port__default-icon { display:none; }
    .mf-workspace-port:hover .mf-workspace-port__hover-icon { display:block; }
    .mf-workspace-port--input { left:calc((var(--mf-node-wrapper-width,600px) - var(--mf-node-content-width)) / 2 - 32px); } .mf-workspace-port--output { right:calc((var(--mf-node-wrapper-width,600px) - var(--mf-node-content-width)) / 2 - 32px); }
    .mf-workspace-port:focus-visible svg { color:#fff; border-color:#b8a9ff; background-color:#6f54df; }
    .mf-workspace-port:focus-visible { outline:none; }
    .mf-workspace-port:disabled { display:none; }
    /* Invisible ports retain their hit regions so entering from the canvas reveals them. */
    .mf-workspace-node:not([data-selected="true"]):not(:hover) .mf-workspace-port { opacity:0; }
    .mf-workspace-node__focus { position:absolute; z-index:1; inset:0; width:100%; border:1px solid transparent; border-radius:14px; color:transparent; background:transparent; cursor:zoom-in; }
    .mf-workspace-node__focus:focus-visible { border-color:var(--mf-accent); outline:2px solid rgba(134,102,255,.45); outline-offset:3px; }
    .mf-workspace-edges { position:absolute; inset:0; overflow:visible; pointer-events:none; }
    .mf-workspace-edge { fill:none; stroke:rgba(255,255,255,.34); stroke-width:2; vector-effect:non-scaling-stroke; }
    .mf-workspace-edge-hit { fill:none; stroke:transparent; stroke-width:16; vector-effect:non-scaling-stroke; pointer-events:stroke; }
    .mf-workspace-edge-group[data-mf-edge-related="true"] .mf-workspace-edge { stroke:rgba(255,255,255,.55); }
    .mf-workspace-edge-group:is([data-mf-edge-hovered="true"],[data-mf-edge-selected="true"]) .mf-workspace-edge { stroke:#fff; stroke-width:2.5; }
    .mf-workspace-edge-meteor { fill:none; stroke-linecap:round; pointer-events:none; }
    /* No width/transform transitions: animated card sizing feeds back into the
       ResizeObserver that owns --mf-node-content-width and jitters forever. */
    .mf-workspace-node :is(.mf-node__content-card,.mf-workspace-node__card) { transform:none; transform-origin:center center; transition:none; }
    .mf-workspace-node[data-mf-connect-follow="true"] :is(.mf-node__content-card,.mf-workspace-node__card) { transform:perspective(900px) rotateX(var(--mf-connect-rotate-x,0deg)) rotateY(var(--mf-connect-rotate-y,0deg)); }
    @media (prefers-reduced-motion:reduce) { .mf-workspace-edge-meteor { animation:none; } }
    .mf-workspace-edge--pending { stroke:#b7a6ff; stroke-width:2; stroke-dasharray:7 6; opacity:.92; }
    .mf-workspace-node[data-mf-connect-available="true"] .mf-workspace-port--input svg { color:#fff; border-color:#b8a9ff; background-color:#6f54df; }
    .mf-workspace-node[data-mf-connect-hover="true"] .mf-workspace-port[data-mf-connect-cursor="true"] { display:grid; pointer-events:none; }
    .mf-workspace-node[data-mf-connect-hover="true"] .mf-workspace-port[data-mf-connect-cursor="true"] svg { color:#fff; border-color:#fff; background-color:#8666ff; box-shadow:0 0 0 6px rgba(134,102,255,.28); }
    .mf-canvas[data-mf-selection-chrome-hidden="true"] :is(.mf-node__prompt-anchor,.mf-node__action-rail,.mf-workspace-port) { display:none; }
    .mf-selection-frame { position:absolute; z-index:22; border:1px solid #6b9dff; background:rgba(107,157,255,.025); pointer-events:none; outline:none; }
    .mf-selection-frame:focus-visible { outline:2px solid #a8c5ff; outline-offset:3px; }
    .mf-selection-frame__edge,.mf-selection-frame__corner { position:absolute; pointer-events:auto; cursor:grab; }
    .mf-selection-frame[data-mf-selection-dragging="true"] span { cursor:grabbing; }
    .mf-selection-frame__edge--n,.mf-selection-frame__edge--s { left:0; right:0; height:10px; }
    .mf-selection-frame__edge--n { top:-5px; }.mf-selection-frame__edge--s { bottom:-5px; }
    .mf-selection-frame__edge--e,.mf-selection-frame__edge--w { top:0; bottom:0; width:10px; }
    .mf-selection-frame__edge--e { right:-5px; }.mf-selection-frame__edge--w { left:-5px; }
    .mf-selection-frame__corner { width:6px; height:6px; border:1px solid #a8c5ff; background:#426ba8; }
    .mf-selection-frame__corner--nw { left:-3px; top:-3px; }.mf-selection-frame__corner--ne { right:-3px; top:-3px; }
    .mf-selection-frame__corner--sw { left:-3px; bottom:-3px; }.mf-selection-frame__corner--se { right:-3px; bottom:-3px; }
    .mf-selection-box { position:absolute; z-index:10; border:1px solid rgba(183,166,255,.9); border-radius:4px; background:rgba(134,102,255,.18); pointer-events:none; }
    .mf-workspace__project, .mf-workspace__toolbar, .mf-workspace__drawer, .mf-empty-state, .mf-minimap, .mf-workspace__connection-hint { position:absolute; z-index:5; }
    .mf-workspace__connection-hint { left:50%; bottom:76px; transform:translateX(-50%); max-width:min(480px,calc(100% - 48px)); border:1px solid rgba(183,166,255,.45); border-radius:999px; padding:7px 14px; color:#efeaff; background:rgba(38,32,54,.94); font-size:12px; text-align:center; box-shadow:0 12px 34px rgba(0,0,0,.4); pointer-events:none; }
    .mf-workspace__project { left:16px; top:16px; width:169px; height:32px; display:flex; align-items:center; border-radius:8px; background:#272727; color:#fff; z-index:7; }
    .mf-workspace__home { display:grid; place-items:center; width:34px; min-width:34px; height:32px; padding:0; border:0; background:transparent; color:#f6f6f6; cursor:pointer; }
    .mf-workspace__title-divider { flex:none; width:1px; height:16px; background:#3f3f3f; }
    .mf-workspace__project input { box-sizing:border-box; width:134px; min-width:0; height:32px; padding:0 8px 0 9px; border:0; border-radius:4px; background:transparent; color:#fff; font-size:14px; outline:none; }
    .mf-workspace__project[data-mf-project-switcher="true"] { width:220px; }
    .mf-workspace__project[data-mf-project-switcher="true"] input { flex:1; padding-right:32px; }
    .mf-workspace__switcher-anchor { position:absolute; inset:0; pointer-events:none; }
    .mf-workspace .mf-workspace__switcher { position:absolute; right:0; top:0; display:grid; place-items:center; width:32px; min-width:32px; height:32px; padding:0!important; border:0!important; pointer-events:auto; }
    .mf-workspace__project input:focus-visible { outline:1px solid #777; }
    .mf-workspace-menu { box-sizing:border-box; width:280px; max-width:calc(100vw - 32px); padding:4px; border:1px solid #353535; border-radius:4px; background:#272727; color:#ccc; box-shadow:0 8px 24px rgba(0,0,0,.3); font-size:14px; }
    .mf-workspace-menu__list { max-height:min(360px,calc(100dvh - 180px)); overflow-y:auto; }
    .mf-workspace-menu .mf-workspace-menu__item { display:block; width:100%; height:32px; padding:0 8px!important; overflow:hidden; text-align:left; text-overflow:ellipsis; white-space:nowrap; border:0!important; border-radius:2px; color:inherit; background:transparent; font:inherit; cursor:pointer; box-sizing:border-box; transform:none!important; transition:none!important; }
    .mf-workspace-menu .mf-workspace-menu__item:is(:hover,:focus-visible), .mf-workspace-menu .mf-workspace-menu__item[aria-checked="true"] { background:#414141; color:#f6f6f6; }
    .mf-workspace-menu .mf-workspace-menu__item:focus-visible { outline:1px solid #aaa; outline-offset:-1px; }
    .mf-workspace-menu .mf-workspace-menu__item:disabled { opacity:.45; cursor:not-allowed; }
    .mf-workspace-menu__footer { margin-top:4px; padding-top:4px; border-top:1px solid #333; }
    .mf-workspace-menu__status { padding:8px; color:#aaa; font-size:12px; }
    .mf-workspace__rename-error { position:absolute; top:36px; left:0; width:220px; padding:8px; background:#272727; color:#f6f6f6; font-size:12px; border-radius:8px; }
    .mf-workspace[data-mf-sidebar-expanded="true"] .mf-workspace__project { width:calc(var(--mf-sidebar-width,300px) - 32px); background:transparent; }
    .mf-workspace[data-mf-sidebar-expanded="true"] .mf-workspace__switcher { justify-items:end; }
    .mf-workspace__sidebar { position:absolute; z-index:6; left:0; top:0; bottom:0; width:var(--mf-sidebar-width,300px); min-width:0; overflow:hidden; background:#111111; border-right:1px solid #3f3f3f; box-sizing:border-box; }
    .mf-workspace__sidebar::before { content:''; position:absolute; left:16px; right:16px; top:59px; height:1px; background:#3f3f3f; }
    .mf-workspace__sidebar-tabs { position:absolute; top:69px; left:16px; right:16px; min-width:0; display:flex; gap:12px; height:44px; border-bottom:1px solid #3f3f3f; box-sizing:border-box; }
    .mf-workspace__sidebar-tabs button { min-width:0; height:32px; padding:0 10px; border:0; border-radius:8px; background:transparent; color:#d7d9db; font-size:14px; cursor:pointer; }
    .mf-workspace .mf-workspace__sidebar-tabs .media-ui-button[aria-pressed="true"] { background:#2c2c2c!important; border-color:transparent!important; }
    .mf-workspace__sidebar-footer { position:absolute; left:16px; right:16px; bottom:24px; height:45px; border-top:1px solid #3f3f3f; color:#888; font-size:12px; display:flex; align-items:center; justify-content:flex-end; }
    .mf-workspace__sidebar-toggle { position:absolute; z-index:7; left:26px; bottom:calc(var(--mf-toolbar-bottom) + (var(--mf-toolbar-height) - 20px) / 2); display:flex; align-items:center; gap:6px; height:20px; padding:0; border:0; background:transparent; color:#d7d9db; font-size:14px; cursor:pointer; }
    .mf-workspace .mf-workspace__sidebar-toggle, .mf-workspace .mf-workspace__sidebar-toggle:is(:hover,:active) { border-width:0!important; padding:0!important; box-sizing:border-box!important; }
    .mf-workspace .mf-workspace__sidebar-toggle, .mf-workspace .mf-workspace__sidebar-toggle *, .mf-workspace .mf-workspace__sidebar-toggle::before, .mf-workspace .mf-workspace__sidebar-toggle::after, .mf-workspace .mf-workspace__sidebar-toggle *::before, .mf-workspace .mf-workspace__sidebar-toggle *::after, .mf-workspace .mf-workspace__sidebar-toggle:is(:hover,:active), .mf-workspace .mf-workspace__sidebar-toggle:is(:hover,:active) * { transform:none!important; translate:none!important; transition:none!important; animation:none!important; }
    .mf-workspace[data-mf-sidebar-expanded="true"] .mf-workspace__toolbar { left:calc(var(--mf-sidebar-width,300px) + 24px); max-width:calc(100% - var(--mf-sidebar-width,300px) - 48px); }
    .mf-workspace__sidebar-resize { --mf-workspace-cursor:col-resize; position:absolute; z-index:8; left:calc(var(--mf-sidebar-width,300px) - 4px); top:0; bottom:0; width:8px; cursor:col-resize; touch-action:none; user-select:none; outline:none; }
    .mf-workspace__sidebar-resize::after { content:''; position:absolute; top:0; bottom:0; left:3px; width:2px; background:transparent; }
    .mf-workspace__sidebar-resize:hover::after, .mf-workspace__sidebar-resize:focus-visible::after, .mf-workspace[data-mf-sidebar-resizing="true"] .mf-workspace__sidebar-resize::after { background:var(--mf-accent); }
    .mf-workspace[data-mf-sidebar-resizing="true"], .mf-workspace[data-mf-sidebar-resizing="true"] * { --mf-workspace-cursor:col-resize!important; cursor:col-resize!important; user-select:none!important; }
    .mf-workspace :is(.mf-workspace__sidebar,.mf-workspace__project), .mf-workspace :is(.mf-workspace__sidebar,.mf-workspace__project) *, .mf-workspace :is(.mf-workspace__sidebar,.mf-workspace__project)::before, .mf-workspace :is(.mf-workspace__sidebar,.mf-workspace__project)::after, .mf-workspace :is(.mf-workspace__sidebar,.mf-workspace__project) *::before, .mf-workspace :is(.mf-workspace__sidebar,.mf-workspace__project) *::after { transform:none!important; translate:none!important; scale:none!important; rotate:none!important; transition-property:color,background-color,border-color!important; animation:none!important; }
    .mf-workspace .mf-workspace__sidebar button { border-width:1px!important; }
    .mf-workspace .mf-workspace__home, .mf-workspace .mf-workspace__sidebar-tabs button, .mf-workspace .mf-workspace__sidebar .mf-workspace__element, .mf-workspace .mf-workspace__sidebar .mf-workspace__drawer-close { border-width:0!important; }
    .mf-workspace :is(.mf-workspace__sidebar,.mf-workspace__project) button:not(:disabled):is(:hover,:active) { color:#fff!important; }
    .mf-workspace__save-status { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip-path:inset(50%); white-space:nowrap; border:0; pointer-events:none; }
    .mf-workspace__toolbar { left:24px; max-width:calc(100% - 48px); overflow-x:auto; bottom:var(--mf-toolbar-bottom); display:flex; align-items:center; gap:2px; height:var(--mf-toolbar-height); border:1px solid rgba(255,255,255,.06); border-radius:8px; padding:3px; background:#242426; box-shadow:0 10px 30px rgba(0,0,0,.32); }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__tool { position:relative!important; inset:auto!important; display:grid!important; place-items:center!important; flex:0 0 28px!important; box-sizing:border-box!important; width:28px!important; min-width:28px!important; max-width:28px!important; height:28px!important; min-height:28px!important; max-height:28px!important; margin:0!important; border:0 solid transparent!important; border-radius:6px!important; padding:0!important; gap:0!important; color:#aaa7af!important; background:transparent!important; box-shadow:none!important; opacity:1!important; cursor:pointer!important; font-size:12px!important; font-weight:400!important; font-variation-settings:normal!important; letter-spacing:normal!important; line-height:1!important; transition:none!important; transform:none!important; translate:none!important; scale:none!important; rotate:none!important; animation:none!important; filter:none!important; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__tool:disabled { cursor:not-allowed!important; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__tool-icon { display:block!important; flex:none!important; box-sizing:border-box!important; width:16px!important; min-width:16px!important; max-width:16px!important; height:16px!important; min-height:16px!important; max-height:16px!important; margin:0!important; border:0!important; padding:0!important; pointer-events:none; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__tool:not(:disabled):is(:hover,:active) { color:#f7f5fa!important; background:#252525!important; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__tool[aria-pressed="true"], .mf-workspace .mf-workspace__toolbar .mf-workspace__tool[aria-expanded="true"], .mf-workspace .mf-workspace__toolbar .mf-workspace__tool[data-selected="true"], .mf-workspace .mf-workspace__toolbar .mf-workspace__tool[aria-pressed="true"]:is(:hover,:active), .mf-workspace .mf-workspace__toolbar .mf-workspace__tool[aria-expanded="true"]:is(:hover,:active), .mf-workspace .mf-workspace__toolbar .mf-workspace__tool[data-selected="true"]:is(:hover,:active) { color:#fff!important; background:#0F0F0F!important; border-color:transparent!important; box-shadow:none!important; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__mode { background:transparent!important; border-color:transparent!important; box-shadow:none!important; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__mode:not(:disabled):is(:hover,:active) { background:#252525!important; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__tool, .mf-workspace .mf-workspace__toolbar .mf-workspace__tool *, .mf-workspace .mf-workspace__toolbar .mf-workspace__tool::before, .mf-workspace .mf-workspace__toolbar .mf-workspace__tool::after, .mf-workspace .mf-workspace__toolbar .mf-workspace__tool *::before, .mf-workspace .mf-workspace__toolbar .mf-workspace__tool *::after, .mf-workspace .mf-workspace__toolbar .mf-workspace__tool:is(:hover,:active), .mf-workspace .mf-workspace__toolbar .mf-workspace__tool:is(:hover,:active) * { transform:none!important; translate:none!important; scale:none!important; rotate:none!important; transition:none!important; animation:none!important; filter:none!important; }
    .mf-workspace .mf-workspace__toolbar .mf-workspace__tool :is(span,svg,path,rect,line) { margin:0!important; padding:0!important; }
    .mf-workspace__tool:focus-visible { outline:2px solid rgba(183,166,255,.9); outline-offset:2px; }
    @media (prefers-reduced-motion:reduce) { .mf-workspace__tool { transition:none; } }
    .mf-workspace__mode { display:grid; place-items:center; }
    .mf-canvas-help { color:#e9e6ed; font-size:13px; line-height:1.75; }
    .mf-canvas-help__modes { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:10px; margin-bottom:16px; }
    .mf-canvas-help__mode { min-width:0; border:1px solid #3b3b3e; border-radius:8px; padding:12px; background:#1d1d1f; }
    .mf-canvas-help__mode[data-active="true"] { border-color:#8666ff; background:#252130; }
    .mf-canvas-help__mode h3 { margin:0 0 4px; color:#f4f2f7; font-size:14px; }
    .mf-canvas-help__mode p { margin:0; color:#aaa6b0; font-size:12px; }
    .mf-canvas-help__guides { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:18px; }
    .mf-canvas-help__guides h3 { margin:0 0 6px; color:#d6d2dc; font-size:12px; }
    .mf-canvas-help ul { margin:0 0 16px; padding-left:20px; }
    .mf-canvas-help label { display:flex; align-items:center; gap:8px; border-top:1px solid #444; padding-top:14px; }
    .mf-canvas-help input[type="checkbox"] { width:14px; height:14px; flex:none; margin:0; padding:0; accent-color:#f6f6f6; cursor:pointer; }
    @media (max-width:560px) { .mf-canvas-help__modes, .mf-canvas-help__guides { grid-template-columns:1fr; } }
    .mf-workspace__divider { width:1px; height:18px; margin:0 2px; background:rgba(255,255,255,.12); }
    .mf-workspace__zoom-control { display:flex; flex:none; align-items:center; gap:8px; height:28px; margin:0 7px 0 8px; }
    .mf-workspace__zoom-slider { flex:0 0 88px; width:88px; height:28px; margin:0; padding:0; border:0; background:transparent; cursor:pointer; appearance:none; -webkit-appearance:none; outline:none; }
    .mf-workspace__zoom-slider::-webkit-slider-runnable-track { width:100%; height:5px; border:0; border-radius:999px; background:#555558; }
    .mf-workspace__zoom-slider::-webkit-slider-thumb { box-sizing:border-box; width:20px; height:20px; margin-top:-7.5px; border:4px solid #f4f4f5; border-radius:50%; background:#242426; box-shadow:none; appearance:none; -webkit-appearance:none; }
    .mf-workspace__zoom-slider::-moz-range-track { width:100%; height:5px; border:0; border-radius:999px; background:#555558; }
    .mf-workspace__zoom-slider::-moz-range-progress { height:5px; border:0; border-radius:999px; background:#555558; }
    .mf-workspace__zoom-slider::-moz-range-thumb { box-sizing:border-box; width:20px; height:20px; border:4px solid #f4f4f5; border-radius:50%; background:#242426; box-shadow:none; }
    .mf-workspace__zoom-slider:focus-visible::-webkit-slider-thumb { box-shadow:0 0 0 2px #242426,0 0 0 4px rgba(183,166,255,.9); }
    .mf-workspace__zoom-slider:focus-visible::-moz-range-thumb { box-shadow:0 0 0 2px #242426,0 0 0 4px rgba(183,166,255,.9); }
    .mf-workspace__zoom-value { width:36px; color:#d7d3db; font-size:11px; font-variant-numeric:tabular-nums; line-height:1; text-align:right; }
    .mf-workspace__drawer { position:absolute; left:16px; right:4px; top:128px; bottom:85px; min-width:0; box-sizing:border-box; overflow-y:auto; overflow-x:hidden; scrollbar-gutter:stable; scrollbar-width:thin; scrollbar-color:#393a3d transparent; }
    .mf-workspace__drawer-content { width:100%; min-width:0; padding:0 12px 8px 0; box-sizing:border-box; container:mf-drawer / inline-size; }
    .mf-workspace__drawer-content > * { min-width:0; max-width:100%; box-sizing:border-box; }
    .mf-workspace__drawer .mf-library__list { display:grid; grid-template-columns:minmax(0,1fr); gap:12px; align-items:stretch; }
    .mf-workspace[data-mf-sidebar-grid="true"] .mf-workspace__element-list { grid-template-columns:repeat(auto-fill,minmax(96px,1fr)); gap:16px 8px; }
    .mf-workspace[data-mf-sidebar-grid="true"] .mf-workspace__element.media-ui-button { grid-template-columns:minmax(0,1fr); align-content:start; gap:8px; height:auto; padding:4px; border-radius:10px!important; }
    .mf-workspace[data-mf-sidebar-grid="true"] .mf-workspace__element-thumbnail { width:100%; height:auto; aspect-ratio:1; border:1px solid #343434; border-radius:10px; }
    .mf-workspace[data-mf-sidebar-grid="true"] .mf-workspace__element-icon > * { width:24px!important; height:24px!important; }
    .mf-workspace__drawer > *, .mf-workspace__drawer .mf-library, .mf-workspace__drawer .mf-library > * { min-width:0; max-width:100%; box-sizing:border-box; }
    .mf-workspace__drawer :is(p,span,code,h2) { min-width:0; overflow-wrap:anywhere; }
    .mf-workspace__drawer .mf-library__meta, .mf-workspace__drawer .mf-library__pagination { min-width:0; flex-wrap:wrap; }
    .mf-workspace__drawer .mf-library__meta > *, .mf-workspace__drawer .mf-library__pagination > * { min-width:0; max-width:100%; }
    .mf-workspace__drawer select { border:0; background:#111111; color:#f6f6f6; font-size:14px; }
    .mf-workspace__drawer option { background:#111111; color:#f6f6f6; }
    .mf-workspace__drawer header { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
    .mf-workspace__drawer h2 { margin:0; font-size:12px; font-weight:400; color:#aaa; }
    .mf-workspace__drawer-close { width:28px; height:28px; border:0; border-radius:6px; color:#b8b3bd; background:transparent; cursor:pointer; }
    .mf-workspace__empty-panel { margin:28px 8px; color:#8d8992; text-align:center; font-size:13px; }
    .mf-workspace__element-list { display:grid; grid-template-columns:minmax(0,1fr); gap:4px; }
    .mf-workspace__drawer .mf-workspace__element.media-ui-button { display:grid; grid-template-columns:32px minmax(0,1fr); align-items:center; gap:8px; width:100%; min-width:0; height:40px; min-height:40px; border:0; border-radius:4px!important; padding:4px; color:#D7D8D9!important; background:transparent!important; box-shadow:none!important; cursor:pointer; text-align:left; white-space:nowrap; }
    .mf-workspace__element-group-members { min-width:0; padding-top:4px; }
    .mf-workspace[data-mf-sidebar-grid="false"] .mf-workspace__element-group-members .mf-workspace__element.media-ui-button { padding-left:52px; }
    .mf-workspace[data-mf-sidebar-grid="true"] .mf-workspace__element-group-members { padding-left:28px; }
    .mf-workspace__element-group-members > p { margin:0; padding:8px 8px 8px 52px; }
    .mf-workspace__drawer .mf-workspace__element.media-ui-button:hover { background:#232323!important; }
    .mf-workspace__drawer .mf-workspace__element.media-ui-button[aria-pressed="true"] { background:#2c2c2c!important; }
    .mf-workspace__drawer .mf-workspace__element.media-ui-button:focus-visible { outline:1px solid #D7D8D9; outline-offset:-1px; }
    .mf-workspace__element-thumbnail { display:grid; place-items:center; width:32px; height:32px; overflow:hidden; border-radius:4px; background:#232323; color:#D7D8D9; pointer-events:none; }
    .mf-workspace__element-media { display:block; width:100%; height:100%; object-fit:cover; }
    .mf-workspace__element-icon { display:grid; place-items:center; color:#D7D8D9; }
    .mf-workspace__element-icon img { filter:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='tint' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.843137255 0 0 0 0 0.847058824 0 0 0 0 0.850980392 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#tint"); }
    .mf-workspace__element-copy { display:grid; min-width:0; }
    .mf-workspace__element-copy strong { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-size:14px; line-height:20px; font-weight:400; }
    .mf-workspace__drawer .mf-workspace__element-toolbar { min-height:28px; margin-bottom:16px; }
    .mf-workspace .mf-workspace__sidebar .mf-workspace__element-filter { display:flex; align-items:center; gap:4px; min-width:0; height:28px; padding:0 4px; border:0!important; border-radius:4px; color:#aaa!important; background:transparent!important; box-shadow:none!important; font-size:12px; }
    .mf-workspace .mf-workspace__sidebar .mf-workspace__element-filter:hover { background:#232323!important; }
    .mf-workspace__element-filter:focus-visible { outline:1px solid #D7D8D9; outline-offset:-1px; }
    .mf-empty-state { left:50%; top:calc(50% - 32px); display:flex; flex-direction:column; align-items:center; gap:8px; width:min(620px,calc(100% - 48px)); transform:translate(-50%,-50%); text-align:center; pointer-events:none; }
    .mf-workspace[data-mf-sidebar-expanded="true"] .mf-empty-state { left:calc(50% + var(--mf-sidebar-width,300px) / 2); width:min(620px,calc(100% - var(--mf-sidebar-width,300px) - 48px)); }
    .mf-empty-state__gesture { display:flex; align-items:center; justify-content:center; gap:8px; margin:0; color:#a0a0a0; font-size:13px; font-weight:400; line-height:20px; }
    .mf-empty-state__gesture-key { display:inline-flex; flex:none; align-items:center; justify-content:center; gap:2px; width:92px; height:32px; border:1px solid #3f3f3f; border-radius:8px; background:#2c2c2c; color:#f6f6f6; font-weight:500; }
    .mf-empty-state__gesture-key .mf-workspace__tool-icon { width:16px; height:16px; color:#fff; }
    .mf-empty-state__actions { display:flex; justify-content:center; flex-wrap:wrap; gap:8px; }
    .mf-empty-state .mf-empty-state__action { --mf-workspace-cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:4px; height:32px; min-height:32px; max-width:100%; border:1px solid #3f3f3f!important; border-radius:8px; padding:0 12px; color:#a0a0a0!important; background:#141414!important; font-size:13px; font-weight:400; line-height:18px; pointer-events:auto; }
    .mf-empty-state .mf-empty-state__action:not(:disabled):hover { color:#f6f6f6!important; border-color:#555!important; background:#292929!important; }
    .mf-empty-state .mf-empty-state__action:disabled { --mf-workspace-cursor:not-allowed; }
    .mf-node-selector__anchor { position:absolute; width:0; height:0; pointer-events:none; }
    .mf-workspace-menu.mf-node-selector { box-sizing:border-box; max-height:calc(100dvh - 32px); padding:4px; border:1px solid #333; border-radius:16px; background:#1a1a1a; color:#bfbfbf; overflow-y:auto; overscroll-behavior:contain; outline:none; }
    .mf-node-selector__heading { display:flex; align-items:center; height:40px; margin:0; padding:0 8px; color:#777; font-size:14px; font-weight:400; }
    .mf-node-selector__group { display:grid; gap:4px; }
    .mf-node-selector .mf-node-selector__option { display:grid; grid-template-columns:36px minmax(0,1fr); align-items:center; column-gap:8px; height:52px; white-space:normal; border-radius:16px; color:#f6f6f6; }
    .mf-node-selector .mf-node-selector__option:is(:hover,:focus-visible,[data-selected="true"]):not(:disabled) { background:#2c2c2c; color:#f6f6f6; }
    .mf-node-selector__option:not(:disabled) .mf-node-selector__icon img { filter:brightness(0) invert(96.47058824%); }
    .mf-node-selector .mf-node-selector__option:disabled { opacity:1; color:#686868; background:transparent; }
    .mf-node-selector__icon { position:relative; display:grid; place-items:center; width:36px; height:36px; border-radius:11px; background:#292929; color:inherit; }
    .mf-node-selector__copy { display:grid; gap:2px; min-width:0; }
    .mf-node-selector__copy strong { display:flex; align-items:center; gap:5px; font-size:14px; line-height:20px; font-weight:600; }
    .mf-node-selector__copy small { display:none; overflow:hidden; color:#777; font-size:12px; line-height:18px; text-overflow:ellipsis; white-space:nowrap; }
    .mf-node-selector__option:is(:hover,:focus-visible,[data-selected="true"]):not(:disabled) .mf-node-selector__copy small { display:block; }
    .mf-minimap { left:24px; bottom:72px; width:220px; height:150px; overflow:hidden; border:1px solid rgba(255,255,255,.1); border-radius:14px; background:rgba(36,36,36,.98); box-shadow:0 12px 30px rgba(0,0,0,.34); cursor:crosshair; touch-action:none; user-select:none; }
    .mf-workspace[data-mf-sidebar-expanded="true"] .mf-minimap { left:calc(var(--mf-sidebar-width,300px) + 24px); }
    .mf-minimap svg { display:block; outline:none; }
    .mf-minimap:focus-within { outline:2px solid rgba(190,190,190,.68); outline-offset:2px; }
    .mf-minimap[data-mf-minimap-dragging="true"] { cursor:grabbing; }
    .mf-minimap__node { fill:#888; stroke:#242424; stroke-width:1; }
    .mf-minimap__mask { fill:rgba(9,9,9,.52); pointer-events:none; }
    .mf-minimap__viewport { fill:none; stroke:rgba(190,190,190,.68); stroke-width:2; pointer-events:none; }
    .mf-connect-dialog { color:#eeeaf3; }
    .mf-connect-dialog h2 { margin:0 0 7px; font-size:18px; }
    .mf-connect-dialog p { color:#a9a4af; font-size:12px; line-height:1.55; }
    .mf-connect-dialog__section { display:grid; gap:7px; margin-top:16px; }
    .mf-connect-dialog__section h3 { margin:0 0 3px; font-size:13px; }
    .mf-connect-dialog__action { min-height:40px; border:1px solid rgba(255,255,255,.1); border-radius:8px; padding:8px 10px; color:#eeeaf3; background:#2b2a2e; cursor:pointer; text-align:left; }
    .mf-connect-dialog__action--delete { color:#ffc1c6; background:#34252a; }
    .mf-connect-dialog__footer { display:flex; justify-content:flex-end; margin-top:18px; }
    .mf-connect-dialog__footer button { min-height:36px; border:0; border-radius:8px; padding:0 14px; color:#17131c; background:#f1eef4; cursor:pointer; }
    @media (max-width:767px) { .mf-workspace { --mf-toolbar-height:40px; } .mf-workspace[data-mf-sidebar-expanded="true"] .mf-empty-state { display:none; } .mf-workspace__tool:disabled { display:none; } .mf-minimap { display:none; } }
    @media (prefers-reduced-motion:reduce) { .mf-workspace *, .mf-workspace *::before, .mf-workspace *::after { animation-duration:.01ms !important; transition-duration:.01ms !important; scroll-behavior:auto !important; } }
    .mf-workspace[data-mf-workspace="true"] :is(.mf-workspace__sidebar button, .mf-workspace__project button, .mf-workspace__sidebar-toggle):not(:disabled):not([aria-disabled="true"]) { --mf-workspace-cursor:pointer; }
    .mf-workspace[data-mf-workspace="true"] :is(.mf-workspace__sidebar button, .mf-workspace__project button, .mf-workspace__sidebar-toggle):is(:disabled, [aria-disabled="true"]) { --mf-workspace-cursor:not-allowed; }
    :is(.mf-node, .mf-prompt, .mf-node-toolbar, .media-ui-overlay) :is(button, select, input[type="range"], input[type="checkbox"], input[type="radio"], [role="button"], [role="option"], [role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="tab"], [role="switch"]):not(:disabled):not([aria-disabled="true"]) { --mf-workspace-cursor:pointer; }
    :is(.mf-node, .mf-prompt, .mf-node-toolbar, .media-ui-overlay) :is(button, select, input, [role="button"], [role="option"], [role="menuitem"], [role="menuitemradio"], [role="menuitemcheckbox"], [role="tab"], [role="switch"]):is(:disabled, [aria-disabled="true"]) { --mf-workspace-cursor:not-allowed; }
    .mf-workspace[data-mf-workspace="true"], .mf-workspace[data-mf-workspace="true"] *, .media-ui-overlay, .media-ui-overlay * { cursor: var(--mf-workspace-cursor, default) !important; }
    .mf-workspace[data-mf-workspace="true"] :is(textarea:not(:disabled), input:not([type="button"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not(:disabled), [contenteditable="true"], [role="textbox"]), .media-ui-overlay :is(textarea:not(:disabled), input:not([type="button"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]):not(:disabled), [contenteditable="true"], [role="textbox"]) { cursor: text !important; }
    .mf-node[data-mf-node-shell="text"][data-mf-node-editing="true"] .mf-node__content-card, .mf-node[data-mf-node-shell="text"][data-mf-node-editing="true"] .mf-node__content-card *:not(:is(button, a, input, textarea, select, [role="button"], [contenteditable="true"])) { cursor: text !important; }
    .mf-workspace[data-mf-workspace="true"] .mf-text-toolbar button:not(:disabled), .mf-workspace[data-mf-workspace="true"] .mf-text-toolbar button:not(:disabled) *, .mf-workspace[data-mf-workspace="true"] .mf-workspace__toolbar .mf-workspace__tool:not(:disabled), .mf-workspace[data-mf-workspace="true"] .mf-workspace__toolbar .mf-workspace__tool:not(:disabled) * { cursor:pointer!important; }
    .mf-workspace[data-mf-workspace="true"] .mf-workspace__element:not(:disabled), .mf-workspace[data-mf-workspace="true"] .mf-workspace__element:not(:disabled) * { cursor:pointer!important; }
    .mf-workspace[data-mf-workspace="true"] .mf-text-toolbar button:disabled, .mf-workspace[data-mf-workspace="true"] .mf-text-toolbar button:disabled *, .mf-workspace[data-mf-workspace="true"] .mf-workspace__toolbar .mf-workspace__tool:disabled, .mf-workspace[data-mf-workspace="true"] .mf-workspace__toolbar .mf-workspace__tool:disabled * { cursor:not-allowed!important; }
    .mf-workspace[data-mf-workspace="true"] .mf-text-toolbar button:not(:disabled):is(:hover,:focus-visible), .mf-workspace[data-mf-workspace="true"] .mf-text-toolbar button[aria-pressed="true"] { color:#f7f5fa!important; background:#252525!important; }
  ` }} />
}

function SidebarIcon({ collapsed }: { readonly collapsed: boolean }) {
  return <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d={collapsed ? "M4 12L8 8L4 4" : "M12 4L8 8L12 12"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="0.75" y="0.75" width="14.5" height="14.5" rx="3.25" stroke="currentColor" strokeWidth="1.5" />
    <line x1={collapsed ? "11.25" : "4.75"} y1="15" x2={collapsed ? "11.25" : "4.75"} y2="1" stroke="currentColor" strokeWidth="1.5" />
  </svg>
}

type CanvasToolIconName = 'pointer' | 'grid' | 'comfyui' | 'infinite-canvas' | 'minimap' | 'connections' | 'fit'

const CANVAS_TOOL_ICON_PATHS: Readonly<Record<CanvasToolIconName, readonly string[]>> = {
    pointer: [
      'M687.104 687.936l-81.92 179.84c-16.576 36.608-39.744 51.2-69.568 43.712a53.568 53.568 0 0 1-35.712-28.48 54.144 54.144 0 0 1-4.16-10.944L385.024 446.72l-0.576-2.176a61.44 61.44 0 0 1 2.688-47.104 54.592 54.592 0 0 1 19.328-20.608l1.216-0.64a62.72 62.72 0 0 1 51.968-2.944l2.24 0.64 56.128 16.192 361.984 104.064a40.96 40.96 0 0 1 2.88 0.896l2.56 1.024a54.528 54.528 0 0 1 29.184 74.112l-0.128 0.192-0.64 1.344a61.184 61.184 0 0 1-30.656 26.56l-0.896 0.384-195.2 89.216z m100.864-135.68L638.528 620.672l-13.952 6.336-68.352 150.144L474.24 462.08l20.8 6.016L787.84 552.32h0.064z',
      'M100.736 394.752c-0.064-157.312 125.312-286.976 284.672-294.4 159.488-7.424 296.704 110.08 311.68 266.624l-86.016-17.408a212.352 212.352 0 0 0-10.24-33.28C540.928 167.68 342.656 134.4 236.352 255.104 130.048 375.872 191.36 564.736 348.928 602.24l22.848 85.952c-153.6-14.208-271.04-141.312-271.04-293.376v-0.064z',
      'M548.608 354.432l-152.96-32.256c-42.624-9.088-81.088 27.2-73.856 69.632l24.704 146.176c-108.736-39.168-136.768-177.92-51.584-255.232 85.12-77.312 223.104-38.336 253.76 71.744l-0.064-0.064z',
    ],
    grid: ['M629.467429 77.312v238.592H390.875429V77.312h238.592z m76.8 0h202.093714a38.4 38.4 0 0 1 38.326857 38.326857V315.977143H706.194286V77.312z m-76.8 315.318857V631.222857H390.875429V392.630857h238.592z m76.8 238.592V392.630857h240.420571V631.222857H706.194286z m-76.8 76.8v239.177143H390.875429V708.022857h238.592z m76.8 239.177143V708.022857h240.420571v200.850286a38.4 38.4 0 0 1-38.326857 38.4H706.194286zM115.053714 77.385143h198.948572v238.592H76.8V115.565714a38.4 38.4 0 0 1 38.4-38.326857zM76.8 392.630857h237.348571V631.222857H76.8V392.630857z m0 315.392h237.348571v239.177143h-198.948571a38.4 38.4 0 0 1-38.4-38.326857V708.022857zM115.126857 0.438857A115.126857 115.126857 0 0 0 0 115.638857v793.161143A115.126857 115.126857 0 0 0 115.126857 1024H908.434286a115.126857 115.126857 0 0 0 115.126857-115.126857V115.638857A115.126857 115.126857 0 0 0 908.361143 0.438857H115.126857z'],
    comfyui: ['M855.771429 64l11.556571 0.512c27.062857 2.486857 52.955429 13.750857 71.460571 36.571429 19.529143 23.917714 24.649143 54.125714 19.090286 82.285714l-2.925714 11.922286-37.888 122.294857a148.699429 148.699429 0 0 1-55.808 74.898285 153.892571 153.892571 0 0 1-88.649143 29.842286L591.725714 422.765714h-68.461714l-55.588571 178.614857 88.649142-0.219428H688.274286c31.158857 0 61.952 10.971429 83.090285 37.083428 22.381714 27.428571 25.746286 62.976 16.091429 94.354286l-37.814857 122.148572a148.845714 148.845714 0 0 1-55.808 74.971428 153.892571 153.892571 0 0 1-88.722286 29.842286v0.073143l-180.809143 0.365714H292.352c-31.232 0-61.878857-11.117714-83.017143-37.083429-22.308571-27.428571-25.746286-62.976-16.091428-94.354285l14.848-47.981715h-39.789715c-31.305143 0-61.952-11.117714-83.163428-37.010285v-0.146286C62.902857 716.068571 59.392 680.594286 69.046857 649.362286v-0.073143l84.406857-271.506286 9.289143-29.842286c9.728-31.232 31.158857-57.051429 55.734857-74.825142 24.795429-17.846857 56.027429-29.988571 88.722286-29.988572h68.461714l22.820572-73.874286c9.654857-31.232 31.085714-57.124571 55.808-74.971428A154.038857 154.038857 0 0 1 542.939429 64.365714l180.662857-0.365714H855.771429zM543.012571 153.819429h-0.073142a64.731429 64.731429 0 0 0-36.352 13.092571 63.049143 63.049143 0 0 0-21.284572 25.234286l-1.316571 3.437714-27.794286 90.331429a67.145143 67.145143 0 0 1-64.585143 46.665142H307.2c-10.825143 0-24.137143 4.388571-36.205714 13.092572a59.684571 59.684571 0 0 0-22.674286 28.818286l-9.362286 29.769142-84.333714 271.36a19.894857 19.894857 0 0 0-0.950857 8.777143 5.485714 5.485714 0 0 0 1.024 2.633143l1.024 0.950857c1.462857 1.243429 4.973714 2.925714 12.580571 2.925715h70.509715c7.460571 0 14.848 1.243429 21.942857 3.657142l6.948571 2.779429 6.656 3.437714c4.388571 2.633143 8.338286 5.778286 11.995429 9.362286l5.266285 5.558857v0.146286a64.365714 64.365714 0 0 1 11.337143 20.553143l2.048 7.753143 1.097143 7.972571a64.877714 64.877714 0 0 1-0.877714 15.798857l-1.901714 7.753143-24.502858 79.36a20.041143 20.041143 0 0 0-0.950857 8.704 5.558857 5.558857 0 0 0 0.877715 2.633143c0.731429 0.877714 3.510857 3.949714 13.604571 3.949714h131.730286l180.955428-0.292571c10.971429 0 24.356571-4.388571 36.425143-13.019429a59.684571 59.684571 0 0 0 22.528-28.672l37.888-122.294857a20.041143 20.041143 0 0 0 0.950857-8.630857 6.802286 6.802286 0 0 0-0.512-1.828572L701.878857 694.857143c-0.731429-0.877714-3.437714-4.022857-13.677714-4.022857h-131.657143l-119.588571 0.292571a68.461714 68.461714 0 0 1-52.809143-24.722286l-0.073143-0.146285a65.024 65.024 0 0 1-11.702857-59.977143l70.363428-226.450286 0.146286-0.292571a66.267429 66.267429 0 0 1 25.234286-34.157715l4.461714-2.779428a68.461714 68.461714 0 0 1 34.669714-9.362286h84.333715l180.882285-0.365714h0.146286l4.169143-0.219429a67.949714 67.949714 0 0 0 32.109714-12.8 59.538286 59.538286 0 0 0 22.674286-28.672l37.668571-122.294857a20.041143 20.041143 0 0 0 1.024-8.630857 5.339429 5.339429 0 0 0-0.950857-2.633143l-1.024-1.024a13.677714 13.677714 0 0 0-6.217143-2.413714L855.771429 153.6H723.529143l-180.443429 0.219429z'],
    'infinite-canvas': ['M0.036571 196.163139c0-21.638051 17.558149-39.1962 39.196201-39.1962h627.722045a39.1962 39.1962 0 0 1 0 78.465256H39.232772A39.1962 39.1962 0 0 1 0.036571 196.163139zM156.967083 39.232628a39.1962 39.1962 0 0 1 78.465255 0v117.734311H156.967083V39.232628zM823.885328 0.036428c21.638051 0 39.1962 17.558149 39.1962 39.1962v627.722045a39.1962 39.1962 0 1 1-78.465255 0V39.232628c0-21.638051 17.558149-39.1962 39.269055-39.1962zM980.742984 156.966939a39.1962 39.1962 0 0 1 0 78.465256h-117.661456V156.966939h117.661456zM1020.01204 823.885184a39.269056 39.269056 0 0 1-39.269056 39.196201H353.093794a39.1962 39.1962 0 1 1 0-78.465256h627.722046c21.638051 0 39.1962 17.558149 39.1962 39.269055zM863.081528 980.74284a39.1962 39.1962 0 1 1-78.465255 0v-117.661455h78.465255v117.661455zM196.163283 1020.011896a39.1962 39.1962 0 0 1-39.1962-39.269056V353.09365a39.1962 39.1962 0 0 1 78.465255 0v627.722046a39.1962 39.1962 0 0 1-39.269055 39.1962z', 'M39.232772 863.081385a39.1962 39.1962 0 1 1 0-78.465256h117.734311v78.465256H39.232772z'],
    minimap: ['M950.857143 36.571429a36.571429 36.571429 0 0 1 36.571428 36.571428v731.428572a36.571429 36.571429 0 0 1-36.571428 36.571428H689.664L345.965714 1019.830857a36.571429 36.571429 0 0 1-38.107428-2.633143l-256-182.857143A36.571429 36.571429 0 0 1 36.571429 804.571429V73.142857a36.571429 36.571429 0 0 1 57.856-29.769143l237.129142 169.325715L640.512 41.179429l4.169143-1.974858A36.571429 36.571429 0 0 1 658.285714 36.571429h292.571429zM346.916571 288.036571a36.644571 36.644571 0 0 1-39.058285-2.194285L109.714286 144.164571v641.462858l222.500571 158.866285 331.702857-172.397714 8.118857-3.072a36.717714 36.717714 0 0 1 8.777143-1.097143H914.285714v-658.285714H667.794286L346.989714 288.036571z', 'M658.285714 73.142857h73.142857v731.428572H658.285714V73.142857zM292.571429 219.428571h73.142857v731.428572H292.571429V219.428571z'],
    connections: ['M292.571429 182.857143A109.714286 109.714286 0 1 0 182.857143 292.571429v73.142857a182.857143 182.857143 0 1 1 0-365.714286 182.857143 182.857143 0 0 1 0 365.714286V292.571429A109.714286 109.714286 0 0 0 292.571429 182.857143zM950.857143 841.142857a109.714286 109.714286 0 1 0-109.714286 109.714286v73.142857a182.857143 182.857143 0 1 1 0-365.714286 182.857143 182.857143 0 0 1 0 365.714286v-73.142857a109.714286 109.714286 0 0 0 109.714286-109.714286z', 'M519.606857 201.142857c101.010286 16.164571 180.809143 98.084571 180.589714 178.249143-0.219429 79.725714-80.018286 162.962286-179.492571 182.857143-58.075429 11.629714-88.722286 57.929143-90.550857 106.715428-1.828571 48.493714 25.014857 94.939429 81.92 108.470858L670.573714 804.571429v80.530285l-175.396571-36.571428c-95.817143-22.747429-140.946286-104.301714-138.093714-182.345143 2.925714-77.750857 53.467429-156.525714 149.211428-175.762286 60.489143-12.068571 115.273143-60.708571 115.419429-111.030857 0.146286-50.029714-53.76-96.402286-113.737143-106.057143L329.142857 256l36.571429-73.142857 153.892571 18.285714z'],
    fit: ['M292.571429 182.857143A109.714286 109.714286 0 1 0 182.857143 292.571429v73.142857a182.857143 182.857143 0 1 1 0-365.714286 182.857143 182.857143 0 0 1 0 365.714286V292.571429A109.714286 109.714286 0 0 0 292.571429 182.857143zM950.857143 182.857143A109.714286 109.714286 0 1 0 841.142857 292.571429v73.142857a182.857143 182.857143 0 1 1 0-365.714286 182.857143 182.857143 0 0 1 0 365.714286V292.571429A109.714286 109.714286 0 0 0 950.857143 182.857143zM292.571429 841.142857A109.714286 109.714286 0 1 0 182.857143 950.857143v73.142857a182.857143 182.857143 0 1 1 0-365.714286 182.857143 182.857143 0 0 1 0 365.714286v-73.142857A109.714286 109.714286 0 0 0 292.571429 841.142857z', 'M829.366857 355.986286l-475.428571 438.857143-49.590857-53.686858 475.428571-438.857142 49.590857 53.686857zM694.857143 146.285714v73.142857h-365.714286V146.285714h365.714286z', 'M658.285714 1024V658.285714l365.714286 190.025143L658.285714 1024z m83.017143-249.782857V914.285714l136.411429-73.142857-136.411429-66.925714z', 'M694.857143 804.571429v73.142857h-365.714286v-73.142857h365.714286z'],
}

function CanvasToolIcon({ name }: { readonly name: CanvasToolIconName }) {
  return <svg className="mf-workspace__tool-icon" viewBox="0 0 1024 1024" aria-hidden="true">{CANVAS_TOOL_ICON_PATHS[name].map((path, index) => <path key={index} d={path} fill="currentColor" />)}</svg>
}

function CanvasToolTooltip({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const trigger = isValidElement<{ onClick?: MouseEventHandler<HTMLElement> }>(children)
    ? cloneElement(children, {
      onClick: (event) => {
        setOpen(false)
        children.props.onClick?.(event)
      },
    })
    : children
  return <Tooltip title={label} placement="top" trigger={['hover']} mouseEnterDelay={0.25} open={open} onOpenChange={setOpen}>{trigger}</Tooltip>
}

export function MediaCanvasWorkspace({
  controller,
  referenceTargetNodeId,
  onExitReferenceSelection,
  onReferenceConnected,
  preferenceNamespace,
  nodeComponents,
  projectName = '未命名工作区',
  projectSwitcher,
  onRenameProject,
  onSendToAgent,
  onSaveNodeAsset,
  saveStatus,
  readOnly = false,
  viewportSize: requestedViewportSize,
  panelContent,
  renderNode,
  getElementPreviewUrl,
  onCreateIntent,
  onDropFiles,
  onRestoreEditing,
  telemetry,
}: MediaCanvasWorkspaceProps) {
  // Creation-surface registries merged with the administrator-managed catalog.
  // The upload entry always trails the (variable-length) selector list.
  const componentSurfaces = buildCanvasComponentSurfaces(nodeComponents)
  const selectorUploadIndex = componentSurfaces.selectorOptions.length
  const [, redraw] = useReducer((revision: number) => revision + 1, 0)
  const { preferences, setPreferences } = useMediaCanvasPreferences(preferenceNamespace)
  const [helpOpen, setHelpOpen] = useState(false)
  const [gridVisible, setGridVisible] = useState(true)
  const [fileDropActive, setFileDropActive] = useState(false)
  const [fileDropTargetNodeId, setFileDropTargetNodeId] = useState<string>()
  const [connectionsVisible, setConnectionsVisible] = useState(true)
  const [minimapVisible, setMinimapVisible] = useState(false)
  const [preferredSidebarWidth, setPreferredSidebarWidth] = useState(MIN_SIDEBAR_WIDTH)
  const [sidebarResizing, setSidebarResizing] = useState(false)
  const sidebarResizeGesture = useRef<{
    readonly pointerId: number
    readonly startClientX: number
    readonly startWidth: number
    readonly captureTarget: HTMLDivElement
  } | undefined>(undefined)
  const [panning, setPanning] = useState(false)
  const [spacePanActive, setSpacePanActive] = useState(false)
  const [nodeDragPreview, setNodeDragPreview] = useState<NodeDragPreview>()
  const [groupDragPreview, setGroupDragPreview] = useState<NodeDragPreview>()
  const [groupResizePreview, setGroupResizePreview] = useState<MediaCanvasGroup>()
  const [contextMenu, setContextMenu] = useState<{ point: MediaCanvasPoint; canvasPoint: MediaCanvasPoint; nodeId?: string }>()
  const [groupCancellation, cancelGroupEditing] = useReducer((value: number) => value + 1, 0)
  const groupResizeGesture = useRef<GroupResizeGesture | undefined>(undefined)
  const [selectionBox, setSelectionBox] = useState<SelectionBox>()
  const [selectionBounds, setSelectionBounds] = useState<SelectionBox>()
  const [createIntent, setCreateIntent] = useState<MediaCreateIntent>()
  const [pendingEditAction, setPendingEditAction] = useState<PendingEditAction>()
  const [connectTargetNodeId, setConnectTargetNodeId] = useState<string>()
  const [outputDragPreview, setOutputDragPreview] = useState<OutputDragPreview>()
  const [portHover, setPortHover] = useState<{ readonly nodeId: string; readonly port: 'input' | 'output'; readonly x: number; readonly y: number }>()
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string>()
  const [selectedEdgeId, setSelectedEdgeId] = useState<string>()
  const edgeDeleteKeyHeld = useRef(false)
  const [connectionHint, setConnectionHint] = useState<string>()
  const [selectorIndex, setSelectorIndex] = useState(-1)
  const selectorUploadRef = useRef<HTMLInputElement>(null)
  const selectorUploadPoint = useRef<MediaCanvasPoint | undefined>(undefined)
  const lastPanel = useRef<Exclude<MediaCanvasPanel, 'none'>>('elements')
  const [elementFilter, setElementFilter] = useState<MediaModality | 'all'>('all')
  const [elementFilterOpen, setElementFilterOpen] = useState(false)
  const [collapsedElementGroups, setCollapsedElementGroups] = useState<ReadonlySet<string>>(() => new Set())
  const reducedMotion = useSyncExternalStore(subscribeReducedMotion, reducedMotionSnapshot, () => true)
  const lastPointerPoint = useRef<MediaCanvasPoint | undefined>(undefined)
  const pendingOutputDrag = useRef<PendingOutputDrag | undefined>(undefined)
  const inputDragMoved = useRef(false)
  const connectionHintTimer = useRef<ReturnType<typeof globalThis.setTimeout> | undefined>(undefined)
  const panGesture = useRef<PanGesture | undefined>(undefined)
  const nodeDragGesture = useRef<NodeDragGesture | undefined>(undefined)
  const selectionBoxGesture = useRef<SelectionBoxGesture | undefined>(undefined)
  const spacePanReady = useRef(false)
  const pointerOverCanvas = useRef(false)
  const pointerOverBlankCanvas = useRef(false)
  const suppressContextMenu = useRef(false)
  const cancelWheelFrame = useRef<(() => void) | undefined>(undefined)
  const selectorRef = useRef<HTMLDivElement>(null)
  const workspaceRef = useRef<HTMLElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const drawerCloseRef = useRef<HTMLButtonElement>(null)
  const resourcePanelTriggerRef = useRef<HTMLButtonElement>(null)
  const restoreResourcePanelFocus = useRef(false)
  const returnFocusRef = useRef<HTMLElement | undefined>(undefined)
  const nodeRefs = useRef(new Map<string, HTMLElement>())
  const nodeEditorRefs = useRef(new Map<string, HTMLDivElement>())
  const pendingNodeFocusId = useRef<string | undefined>(undefined)
  const [observedViewportSize, setObservedViewportSize] = useState<MediaCanvasViewportSize>()
  const viewportSize = validViewportSize(requestedViewportSize)
    ? requestedViewportSize
    : observedViewportSize ?? FALLBACK_VIEWPORT_SIZE
  // On very narrow screens the 300px minimum takes precedence over the ratio.
  const maxSidebarWidth = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, Math.floor(viewportSize.width * 2 / 3)))
  const sidebarWidth = Math.min(preferredSidebarWidth, maxSidebarWidth)
  const clampSidebarWidth = (width: number) => Math.min(maxSidebarWidth, Math.max(MIN_SIDEBAR_WIDTH, width))
  const finishSidebarResize = () => {
    const gesture = sidebarResizeGesture.current
    sidebarResizeGesture.current = undefined
    setSidebarResizing(false)
    if (gesture) releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
  }
  const handleSidebarResizeStart = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || sidebarResizeGesture.current) return
    event.preventDefault()
    event.stopPropagation()
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      return
    }
    event.currentTarget.focus?.({ preventScroll: true })
    sidebarResizeGesture.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startWidth: sidebarWidth,
      captureTarget: event.currentTarget,
    }
    setSidebarResizing(true)
  }
  const handleSidebarResizeMove = (event: PointerEvent<HTMLDivElement>) => {
    const gesture = sidebarResizeGesture.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    event.preventDefault()
    event.stopPropagation()
    setPreferredSidebarWidth(clampSidebarWidth(gesture.startWidth + event.clientX - gesture.startClientX))
  }
  const handleSidebarResizeEnd = (event: PointerEvent<HTMLDivElement>) => {
    if (sidebarResizeGesture.current?.pointerId !== event.pointerId) return
    event.stopPropagation()
    finishSidebarResize()
  }
  const handleSidebarResizeKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const width = event.key === 'Home' ? MIN_SIDEBAR_WIDTH
      : event.key === 'End' ? maxSidebarWidth
        : event.key === 'ArrowLeft' ? sidebarWidth - 10
          : event.key === 'ArrowRight' ? sidebarWidth + 10
            : undefined
    if (width === undefined) return
    event.preventDefault()
    event.stopPropagation()
    setPreferredSidebarWidth(clampSidebarWidth(width))
  }
  const snapshot = controller.getSnapshot()
  const contextAssetNode = snapshot.nodes.find((node) => node.id === contextMenu?.nodeId && (node.data.outputs.length > 0 || node.modality === 'text' && Boolean(node.data.manual_text?.trim())))
  useEffect(() => {
    if (snapshot.panel === 'none') finishSidebarResize()
  }, [snapshot.panel])
  useEffect(() => () => {
    const gesture = sidebarResizeGesture.current
    sidebarResizeGesture.current = undefined
    if (gesture) releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
  }, [])
  const layoutZoom = Math.max(MIN_LAYOUT_ZOOM, snapshot.viewport.zoom)
  const transformZoom = snapshot.viewport.zoom / layoutZoom
  const zoomTier = getMediaCanvasZoomTier(snapshot.viewport.zoom)
  const zoomAllowsInteraction = isMediaCanvasEditableAtZoom(snapshot.viewport.zoom)
  const canMutate = !readOnly && zoomAllowsInteraction
  const nodeResize = useMediaNodeResize(controller, canMutate && !referenceTargetNodeId, `${preferences.interactionMode}:${preferenceNamespace}`)
  const selectedIds = new Set(snapshot.selectedNodeIds)
  const multipleObjectsSelected = snapshot.selectedNodeIds.length + snapshot.selectedGroupIds.length > 1
  const selectionChromeHidden = multipleObjectsSelected || snapshot.selectedGroupIds.length > 0 || Boolean(selectionBox)
  const primarySelectedNodeId = snapshot.selectedNodeIds[0]
  const flowingEdges = connectionsVisible && !reducedMotion
    ? snapshot.edges.filter((edge) => selectedIds.has(edge.source_node_uuid) || selectedIds.has(edge.target_node_uuid))
    : []
  const nodesById = new Map(snapshot.nodes.map((node) => [node.id, node]))
  const flowAnimationKey = flowingEdges.map((edge) => {
    const source = nodesById.get(edge.source_node_uuid)
    const target = nodesById.get(edge.target_node_uuid)
    const sourcePosition = nodeDragPreview?.[edge.source_node_uuid] ?? source?.position
    const targetPosition = nodeDragPreview?.[edge.target_node_uuid] ?? target?.position
    return `${edge.id}:${source?.modality}:${sourcePosition?.x}:${sourcePosition?.y}:${target?.modality}:${targetPosition?.x}:${targetPosition?.y}`
  }).sort().join(',')
  useEffect(() => {
    const canvas = canvasRef.current
    const view = canvas?.ownerDocument?.defaultView
    if (!canvas || !view || reducedMotion || !flowAnimationKey) return
    // Actual SVG units avoid older engines' inconsistent pathLength/dash normalization.
    // One RAF clock keeps all tails in phase without per-frame React updates.
    const paths: { element: SVGPathElement; length: number; period: number }[] = []
    canvas.querySelectorAll<SVGGElement>('[data-mf-edge-meteor]').forEach((group) => {
      const trails = group.querySelectorAll<SVGPathElement>('.mf-workspace-edge-meteor')
      const first = trails[0]
      if (!first || typeof first.getTotalLength !== 'function') return
      let total: number
      try { total = first.getTotalLength() } catch { return }
      if (!Number.isFinite(total) || total <= 0) return
      trails.forEach((element, index) => {
        const length = 10 * (1 - index / 16) * total / 100
        const period = 36 * total / 100
        element.setAttribute('stroke-dasharray', `${length} ${period - length}`)
        paths.push({ element, length, period })
      })
    })
    if (paths.length === 0) return
    let raf = 0
    const draw = (time: number) => {
      if (canvas.ownerDocument.visibilityState === 'hidden') return
      const phase = (time % 1400) / 1400
      paths.forEach(({ element, length, period }) => {
        element.setAttribute('stroke-dashoffset', String(length - phase * period))
      })
      raf = view.requestAnimationFrame(draw)
    }
    const resume = () => {
      view.cancelAnimationFrame(raf)
      if (canvas.ownerDocument.visibilityState !== 'hidden') raf = view.requestAnimationFrame(draw)
    }
    resume()
    canvas.ownerDocument.addEventListener('visibilitychange', resume)
    return () => {
      view.cancelAnimationFrame(raf)
      canvas.ownerDocument.removeEventListener('visibilitychange', resume)
    }
  }, [controller, flowAnimationKey, reducedMotion])
  const clearEdgeSelection = () => {
    setSelectedEdgeId(undefined)
    setHoveredEdgeId(undefined)
  }
  const nodeSelectionKey = snapshot.selectedNodeIds.join(',')
  const nodeIdsKey = snapshot.nodes.map((node) => node.id).join(',')
  const groupIdsKey = snapshot.groups.map((group) => group.uuid).join(',')
  const selectedEdgeExists = snapshot.edges.some((edge) => edge.id === selectedEdgeId)
  useEffect(() => {
    clearEdgeSelection()
  }, [controller, canMutate, connectionsVisible, nodeSelectionKey])
  useEffect(() => {
    if (!selectedEdgeExists) setSelectedEdgeId(undefined)
  }, [selectedEdgeExists])
  const deleteCanvasEdge = (edgeId: string) => {
    if (!canMutate) return
    controller.deleteEdge(edgeId)
    clearEdgeSelection()
    redraw()
  }
  useEffect(() => {
    const view = canvasRef.current?.ownerDocument?.defaultView
    if (!view) return
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (!canMutate || !connectionsVisible || event.defaultPrevented || event.isComposing
        || event.ctrlKey || event.metaKey || event.altKey
        || (event.key !== 'Backspace' && event.key !== 'Delete')
        || !event.target || isCanvasShortcutProtectedTarget(event.target)) return
      const target = event.target
      if (target !== view.document.body && target !== view.document.documentElement
        && !canvasRef.current?.contains(target as Node)) return
      if (!edgeDeleteKeyHeld.current && (!selectedEdgeId || !controller.getSnapshot().edges.some((edge) => edge.id === selectedEdgeId))) return
      event.preventDefault()
      event.stopPropagation()
      // Keep key repeats from falling through to node deletion after cutting the edge.
      edgeDeleteKeyHeld.current = true
      if (selectedEdgeId) deleteCanvasEdge(selectedEdgeId)
    }
    const releaseKey = () => { edgeDeleteKeyHeld.current = false }
    const onKeyUp = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Backspace' || event.key === 'Delete') releaseKey()
    }
    view.addEventListener('keydown', onKeyDown, true)
    view.addEventListener('keyup', onKeyUp)
    view.addEventListener('blur', releaseKey)
    return () => {
      view.removeEventListener('keydown', onKeyDown, true)
      view.removeEventListener('keyup', onKeyUp)
      view.removeEventListener('blur', releaseKey)
    }
  }, [controller, canMutate, connectionsVisible, selectedEdgeId])
  const restoreDialogFocus = () => {
    const target = returnFocusRef.current
    returnFocusRef.current = undefined
    const unavailable = Boolean(target && (
      ('disabled' in target && (target as HTMLButtonElement).disabled)
      || target.isConnected === false
    ))
    const destination = target && !unavailable ? target : canvasRef.current
    destination?.focus?.()
  }

  useEffect(() => {
    const element = workspaceRef.current
    if (!element) return
    const acceptSize = (size: MediaCanvasViewportSize) => {
      if (!validViewportSize(size)) return
      setObservedViewportSize((current) => current?.width === size.width && current.height === size.height ? current : size)
    }
    if (typeof element.getBoundingClientRect === 'function') {
      const rect = element.getBoundingClientRect()
      if (!validViewportSize(requestedViewportSize)) acceptSize({ width: rect.width, height: rect.height })
    }
    if (typeof ResizeObserver === 'undefined') return
    const view = element.ownerDocument?.defaultView
    let frame = 0
    // Sub-pixel card metrics feed back into --mf-node-content-width and jitter
    // the card forever; only commit whole-pixel changes that clear a dead zone.
    const lastMetrics = new WeakMap<HTMLElement, { readonly width: number; readonly height: number }>()
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.target || entry.target === element) {
          if (!validViewportSize(requestedViewportSize)) acceptSize({ width: entry.contentRect.width, height: entry.contentRect.height })
          return
        }
        const card = entry.target as HTMLElement
        if (!card) return
        const article = card.closest?.<HTMLElement>('[data-mf-workspace-node]')
        if (!article) return
        const borderBox = entry.borderBoxSize[0]
        const width = Math.round(borderBox?.inlineSize || card.offsetWidth || entry.contentRect.width)
        const height = Math.round(borderBox?.blockSize || card.offsetHeight || entry.contentRect.height)
        const last = lastMetrics.get(article)
        if (last && Math.abs(last.width - width) < 1 && Math.abs(last.height - height) < 1) return
        lastMetrics.set(article, { width, height })
        article.style.setProperty('--mf-node-content-width', `${width}px`)
        article.style.setProperty('--mf-node-port-y', `${card.offsetTop + height / 2}px`)
        article.style.setProperty('--mf-node-prompt-top', `${card.offsetTop + height}px`)
      })
      if (!view || frame) return
      frame = view.requestAnimationFrame(() => { frame = 0; redraw() })
    })
    observer.observe(element)
    nodeRefs.current.forEach((node) => {
      if (typeof node.querySelector !== 'function') return
      const card = node.querySelector('.mf-node__content-card, .mf-workspace-node__card')
      if (card) observer.observe(card)
    })
    return () => { observer.disconnect(); if (view && frame) view.cancelAnimationFrame(frame) }
  }, [requestedViewportSize?.height, requestedViewportSize?.width, nodeIdsKey])

  useEffect(() => {
    if (createIntent) (selectorRef.current as { focus?: () => void } | null)?.focus?.()
  }, [createIntent])

  useEffect(() => {
    const nodeId = pendingNodeFocusId.current
    if (!nodeId) return
    pendingNodeFocusId.current = undefined
    nodeRefs.current.get(nodeId)?.focus()
  })

  useEffect(() => {
    let cancelled = false
    // Tooltip focus handlers may flush synchronously, so run outside React's effect phase.
    globalThis.queueMicrotask(() => {
      if (cancelled) return
      if (snapshot.panel !== 'none') drawerCloseRef.current?.focus()
      else if (restoreResourcePanelFocus.current) {
        restoreResourcePanelFocus.current = false
        resourcePanelTriggerRef.current?.focus()
      }
    })
    return () => { cancelled = true }
  }, [snapshot.panel])

  useEffect(() => {
    if (!pendingOutputDrag.current) setPortHover(undefined)
  }, [snapshot.viewport.x, snapshot.viewport.y, snapshot.viewport.zoom, sidebarResizing])

  useEffect(() => {
    if (readOnly) {
      cancelGestures()
      controller.setEditingNode(undefined)
      const drag = nodeDragGesture.current
      nodeDragGesture.current = undefined
      if (drag) releaseCapturedPointer(drag.captureTarget, drag.pointerId)
      setNodeDragPreview(undefined)
      setPortHover(undefined)
      const shouldRestoreFocus = Boolean(createIntent || connectTargetNodeId)
      setCreateIntent(undefined)
      setConnectTargetNodeId(undefined)
      setOutputDragPreview(undefined)
      clearConnectionHint()
      if (shouldRestoreFocus) globalThis.queueMicrotask(restoreDialogFocus)
    }
  }, [readOnly])
  const closeCreateIntent = () => {
    setCreateIntent(undefined)
    globalThis.queueMicrotask(restoreDialogFocus)
  }
  const closeConnectionDialog = () => {
    setConnectTargetNodeId(undefined)
    globalThis.queueMicrotask(restoreDialogFocus)
  }
  const clearConnectionHint = () => {
    if (connectionHintTimer.current !== undefined) {
      globalThis.clearTimeout(connectionHintTimer.current)
      connectionHintTimer.current = undefined
    }
    setConnectionHint(undefined)
  }
  const showConnectionHint = (message: string) => {
    if (connectionHintTimer.current !== undefined) globalThis.clearTimeout(connectionHintTimer.current)
    setConnectionHint(message)
    connectionHintTimer.current = globalThis.setTimeout(() => {
      connectionHintTimer.current = undefined
      setConnectionHint(undefined)
    }, CONNECTION_HINT_VISIBLE_MS)
  }
  useEffect(() => () => {
    if (connectionHintTimer.current !== undefined) globalThis.clearTimeout(connectionHintTimer.current)
  }, [])
  const showCreateIntent = (intent: MediaCreateIntent, trigger?: HTMLElement) => {
    if (readOnly) return
    returnFocusRef.current = trigger ?? (typeof document === 'undefined' ? undefined : document.activeElement as HTMLElement | null) ?? undefined
    setCreateIntent(intent)
    setSelectorIndex(-1)
    onCreateIntent?.(intent)
  }
  const createNode = (modality: MediaModality, point: MediaCanvasPoint, creationMethod: MediaNodeCreationMethod) => {
    if (readOnly) return
    const node = controller.createNode({ modality, position: point })
    telemetry?.capture('media_node_created', { node_type: modality, creation_method: creationMethod })
    returnFocusRef.current = undefined
    pendingNodeFocusId.current = node.id
    setCreateIntent(undefined)
    redraw()
    return node
  }
  const clearSelectionAfterConnection = () => {
    controller.setSelection([])
    controller.setEditingNode(undefined)
    clearEdgeSelection()
    pendingNodeFocusId.current = undefined
    returnFocusRef.current = undefined
    canvasRef.current?.focus?.({ preventScroll: true })
  }
  const connectDraggedNodes = (sourceNodeId: string, targetNodeId: string) => {
    const source = controller.getSnapshot().nodes.find((node) => node.id === sourceNodeId)
    if (!source || readOnly) return
    const output = selectedOutput(source)
    const result = output
      ? controller.createConnection({ sourceNodeId, targetNodeId, sourcePort: 'output', targetPort: 'input', sourceOutput: output })
      : controller.createPendingConnection({ sourceNodeId, targetNodeId })
    const target = controller.getSnapshot().nodes.find((node) => node.id === targetNodeId)
    telemetry?.capture('media_edge_created', { source_type: source.modality, target_type: target?.modality ?? source.modality, accepted: result.ok })
    if (result.ok) {
      clearConnectionHint()
      clearSelectionAfterConnection()
    } else showConnectionHint(mediaConnectionFailureHint(source.name, result.reason))
    redraw()
    return result
  }
  const referenceTarget = !readOnly ? snapshot.nodes.find((node) => node.id === referenceTargetNodeId) : undefined
  const pickNodeReference = (sourceNodeId: string) => {
    if (!referenceTarget) return
    const source = controller.getSnapshot().nodes.find((node) => node.id === sourceNodeId)
    if (!source) return
    const output = selectedOutput(source)
    const result = output
      ? controller.createConnection({ sourceNodeId, targetNodeId: referenceTarget.id, sourcePort: 'output', targetPort: 'input', sourceOutput: output })
      : controller.createPendingConnection({ sourceNodeId, targetNodeId: referenceTarget.id })
    telemetry?.capture('media_edge_created', { source_type: source.modality, target_type: referenceTarget.modality, accepted: result.ok })
    if (!result.ok) showConnectionHint(mediaConnectionFailureHint(source.name, result.reason))
    else {
      clearConnectionHint()
      setConnectionsVisible(true)
      controller.setSelection([referenceTarget.id])
      controller.setEditingNode(referenceTarget.modality === 'text' ? undefined : referenceTarget.id)
      onReferenceConnected?.(referenceTarget.id)
    }
    redraw()
  }
  useEffect(() => {
    if (!referenceTargetNodeId || readOnly) return
    const view = workspaceRef.current?.ownerDocument?.defaultView
    const escape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing) return
      event.preventDefault()
      event.stopImmediatePropagation()
      onExitReferenceSelection?.()
    }
    view?.addEventListener('keydown', escape, true)
    return () => view?.removeEventListener('keydown', escape, true)
  }, [referenceTargetNodeId, onExitReferenceSelection, readOnly])
  useEffect(() => {
    clearConnectionHint()
    if (!referenceTargetNodeId) return
    cancelGestures()
    setCreateIntent(undefined)
    setConnectTargetNodeId(undefined)
    clearEdgeSelection()
    setConnectionsVisible(true)
  }, [referenceTargetNodeId])
  const dragEndpoints = (drag: Pick<PendingOutputDrag, 'sourceNodeId' | 'sourcePort'>, otherNodeId: string) => drag.sourcePort === 'input'
    ? { sourceNodeId: otherNodeId, targetNodeId: drag.sourceNodeId }
    : { sourceNodeId: drag.sourceNodeId, targetNodeId: otherNodeId }
  const createFromSelector = (modality: MediaModality) => {
    if (!createIntent || !selectorOptionIsCompatible({ modality })) return
    const creationMethod: MediaNodeCreationMethod = createIntent.kind === 'double-click'
      ? 'double_click'
      : createIntent.kind === 'output-drag' || createIntent.kind === 'input-drag'
        ? 'connection_drop'
        : 'keyboard'
    const node = createNode(modality, createIntent.point, creationMethod)
    if (!node) return
    if (createIntent.kind === 'output-drag' || createIntent.kind === 'input-drag') {
      const endpoints = dragEndpoints(createIntent, node.id)
      connectDraggedNodes(endpoints.sourceNodeId, endpoints.targetNodeId)
      return
    }
    if (modality === 'text') {
      controller.setSelection([node.id])
      controller.setEditingNode(undefined)
      redraw()
    }
  }
  const requestEditRecovery = (action: PendingEditAction): void => {
    if (!onRestoreEditing) return
    setPendingEditAction(action)
    void Promise.resolve(onRestoreEditing()).catch(() => {
      setPendingEditAction((pending) => pending === action ? undefined : pending)
    })
  }
  const createAtCenter = (modality: MediaModality) => {
    const point = viewportCenter(snapshot.viewport, viewportSize)
    if (readOnly) {
      requestEditRecovery({ kind: 'create', modality, point, method: 'toolbar' })
      return
    }
    createNode(modality, point, 'toolbar')
  }
  useEffect(() => {
    if (readOnly || !pendingEditAction) return
    setPendingEditAction(undefined)
    if (pendingEditAction.kind === 'create') {
      createNode(pendingEditAction.modality, pendingEditAction.point, pendingEditAction.method)
      return
    }
    showCreateIntent(pendingEditAction.intent)
  }, [pendingEditAction, readOnly])
  const togglePanel = (panel: Exclude<MediaCanvasPanel, 'none'>) => {
    const closing = snapshot.panel === panel
    lastPanel.current = panel
    controller.setPanel(closing ? 'none' : panel)
    if (closing) restoreResourcePanelFocus.current = true
    redraw()
  }
  const setZoom = (zoom: number) => {
    controller.setViewport({ ...controller.getSnapshot().viewport, zoom: clampZoom(zoom) })
    redraw()
  }
  const focusNode = (node: MediaCanvasNode) => {
    const current = controller.getSnapshot().viewport
    const zoom = Math.max(0.6, current.zoom)
    controller.setSelection([node.id])
    controller.setViewport({
      x: viewportSize.width / 2 - (node.position.x + NODE_WIDTH / 2) * zoom,
      y: viewportSize.height / 2 - (node.position.y + NODE_HEIGHT / 2) * zoom,
      zoom,
    })
    pendingNodeFocusId.current = node.id
    redraw()
  }
  const centerNode = (node: MediaCanvasNode, leftInset = 0) => {
    cancelWheelFrame.current?.()
    const current = controller.getSnapshot().viewport
    const bounds = getCardBounds(node.id) ?? defaultNodeCardBounds(node)
    controller.setSelection([node.id])
    controller.setViewport({
      x: (viewportSize.width + leftInset) / 2 - (bounds.left + bounds.width / 2) * current.zoom,
      y: viewportSize.height / 2 - (bounds.top + bounds.height / 2) * current.zoom,
      zoom: current.zoom,
    })
    redraw()
  }
  const focusNodeEditor = (nodeId: string) => {
    globalThis.queueMicrotask(() => {
      const region = nodeEditorRefs.current.get(nodeId)
      const control = region?.querySelector<HTMLElement>('textarea:not(:disabled), select:not(:disabled), input:not(:disabled), [contenteditable="true"]')
      control?.focus?.({ preventScroll: true })
    })
  }
  const requestNodeEdit = (node: MediaCanvasNode) => {
    if (!canMutate) return
    controller.setSelection([node.id])
    controller.setEditingNode(node.id)
    if (node.modality === 'text') {
      cancelWheelFrame.current?.()
      pendingNodeFocusId.current = undefined
    }
    redraw()
    if (node.modality !== 'text') focusNodeEditor(node.id)
  }
  const closeNodeEditor = (nodeId: string) => {
    if (controller.getSnapshot().editingNodeId !== nodeId) return
    controller.setEditingNode(undefined)
    redraw()
    globalThis.queueMicrotask(() => nodeRefs.current.get(nodeId)?.focus?.())
  }

  const finishPan = (captureTarget?: PointerCaptureTarget, pointerId?: number, commit = false) => {
    const gesture = panGesture.current
    if (!gesture || (pointerId !== undefined && pointerId !== gesture.pointerId)) return false
    panGesture.current = undefined
    if (gesture.mode === 'right') suppressContextMenu.current = commit && gesture.moved
    if (commit && gesture.mode === 'left' && !gesture.moved && !referenceTarget) {
      controller.setObjectSelection([], [])
      controller.setEditingNode(undefined)
      redraw()
    }
    releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
    setPanning(false)
    return true
  }
  const finishSelectionBox = (event: PointerEvent<HTMLDivElement>, commit: boolean) => {
    const gesture = selectionBoxGesture.current
    if (!gesture || gesture.pointerId !== event.pointerId) return false
    const box = normalizedSelectionBox(gesture.start, screenPointFromPointer(event))
    selectionBoxGesture.current = undefined
    setSelectionBox(undefined)
    releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
    if (commit) {
      const current = controller.getSnapshot()
      const intersecting = box.width < 3 && box.height < 3 ? [] : current.nodes
        .filter((node) => selectionBoxIntersectsNode(box, getCardBounds(node.id) ?? defaultNodeCardBounds(node), current.viewport))
        .map((node) => node.id)
      const containedGroups = box.width < 3 && box.height < 3 ? [] : current.groups.filter((group) => selectionBoxContainsNode(box, { left: group.position.x, top: group.position.y, width: group.width, height: group.height }, current.viewport)).map((group) => group.uuid)
      controller.setObjectSelection(gesture.additive ? [...gesture.initialSelection, ...intersecting] : intersecting,
        gesture.additive ? [...gesture.initialGroupSelection, ...containedGroups] : containedGroups)
      if (!gesture.additive && intersecting.length === 0) controller.setEditingNode(undefined)
      // Pointer selection prevents native focus; hand keyboard ownership back to the canvas.
      canvasRef.current?.focus?.({ preventScroll: true })
      redraw()
    }
    return true
  }
  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const mode = event.button === 1 ? 'middle'
      : event.button === 0 && spacePanReady.current ? 'space'
        : event.button === 2 ? 'right'
          : event.button === 0 && (referenceTarget || preferences.interactionMode === 'comfyui') && !event.shiftKey && !event.ctrlKey && !event.metaKey ? 'left' : undefined
    if (isCanvasShortcutProtectedTarget(event.target) || (eventHitsCanvasControl(event.target) && mode !== 'space' && mode !== 'middle')) return
    if (nodeResize.gesture.current || panGesture.current || nodeDragGesture.current || selectionBoxGesture.current || pendingOutputDrag.current) return
    const point = screenPointFromPointer(event)
    if (multipleObjectsSelected && selectionBounds && event.button === 0 && !spacePanReady.current && !event.shiftKey && !event.ctrlKey && !event.metaKey
      && point.x >= selectionBounds.left && point.x <= selectionBounds.left + selectionBounds.width
      && point.y >= selectionBounds.top && point.y <= selectionBounds.top + selectionBounds.height) {
      handleSelectionFramePointerDown(event)
      return
    }
    suppressContextMenu.current = false
    setContextMenu(undefined)
    if (createIntent) {
      setCreateIntent(undefined)
      returnFocusRef.current = undefined
    }
    cancelWheelFrame.current?.()
    if (!mode && event.button !== 0) return
    event.preventDefault()
    setPortHover(undefined)
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {
      // The gesture can still end through pointerup when capture is unavailable.
    }
    if (mode) {
      panGesture.current = {
        pointerId: event.pointerId,
        mode,
        moved: false,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startViewport: controller.getSnapshot().viewport,
        captureTarget: event.currentTarget,
      }
      setPanning(mode !== 'right')
      return
    }
    const start = screenPointFromPointer(event)
    selectionBoxGesture.current = {
      pointerId: event.pointerId,
      start,
      initialSelection: controller.getSnapshot().selectedNodeIds,
      initialGroupSelection: controller.getSnapshot().selectedGroupIds,
      additive: event.shiftKey || event.ctrlKey || event.metaKey,
      captureTarget: event.currentTarget,
    }
    setSelectionBox(normalizedSelectionBox(start, start))
  }
  const getCardBounds = (nodeId: string): NodeCardBounds | undefined => {
    const card = nodeRefs.current.get(nodeId)?.querySelector('.mf-node__content-card, .mf-workspace-node__card')
    const rect = card?.getBoundingClientRect()
    const canvasRect = canvasRef.current?.getBoundingClientRect()
    if (!rect?.width || !rect.height || !canvasRect) return undefined
    const viewport = controller.getSnapshot().viewport
    return {
      left: (rect.left - canvasRect.left - viewport.x) / viewport.zoom,
      top: (rect.top - canvasRect.top - viewport.y) / viewport.zoom,
      width: rect.width / viewport.zoom,
      height: rect.height / viewport.zoom,
      cornerRadius: card ? Number.parseFloat(card.ownerDocument?.defaultView?.getComputedStyle?.(card).borderTopLeftRadius ?? '0') || 0 : 25,
    }
  }
  const getPortAnchor = (node: Pick<MediaCanvasNode, 'id' | 'position'>, port: 'input' | 'output'): MediaCanvasPoint | undefined => {
    const nodeElement = nodeRefs.current.get(node.id)
    if (nodeResize.preview?.id === node.id) {
      const { position, size } = nodeResize.preview
      return { x: position.x + (Math.max(NODE_WIDTH, size.width) - size.width) / 2 + (port === 'output' ? size.width : 0), y: position.y + 28 + size.height / 2 }
    }
    if (!nodeElement || typeof nodeElement.querySelector !== 'function') return undefined
    const card = nodeElement.querySelector<HTMLElement>('.mf-node__content-card, .mf-workspace-node__card')
    if (!card || !card.offsetWidth || !card.offsetHeight) return undefined
    return {
      x: node.position.x + card.offsetLeft + (port === 'input' ? 0 : card.offsetWidth),
      y: node.position.y + card.offsetTop + card.offsetHeight / 2,
    }
  }
  const getNodeBounds = (): MediaCanvasNodeBoundsMap => Object.fromEntries(controller.getSnapshot().nodes.map((node) => {
    const bounds = getCardBounds(node.id) ?? defaultNodeCardBounds(node)
    return [node.id, { width: bounds.width, height: bounds.height, offsetX: bounds.left - node.position.x, offsetY: bounds.top - node.position.y }]
  }))
  useEffect(() => {
    if (!multipleObjectsSelected) { setSelectionBounds(undefined); return }
    const updateBounds = () => {
      const boxes = snapshot.nodes.filter((node) => selectedIds.has(node.id)).map((node) => {
        const measured = getCardBounds(node.id)
        const position = nodeDragPreview?.[node.id] ?? node.position
        return measured ?? defaultNodeCardBounds({ ...node, position })
      })
      for (const stored of snapshot.groups.filter((group) => snapshot.selectedGroupIds.includes(group.uuid))) {
        const group = groupResizePreview?.uuid === stored.uuid ? groupResizePreview : stored
        const position = groupDragPreview?.[group.uuid] ?? group.position
        boxes.push({ left: position.x, top: position.y, width: group.width, height: group.height })
      }
      if (boxes.length === 0) { setSelectionBounds(undefined); return }
      const { x, y, zoom } = snapshot.viewport
      const left = Math.min(...boxes.map((box) => box.left)) * zoom + x - 12
      const top = Math.min(...boxes.map((box) => box.top)) * zoom + y - 32
      const right = Math.max(...boxes.map((box) => box.left + box.width)) * zoom + x + 12
      const bottom = Math.max(...boxes.map((box) => box.top + box.height)) * zoom + y + 12
      const next = { left, top, width: right - left, height: bottom - top }
      setSelectionBounds((current) => current && current.left === next.left && current.top === next.top && current.width === next.width && current.height === next.height ? current : next)
    }
    updateBounds()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(updateBounds)
    for (const nodeId of snapshot.selectedNodeIds) {
      const card = nodeRefs.current.get(nodeId)?.querySelector('.mf-node__content-card, .mf-workspace-node__card')
      if (card) observer.observe(card)
    }
    return () => observer.disconnect()
  }, [snapshot.nodes, snapshot.groups, nodeSelectionKey, snapshot.selectedGroupIds.join(','), snapshot.viewport, nodeDragPreview, groupDragPreview, groupResizePreview, multipleObjectsSelected])
  useEffect(() => {
    if (selectionChromeHidden && snapshot.editingNodeId) { controller.setEditingNode(undefined); redraw() }
  }, [controller, selectionChromeHidden, snapshot.editingNodeId])
  const updatePortHover = (point: MediaCanvasPoint) => {
    const current = controller.getSnapshot()
    const zoom = current.viewport.zoom
    let nearest: { readonly nodeId: string; readonly port: 'input' | 'output'; readonly x: number; readonly y: number; readonly distance: number } | undefined
    for (const node of current.nodes) {
      const bounds = getCardBounds(node.id) ?? defaultNodeCardBounds(node)
      for (const port of ['input', 'output'] as const) {
        const borderAnchor = getPortAnchor(node, port)
        const center = borderAnchor
          ? { x: borderAnchor.x + (port === 'input' ? -22 : 22), y: borderAnchor.y }
          : mediaCanvasNodePortCenter(node, port)
        const dx = (point.x - center.x) * zoom
        const dy = (point.y - center.y) * zoom
        const borderX = port === 'input' ? bounds.left : bounds.left + bounds.width
        if (Math.abs(dx) > PORT_HOVER_HALF_WIDTH_SCREEN || Math.abs(dy) > Math.max(PORT_HOVER_HALF_HEIGHT_SCREEN, (bounds.height / 2 + 24) * zoom)
          || (port === 'input' ? point.x >= borderX : point.x <= borderX)) continue
        const distance = Math.hypot(dx, dy)
        if (nearest && nearest.distance <= distance) continue
        // Keep the 20-world-pixel button at least 10 screen pixels off the card.
        const clearance = 10 + 10 / zoom
        const x = port === 'input' ? Math.min(point.x, borderX - clearance) : Math.max(point.x, borderX + clearance)
        nearest = { nodeId: node.id, port, x: x - center.x, y: point.y - center.y, distance }
      }
    }
    setPortHover(nearest)
  }
  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    pointerOverCanvas.current = true
    pointerOverBlankCanvas.current = event.target === canvasRef.current
    if (groupResizeGesture.current && groupResizeGesture.current.pointerId === event.pointerId) { handleGroupResizeMove(event); return }
    if (nodeDragGesture.current && nodeDragGesture.current.pointerId === event.pointerId) { handleNodePointerMove(event); return }
    const gesture = panGesture.current
    if (gesture && gesture.pointerId === event.pointerId) {
      event.preventDefault()
      if (gesture.mode === 'right' && !gesture.moved && Math.max(Math.abs(event.clientX - gesture.startClientX), Math.abs(event.clientY - gesture.startClientY)) <= 4) return
      gesture.moved ||= event.clientX !== gesture.startClientX || event.clientY !== gesture.startClientY
      if (gesture.mode === 'right') { suppressContextMenu.current = true; setContextMenu(undefined) }
      setPanning(true)
      controller.setViewport({
        x: gesture.startViewport.x + event.clientX - gesture.startClientX,
        y: gesture.startViewport.y + event.clientY - gesture.startClientY,
        zoom: gesture.startViewport.zoom,
      })
      redraw()
      return
    }
    const selectionGesture = selectionBoxGesture.current
    if (selectionGesture && selectionGesture.pointerId === event.pointerId) {
      event.preventDefault()
      setSelectionBox(normalizedSelectionBox(selectionGesture.start, screenPointFromPointer(event)))
      return
    }
    const outputDrag = pendingOutputDrag.current
    if (outputDrag && outputDrag.pointerId === event.pointerId) {
      inputDragMoved.current = true
      event.preventDefault()
      const current = controller.getSnapshot()
      const point = canvasPointFromPointer(event, current.viewport)
      const hoverNodeId = findMediaCanvasConnectionTargetNodeId({
        nodes: current.nodes.filter((node) => controller.validatePendingConnection(dragEndpoints(outputDrag, node.id)).ok),
        point,
        // Hit testing stays on pre-drag bounds, never on the visually displaced card.
        getCardBounds: (nodeId) => outputDrag.cardBounds.get(nodeId),
        sourceNodeId: outputDrag.sourceNodeId,
        targetPort: outputDrag.sourcePort === 'input' ? 'output' : 'input',
        radius: PORT_CONNECT_RADIUS_SCREEN / current.viewport.zoom,
      })
      setOutputDragPreview({ sourceNodeId: outputDrag.sourceNodeId, sourcePort: outputDrag.sourcePort, point, ...(hoverNodeId ? { hoverNodeId } : {}) })
      return
    }
    const point = canvasPointFromPointer(event, controller.getSnapshot().viewport)
    lastPointerPoint.current = point
    if (!canMutate || nodeDragGesture.current || sidebarResizeGesture.current || event.buttons) {
      setPortHover(undefined)
      return
    }
    updatePortHover(point)
  }
  const handleDoubleClick = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || eventHitsCanvasControl(event.target) || isCanvasShortcutProtectedTarget(event.target)) return
    if (readOnly && !onRestoreEditing) return
    event.preventDefault()
    const intent = controller.createIntentFromDoubleClick(canvasPointFromPointer(event, controller.getSnapshot().viewport))
    if (readOnly) {
      requestEditRecovery({ kind: 'selector', intent })
      return
    }
    showCreateIntent(intent, event.currentTarget)
  }
  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (finishGroupResize(event, true)) return
    if (finishNodeDrag(event, true)) return
    if (finishPan(event.currentTarget, event.pointerId, true)) return
    if (finishSelectionBox(event, true)) return
    const pending = pendingOutputDrag.current
    if (!pending || (pending.pointerId !== undefined && pending.pointerId !== event.pointerId)) return
    pendingOutputDrag.current = undefined
    setPortHover(undefined)
    releaseCapturedPointer(pending.captureTarget, pending.pointerId)
    setOutputDragPreview(undefined)
    if (pending.sourcePort === 'input' && !inputDragMoved.current) return
    const snapshotAtDrop = controller.getSnapshot()
    const dropPoint = canvasPointFromPointer(event, snapshotAtDrop.viewport)
    const source = snapshotAtDrop.nodes.find((node) => node.id === pending.sourceNodeId)
    const validDropNodeId = findMediaCanvasConnectionTargetNodeId({
      nodes: snapshotAtDrop.nodes.filter((node) => controller.validatePendingConnection(dragEndpoints(pending, node.id)).ok),
      getCardBounds: (nodeId) => pending.cardBounds.get(nodeId),
      point: dropPoint,
      sourceNodeId: pending.sourceNodeId,
      targetPort: pending.sourcePort === 'input' ? 'output' : 'input',
      radius: PORT_CONNECT_RADIUS_SCREEN / snapshotAtDrop.viewport.zoom,
    })
    const portHitNodeId = validDropNodeId ?? findMediaCanvasConnectionTargetNodeId({
      nodes: snapshotAtDrop.nodes,
      getCardBounds,
      point: dropPoint,
      sourceNodeId: pending.sourceNodeId,
      radius: PORT_CONNECT_RADIUS_SCREEN / snapshotAtDrop.viewport.zoom,
    })
    if (readOnly) return
    if (pending.sourcePort === 'input') {
      if (portHitNodeId && source) {
        connectDraggedNodes(portHitNodeId, source.id)
      } else if (source && !eventHitsCanvasControl(event.target)) {
        showCreateIntent({ kind: 'input-drag', sourceNodeId: source.id, sourcePort: 'input', point: dropPoint }, event.currentTarget)
      }
      return
    }
    if (portHitNodeId && source) {
      // The gesture ended on a node that looked like a reference target. Every
      // rejection states its reason as safe copy; silence reads as a dead canvas.
      // A source without a persisted output creates a PENDING edge instead:
      // it binds automatically on the source's first output, so nodes are
      // connectable in any content state.
      const target = snapshotAtDrop.nodes.find((node) => node.id === portHitNodeId)
      const commitResult = (result: MediaConnectionResult) => {
        telemetry?.capture('media_edge_created', { source_type: source.modality, target_type: target?.modality ?? source.modality, accepted: result.ok })
        if (result.ok) {
          clearConnectionHint()
          clearSelectionAfterConnection()
        } else {
          showConnectionHint(mediaConnectionFailureHint(source.name, result.reason))
        }
        redraw()
      }
      const sourceOutput = selectedOutput(source)
      if (sourceOutput) {
        commitResult(controller.createConnection({
          sourceNodeId: source.id,
          targetNodeId: portHitNodeId,
          sourcePort: 'output',
          targetPort: 'input',
          // The full stored output (including index/content facts) must reach
          // the controller: a stripped pointer fails its same-facts gate even
          // though the version exists on the source node.
          sourceOutput: sourceOutput,
        }))
        return
      }
      commitResult(controller.createPendingConnection({ sourceNodeId: source.id, targetNodeId: portHitNodeId }))
      return
    }
    if (portHitNodeId) {
      redraw()
      return
    }
    if (eventHitsCanvasControl(event.target)) return
    const intent = controller.createIntentFromOutputDrag(
      pending.sourceNodeId,
      pending.sourcePort,
      dropPoint,
    )
    if (intent) showCreateIntent(intent, event.currentTarget)
  }
  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (finishGroupResize(event, false)) return
    cancelWheelFrame.current?.()
    if (finishNodeDrag(event, false)) return
    if (finishPan(event.currentTarget, event.pointerId)) return
    if (finishSelectionBox(event, false)) return
    const pending = pendingOutputDrag.current
    if (!pending || (pending.pointerId !== undefined && pending.pointerId !== event.pointerId)) return
    pendingOutputDrag.current = undefined
    setPortHover(undefined)
    releaseCapturedPointer(pending.captureTarget, pending.pointerId)
    setOutputDragPreview(undefined)
  }
  const handleLostPointerCapture = (event: PointerEvent<HTMLDivElement>) => {
    handleNodeLostPointerCapture(event)
    if (groupResizeGesture.current && groupResizeGesture.current.pointerId === event.pointerId) { groupResizeGesture.current = undefined; setGroupResizePreview(undefined) }
    if (panGesture.current?.pointerId === event.pointerId) {
      panGesture.current = undefined
      setPanning(false)
    }
    if (selectionBoxGesture.current?.pointerId === event.pointerId) {
      selectionBoxGesture.current = undefined
      setSelectionBox(undefined)
    }
    if (pendingOutputDrag.current?.pointerId === event.pointerId) {
      pendingOutputDrag.current = undefined
      setPortHover(undefined)
      setOutputDragPreview(undefined)
    }
  }
  useEffect(() => {
    const workspace = workspaceRef.current
    if (!workspace || typeof workspace.addEventListener !== 'function' || typeof workspace.removeEventListener !== 'function') return
    const preventBrowserZoom = (event: globalThis.WheelEvent) => {
      if (event.ctrlKey || event.metaKey) event.preventDefault()
    }
    // Keep browser zoom out of every workspace control; canvas zoom itself is
    // handled by the mode-aware capture listener below.
    workspace.addEventListener('wheel', preventBrowserZoom, { capture: true, passive: false })
    return () => workspace.removeEventListener('wheel', preventBrowserZoom, { capture: true })
  }, [])
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || typeof canvas.addEventListener !== 'function' || typeof canvas.removeEventListener !== 'function') return
    const view = canvas.ownerDocument?.defaultView
    let frame: number | undefined
    let queuedModifiedZoom = false
    let pendingViewport: MediaCanvasViewport | undefined
    let baseViewport: MediaCanvasViewport | undefined
    const sameViewport = (a: MediaCanvasViewport, b: MediaCanvasViewport) => a.x === b.x && a.y === b.y && a.zoom === b.zoom
    const cancel = () => {
      if (frame !== undefined) view?.cancelAnimationFrame(frame)
      frame = undefined
      queuedModifiedZoom = false
      pendingViewport = undefined
      baseViewport = undefined
    }
    const apply = () => {
      const next = pendingViewport
      const base = baseViewport
      cancel()
      const current = controller.getLocalUiSnapshot().viewport
      // A reset, fit-to-view or project update must not be overwritten by an older wheel frame.
      if (next && base && sameViewport(current, base) && !sameViewport(current, next)) {
        controller.setViewport(next)
        redraw()
      }
    }
    cancelWheelFrame.current = cancel
    const handleWheel = (event: globalThis.WheelEvent) => {
      if (!event.target || nodeResize.gesture.current || panGesture.current || nodeDragGesture.current || groupResizeGesture.current || selectionBoxGesture.current || pendingOutputDrag.current) return
      const target = event.target as Element
      const modifiedZoom = event.ctrlKey || event.metaKey
      const prompt = target.closest?.('.mf-node__prompt-anchor, [data-mf-fixed-prompt]')
      // A selected text card owns an ordinary wheel gesture so its fixed-height
      // result viewport scrolls instead of moving the canvas underneath it.
      // Modified wheel gestures remain canvas zoom, matching the rest of a node.
      if (!modifiedZoom && isSelectedTextNodeScrollTarget(target)) return
      // Editors keep ordinary scrolling. Only prompt regions opt into modified
      // canvas zoom; menus, players and other controls retain native behavior.
      if (target.closest?.('[role="dialog"], [role="listbox"], [role="menu"], .media-ui-overlay')
        || ((!modifiedZoom || !prompt) && (isCanvasShortcutProtectedTarget(target) || prompt))) return
      const rect = canvas.getBoundingClientRect()
      const input = {
        deltaX: event.deltaX ?? 0, deltaY: event.deltaY, deltaMode: event.deltaMode, pageHeight: rect.height,
        ctrlKey: Boolean(event.ctrlKey), metaKey: Boolean(event.metaKey), shiftKey: Boolean(event.shiftKey),
        point: { x: event.clientX - rect.left, y: event.clientY - rect.top },
      }
      const current = controller.getLocalUiSnapshot().viewport
      if (baseViewport && !sameViewport(current, baseViewport)) cancel()
      const supersedesPending = modifiedZoom && !queuedModifiedZoom
      const next = mediaCanvasWheelViewport(supersedesPending ? current : pendingViewport ?? current, preferences.interactionMode, input)
      if (!next) return
      event.preventDefault()
      // Ctrl/pinch takes precedence over ordinary wheel input within the same frame.
      if (queuedModifiedZoom && !modifiedZoom) return
      baseViewport = current
      pendingViewport = next
      queuedModifiedZoom = modifiedZoom
      if (frame !== undefined) return
      if (sameViewport(current, next)) { cancel(); return }
      if (view?.requestAnimationFrame) frame = view.requestAnimationFrame(apply)
      else apply()
    }
    canvas.addEventListener('wheel', handleWheel, { capture: true, passive: false })
    return () => {
      cancel()
      cancelWheelFrame.current = undefined
      canvas.removeEventListener('wheel', handleWheel, { capture: true })
    }
  }, [controller, preferences.interactionMode])
  const selectorOptionIsCompatible = (option: Pick<(typeof CREATABLE_NODE_OPTIONS)[number], 'modality'>): boolean => {
    if (createIntent?.kind !== 'output-drag' && createIntent?.kind !== 'input-drag') return true
    const source = controller.getSnapshot().nodes.find((node) => node.id === createIntent.sourceNodeId)
    return Boolean(source && (createIntent.sourcePort === 'input'
      ? canConnectMediaModalities(option.modality, source.modality)
      : canConnectMediaModalities(source.modality, option.modality)))
  }
  const selectorUploadEnabled = Boolean(onDropFiles && !readOnly && createIntent
    && (createIntent.kind === 'double-click' || createIntent.kind === 'keyboard'))
  const openSelectorUpload = () => {
    if (!selectorUploadEnabled || !createIntent) return
    selectorUploadPoint.current = createIntent.point
    selectorUploadRef.current?.click()
    closeCreateIntent()
  }
  const handleSelectorKey = (event: Pick<KeyboardEvent<HTMLDivElement>, 'key' | 'preventDefault'>): boolean => {
    if (!createIntent) return false
    if (event.key === 'Escape') {
      event.preventDefault()
      closeCreateIntent()
      return true
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const direction = event.key === 'ArrowDown' ? 1 : -1
      const optionCount = componentSurfaces.selectorOptions.length + 1
      const current = selectorIndex < 0 ? (direction === 1 ? -1 : optionCount) : selectorIndex
      for (let offset = 1; offset <= optionCount; offset += 1) {
        const candidate = (current + direction * offset + optionCount * 2) % optionCount
        const enabled = candidate === selectorUploadIndex
          ? selectorUploadEnabled
          : selectorOptionIsCompatible(componentSurfaces.selectorOptions[candidate])
        if (!enabled) continue
        setSelectorIndex(candidate)
        selectorRef.current?.querySelector<HTMLElement>(`[data-mf-selector-index="${candidate}"]`)?.focus?.({ preventScroll: false })
        break
      }
      return true
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (selectorIndex === selectorUploadIndex) openSelectorUpload()
      else {
        const option = componentSurfaces.selectorOptions[selectorIndex]
        if (option && selectorOptionIsCompatible(option)) createFromSelector(option.modality)
      }
      return true
    }
    return false
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (handleSelectorKey(event)) return
    if (isCanvasShortcutProtectedTarget(event.target)) return
    const target = keyboardTarget(event.target)
    const composing = event.nativeEvent?.isComposing ?? false
    if (event.key === ' ' && target === 'other' && !composing && !event.ctrlKey && !event.metaKey) {
      event.preventDefault()
      spacePanReady.current = true
      setSpacePanActive(true)
      return
    }
    if (event.key === 'Escape' && selectedEdgeId && !composing) {
      event.preventDefault()
      clearEdgeSelection()
      return
    }
    if (!composing && target === 'other' && (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'a') {
      event.preventDefault()
      const current = controller.getSnapshot()
      controller.setObjectSelection(current.nodes.map((node) => node.id), current.groups.map((group) => group.uuid))
      redraw(); return
    }
    if (event.key === 'ContextMenu' || event.shiftKey && event.key === 'F10') {
      event.preventDefault()
      const point = lastPointerPoint.current ?? viewportCenter(snapshot.viewport, viewportSize)
      setContextMenu({ point: { x: point.x * snapshot.viewport.zoom + snapshot.viewport.x, y: point.y * snapshot.viewport.zoom + snapshot.viewport.y }, canvasPoint: point, nodeId: snapshot.selectedNodeIds.length === 1 ? snapshot.selectedNodeIds[0] : undefined })
      return
    }
    if (event.key === 'Escape') { setContextMenu(undefined); cancelGestures(); return }
    if (readOnly) return
    if (!composing && target === 'other' && !event.ctrlKey && !event.metaKey && !event.altKey
      && (event.key === 'Backspace' || event.key === 'Delete')
      && (edgeDeleteKeyHeld.current || (connectionsVisible && selectedEdgeId))) {
      event.preventDefault()
      edgeDeleteKeyHeld.current = true
      if (selectedEdgeId) deleteCanvasEdge(selectedEdgeId)
      return
    }
    const result = controller.handleKeyboardShortcut({
      key: event.key,
      ctrlKey: event.ctrlKey,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      isComposing: composing,
      target,
      bounds: getNodeBounds(),
      point: lastPointerPoint.current ?? viewportCenter(controller.getSnapshot().viewport, viewportSize),
    })
    if (!result.handled) return
    event.preventDefault()
    if (result.action === 'create-intent') showCreateIntent(result.intent, event.currentTarget)
    redraw()
  }
  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' || event.key === 'Delete') edgeDeleteKeyHeld.current = false
    if (event.key !== ' ') return
    spacePanReady.current = false
    setSpacePanActive(false)
    if (panGesture.current?.mode === 'space') finishPan(event.currentTarget)
  }
  const cancelGestures = () => {
    nodeResize.cancel()
    cancelWheelFrame.current?.()
    suppressContextMenu.current = false
    spacePanReady.current = false
    setSpacePanActive(false)
    const pan = panGesture.current
    const drag = nodeDragGesture.current
    const selection = selectionBoxGesture.current
    const output = pendingOutputDrag.current
    const resize = groupResizeGesture.current
    groupResizeGesture.current = undefined
    panGesture.current = undefined
    nodeDragGesture.current = undefined
    selectionBoxGesture.current = undefined
    pendingOutputDrag.current = undefined
    for (const gesture of [pan, drag, selection, output, resize]) {
      if (gesture) releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
    }
    setPanning(false)
    setNodeDragPreview(undefined)
    setGroupDragPreview(undefined)
    setGroupResizePreview(undefined)
    setContextMenu(undefined)
    cancelGroupEditing()
    setSelectionBox(undefined)
    setOutputDragPreview(undefined)
    setPortHover(undefined)
    finishSidebarResize()
  }
  useEffect(() => {
    const nodeIds = new Set(controller.getSnapshot().nodes.map((node) => node.id))
    const drag = nodeDragGesture.current
    const output = pendingOutputDrag.current
    // A remote deletion can remove the captured element before lostpointercapture
    // reaches this canvas. Discard the whole preview rather than leaving a ghost drag.
    const groupIds = new Set(controller.getSnapshot().groups.map((group) => group.uuid))
    if (drag && (Object.keys(drag.startPositions).some((id) => !nodeIds.has(id)) || Object.keys(drag.startGroupPositions).some((id) => !groupIds.has(id)))
      || groupResizeGesture.current && !groupIds.has(groupResizeGesture.current.group.uuid)
      || output && !nodeIds.has(output.sourceNodeId)) cancelGestures()
  }, [controller, nodeIdsKey, groupIdsKey])
  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget as Node)) return
    cancelGestures()
  }
  useEffect(() => {
    cancelGestures()
    const view = canvasRef.current?.ownerDocument?.defaultView
    view?.addEventListener?.('blur', cancelGestures)
    return () => { view?.removeEventListener?.('blur', cancelGestures); cancelGestures() }
  }, [controller, preferences.interactionMode, preferenceNamespace])
  useEffect(() => {
    const view = canvasRef.current?.ownerDocument?.defaultView
    if (!view) return
    const releaseSpacePan = () => {
      spacePanReady.current = false
      setSpacePanActive(false)
      const gesture = panGesture.current
      if (gesture?.mode === 'space') {
        panGesture.current = undefined
        releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
        setPanning(false)
      }
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || event.code !== 'Space' || event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return
      const target = event.target
      if (!target) return
      const element = target as Element
      const canvasActionAwayFromPointer = pointerOverBlankCanvas.current
        && (element.tagName === 'BUTTON' || element.tagName === 'A')
        && !element.closest?.('[data-mf-prompt-panel], [role="dialog"], .media-ui-overlay')
      if (isCanvasShortcutProtectedTarget(target) && !canvasActionAwayFromPointer) return
      const document = view.document
      // The canvas need not be clicked first, but shortcuts in other UI keep their meaning.
      if (target !== document.body && target !== document.documentElement && !canvasRef.current?.contains(target as Node) && !pointerOverCanvas.current) return
      event.preventDefault()
      spacePanReady.current = true
      setSpacePanActive(true)
    }
    const onKeyUp = (event: globalThis.KeyboardEvent) => {
      if (event.code === 'Space' || event.key === ' ') releaseSpacePan()
    }
    view.addEventListener('keydown', onKeyDown)
    view.addEventListener('keyup', onKeyUp)
    view.addEventListener('blur', releaseSpacePan)
    return () => {
      view.removeEventListener('keydown', onKeyDown)
      view.removeEventListener('keyup', onKeyUp)
      view.removeEventListener('blur', releaseSpacePan)
    }
  }, [])

  const startSelectionDrag = (event: PointerEvent<HTMLElement>, nodeId = '') => {
    if (!canMutate) return
    const current = controller.getSnapshot()
    const groups = new Set(current.selectedGroupIds)
    const moving = current.nodes.filter((node) => current.selectedNodeIds.includes(node.id) || node.group_uuid && groups.has(node.group_uuid))
    cancelWheelFrame.current?.()
    setPortHover(undefined)
    try { event.currentTarget.setPointerCapture(event.pointerId) } catch { /* Document handlers finish uncaptured drags. */ }
    nodeDragGesture.current = {
      pointerId: event.pointerId, nodeId, startClientX: event.clientX, startClientY: event.clientY,
      startPositions: Object.fromEntries(moving.map((node) => [node.id, node.position])),
      startGroupPositions: Object.fromEntries(current.groups.filter((group) => groups.has(group.uuid)).map((group) => [group.uuid, group.position])),
      reassignNodeIds: moving.filter((node) => !node.group_uuid || !groups.has(node.group_uuid)).map((node) => node.id),
      bounds: getNodeBounds(), zoom: current.viewport.zoom, captureTarget: event.currentTarget,
    }
    setNodeDragPreview(nodeDragGesture.current.startPositions)
    setGroupDragPreview(nodeDragGesture.current.startGroupPositions)
  }
  const handleSelectionFramePointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0 || spacePanReady.current || event.shiftKey || event.ctrlKey || event.metaKey) return
    if (nodeResize.gesture.current || panGesture.current || nodeDragGesture.current || groupResizeGesture.current || selectionBoxGesture.current || pendingOutputDrag.current) return
    event.preventDefault(); event.stopPropagation()
    setContextMenu(undefined)
    canvasRef.current?.focus?.({ preventScroll: true })
    startSelectionDrag(event)
  }
  const handleNodePointerDown = (event: PointerEvent<HTMLElement>, node: MediaCanvasNode) => {
    if (spacePanReady.current || event.button !== 0 || nodeDragProtectedTarget(event.target)) return
    event.stopPropagation()
    if (nodeResize.gesture.current || panGesture.current || nodeDragGesture.current || groupResizeGesture.current || selectionBoxGesture.current || pendingOutputDrag.current) return
    const current = controller.getSnapshot()
    const selected = current.selectedNodeIds.includes(node.id)
    const modifier = event.ctrlKey || event.metaKey
    const nextSelection = modifier
      ? selected ? current.selectedNodeIds.filter((id) => id !== node.id) : [...current.selectedNodeIds, node.id]
      : event.shiftKey ? [...current.selectedNodeIds, node.id] : selected ? current.selectedNodeIds : [node.id]
    controller.setObjectSelection(nextSelection, modifier || event.shiftKey || selected ? current.selectedGroupIds : [])
    event.preventDefault()
    setContextMenu(undefined)
    redraw()
    if (modifier && selected) return
    startSelectionDrag(event, node.id)
    event.currentTarget.focus?.({ preventScroll: true })
  }
  const handleGroupPointerDown = (event: PointerEvent<HTMLElement>, group: MediaCanvasGroup) => {
    if (spacePanReady.current || event.button !== 0 || nodeDragProtectedTarget(event.target)) return
    event.stopPropagation(); event.preventDefault()
    if (nodeResize.gesture.current || panGesture.current || nodeDragGesture.current || groupResizeGesture.current || selectionBoxGesture.current || pendingOutputDrag.current) return
    const current = controller.getSnapshot()
    const selected = current.selectedGroupIds.includes(group.uuid)
    const additive = event.ctrlKey || event.metaKey || event.shiftKey
    controller.setObjectSelection(additive || selected ? current.selectedNodeIds : [],
      additive ? selected ? current.selectedGroupIds.filter((id) => id !== group.uuid) : [...current.selectedGroupIds, group.uuid] : selected ? current.selectedGroupIds : [group.uuid])
    if (!additive) controller.setEditingNode(undefined)
    setContextMenu(undefined); redraw()
    if (additive && selected) return
    startSelectionDrag(event)
    event.currentTarget.focus?.({ preventScroll: true })
  }
  const handleNodePointerMove = (event: PointerEvent<HTMLElement>) => {
    const gesture = nodeDragGesture.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    event.preventDefault(); event.stopPropagation()
    const delta = { x: (event.clientX - gesture.startClientX) / gesture.zoom, y: (event.clientY - gesture.startClientY) / gesture.zoom }
    setNodeDragPreview(mediaCanvasDragPositions(gesture.startPositions, delta))
    setGroupDragPreview(mediaCanvasDragPositions(gesture.startGroupPositions, delta))
  }
  const finishNodeDrag = (event: PointerEvent<HTMLElement>, commit: boolean) => {
    const gesture = nodeDragGesture.current
    if (!gesture || gesture.pointerId !== event.pointerId) return false
    event.preventDefault(); event.stopPropagation()
    const delta = { x: (event.clientX - gesture.startClientX) / gesture.zoom, y: (event.clientY - gesture.startClientY) / gesture.zoom }
    nodeDragGesture.current = undefined
    setNodeDragPreview(undefined); setGroupDragPreview(undefined)
    releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
    if (commit && canMutate && (delta.x !== 0 || delta.y !== 0)) controller.moveSelection({ nodes: mediaCanvasDragPositions(gesture.startPositions, delta), groups: mediaCanvasDragPositions(gesture.startGroupPositions, delta), reassignNodeIds: [...gesture.reassignNodeIds], bounds: gesture.bounds })
    redraw(); return true
  }
  const handleNodeLostPointerCapture = (event: PointerEvent<HTMLElement>) => {
    if (nodeDragGesture.current?.pointerId !== event.pointerId) return
    nodeDragGesture.current = undefined
    setNodeDragPreview(undefined); setGroupDragPreview(undefined)
  }
  const handleGroupResizeStart = (event: PointerEvent<HTMLElement>, group: MediaCanvasGroup, handle: MediaGroupResizeHandle) => {
    event.stopPropagation(); event.preventDefault()
    if (nodeResize.gesture.current || nodeDragGesture.current || panGesture.current || groupResizeGesture.current) return
    const panMode = event.button === 1 ? 'middle' : event.button === 0 && spacePanReady.current ? 'space' : undefined
    if (panMode) {
      cancelWheelFrame.current?.()
      try { event.currentTarget.setPointerCapture(event.pointerId) } catch { /* Pointer release still finishes panning. */ }
      panGesture.current = { pointerId: event.pointerId, mode: panMode, moved: false, startClientX: event.clientX, startClientY: event.clientY, startViewport: controller.getSnapshot().viewport, captureTarget: event.currentTarget }
      setPanning(true); return
    }
    if (!canMutate || event.button !== 0) return
    cancelWheelFrame.current?.()
    controller.setObjectSelection([], [group.uuid])
    try { event.currentTarget.setPointerCapture(event.pointerId) } catch { /* Document handlers finish uncaptured drags. */ }
    groupResizeGesture.current = { pointerId: event.pointerId, group, handle, startClient: { x: event.clientX, y: event.clientY }, zoom: snapshot.viewport.zoom, captureTarget: event.currentTarget }
    setGroupResizePreview(group); redraw()
  }
  const handleGroupResizeMove = (event: PointerEvent<HTMLElement>) => {
    const gesture = groupResizeGesture.current
    if (!gesture || gesture.pointerId !== event.pointerId) return
    event.preventDefault(); event.stopPropagation()
    setGroupResizePreview({ ...gesture.group, ...resizeMediaCanvasGroup(gesture.group, gesture.handle, { x: (event.clientX - gesture.startClient.x) / gesture.zoom, y: (event.clientY - gesture.startClient.y) / gesture.zoom }) })
  }
  const finishGroupResize = (event: PointerEvent<HTMLElement>, commit: boolean) => {
    const gesture = groupResizeGesture.current
    if (!gesture || gesture.pointerId !== event.pointerId) return false
    event.preventDefault(); event.stopPropagation()
    groupResizeGesture.current = undefined; setGroupResizePreview(undefined)
    releaseCapturedPointer(gesture.captureTarget, gesture.pointerId)
    if (commit && canMutate) controller.updateGroup(gesture.group.uuid, resizeMediaCanvasGroup(gesture.group, gesture.handle, { x: (event.clientX - gesture.startClient.x) / gesture.zoom, y: (event.clientY - gesture.startClient.y) / gesture.zoom }))
    redraw(); return true
  }
  useEffect(() => {
    if (!nodeDragPreview && !groupResizePreview) return
    const document = canvasRef.current?.ownerDocument
    if (!document) return
    const outside = (event: globalThis.PointerEvent) => !canvasRef.current?.contains(event.target as Node)
    const move = (event: globalThis.PointerEvent) => { if (outside(event)) { handleNodePointerMove(event as unknown as PointerEvent<HTMLElement>); handleGroupResizeMove(event as unknown as PointerEvent<HTMLElement>) } }
    const finish = (event: globalThis.PointerEvent) => { if (outside(event)) { finishNodeDrag(event as unknown as PointerEvent<HTMLElement>, event.type === 'pointerup'); finishGroupResize(event as unknown as PointerEvent<HTMLElement>, event.type === 'pointerup') } }
    document.addEventListener('pointermove', move); document.addEventListener('pointerup', finish); document.addEventListener('pointercancel', finish)
    return () => { document.removeEventListener('pointermove', move); document.removeEventListener('pointerup', finish); document.removeEventListener('pointercancel', finish) }
  }, [nodeDragPreview, groupResizePreview, canMutate])
  const handleNodeKeyDown = (event: KeyboardEvent<HTMLElement>, node: MediaCanvasNode, index: number) => {
    if (event.key === 'Escape' && controller.getSnapshot().editingNodeId === node.id) {
      event.preventDefault()
      event.stopPropagation()
      closeNodeEditor(node.id)
      return
    }
    if (event.nativeEvent?.isComposing || isCanvasShortcutProtectedTarget(event.target)) return
    if (event.key === 'Enter') {
      event.preventDefault()
      controller.setSelection([node.id])
      if (canMutate) requestNodeEdit(node)
      else redraw()
      return
    }
    const direction = event.key === 'ArrowRight' || event.key === 'ArrowDown'
      ? 1
      : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
        ? -1
        : 0
    if (direction === 0 || snapshot.nodes.length < 2) return
    event.preventDefault()
    const next = snapshot.nodes[(index + direction + snapshot.nodes.length) % snapshot.nodes.length]
    controller.setSelection([next.id])
    redraw()
    globalThis.queueMicrotask(() => nodeRefs.current.get(next.id)?.focus())
  }

  // Keep the creation menu beside the sidebar when the remaining canvas can
  // still fit a usable menu; only overlap the sidebar on truly tiny screens.
  const selectorMinLeft = snapshot.panel === 'none' || viewportSize.width - sidebarWidth < 168 ? 16 : sidebarWidth + 8
  const selectorWidth = Math.min(288, Math.max(0, viewportSize.width - selectorMinLeft - 16))
  const selectorLeft = createIntent
    ? Math.min(Math.max(selectorMinLeft, createIntent.point.x * snapshot.viewport.zoom + snapshot.viewport.x), Math.max(selectorMinLeft, viewportSize.width - selectorWidth - 16))
    : 0
  const selectorTop = createIntent
    ? Math.min(Math.max(16, createIntent.point.y * snapshot.viewport.zoom + snapshot.viewport.y), Math.max(16, viewportSize.height - SELECTOR_HEIGHT - 16))
    : 0
  const gridFade = Math.min(1, Math.max(0, (snapshot.viewport.zoom - 0.2) / 0.2))
  const gridStyle: GridStyle = {
    '--mf-grid-base': `${16 * snapshot.viewport.zoom}px`,
    // Preserve world-grid spacing while reducing low-zoom aliasing and fading in smoothly.
    '--mf-grid-dot-radius': `${Math.min(1, 2 * snapshot.viewport.zoom)}px`,
    '--mf-grid-dot-edge': `${Math.min(1.2, 2.4 * snapshot.viewport.zoom)}px`,
    '--mf-grid-dot-opacity': 0.08 * gridFade * gridFade * (3 - 2 * gridFade),
    '--mf-grid-x': `${snapshot.viewport.x}px`,
    '--mf-grid-y': `${snapshot.viewport.y}px`,
  }
  const connectTarget = connectTargetNodeId
    ? snapshot.nodes.find((node) => node.id === connectTargetNodeId)
    : undefined
  const incomingConnections = connectTarget
    ? snapshot.edges.filter((edge) => edge.target_node_uuid === connectTarget.id)
    : []
  const connectCandidates = connectTarget
    ? snapshot.nodes.flatMap((node) => {
      const output = selectedOutput(node)
      const duplicate = incomingConnections.some((edge) => edge.source_node_uuid === node.id)
      // Sources without a persisted output stay connectable: the edge is
      // created pending and binds on the source's first output.
      return node.id !== connectTarget.id
        && !duplicate
        && canConnectMediaModalities(node.modality, connectTarget.modality)
        && (connectTarget.modality === 'text' || connectTarget.data.input_capabilities.input_types.includes(node.modality))
        ? [{ node, output }]
        : []
    })
    : []
  const connectNodes = (source: MediaCanvasNode, output: MediaCanvasNode['data']['outputs'][number] | undefined) => {
    if (!connectTarget || readOnly) return
    const result = output
      ? controller.createConnection({
        sourceNodeId: source.id,
        targetNodeId: connectTarget.id,
        sourcePort: 'output',
        targetPort: 'input',
        // The full stored output keeps the controller's same-facts gate green.
        sourceOutput: output,
      })
      : controller.createPendingConnection({ sourceNodeId: source.id, targetNodeId: connectTarget.id })
    telemetry?.capture('media_edge_created', {
      source_type: source.modality,
      target_type: connectTarget.modality,
      accepted: result.ok,
    })
    if (!result.ok) return
    clearSelectionAfterConnection()
    closeConnectionDialog()
    redraw()
  }

  const filteredElements = snapshot.nodes.filter((node) => elementFilter === 'all' || node.modality === elementFilter)
  const elementGroupIds = new Set(snapshot.groups.map((group) => group.uuid))
  const groupedElements = new Map<string, MediaCanvasNode[]>()
  for (const node of filteredElements) {
    if (!node.group_uuid || !elementGroupIds.has(node.group_uuid)) continue
    const members = groupedElements.get(node.group_uuid)
    if (members) members.push(node)
    else groupedElements.set(node.group_uuid, [node])
  }
  const ungroupedElements = filteredElements.filter((node) => !node.group_uuid || !elementGroupIds.has(node.group_uuid))
  const visibleElementGroups = snapshot.groups.filter((group) => elementFilter === 'all' || groupedElements.has(group.uuid))
  const renderElement = (node: MediaCanvasNode) => {
    const component = componentSurfaces.displayOptions.find((option) => option.modality === node.modality) ?? NODE_OPTIONS.find((option) => option.modality === node.modality)!
    const type = component.label
    const status = runtimeStatusLabel(controller.getRuntime(node.id))
    return <MediaButton
      key={node.id}
      type="button"
      className="mf-workspace__element"
      data-mf-element-node={true}
      data-mf-node-id={node.id}
      aria-label={referenceTarget ? `将${node.name}添加为参考` : `定位${node.name}`}
      aria-pressed={selectedIds.has(node.id)}
      title={`${node.name} · ${type} · ${status}`}
      onClick={() => referenceTarget ? pickNodeReference(node.id) : focusNode(node)}
    ><ElementThumbnail modality={node.modality} icon={component.icon} sourceUrl={getElementPreviewUrl?.(node)} /><span className="mf-workspace__element-copy"><strong>{node.name}</strong></span></MediaButton>
  }
  const defaultPanelContent = snapshot.panel === 'elements'
    ? filteredElements.length === 0 && visibleElementGroups.length === 0
      ? <p className="mf-workspace__empty-panel" data-mf-elements-empty={true}>{snapshot.nodes.length === 0 ? '画布中暂无节点' : '暂无符合条件的画布元素'}</p>
      : <div className="flex min-w-0 flex-col gap-2">{visibleElementGroups.map((group) => {
        const members = groupedElements.get(group.uuid) ?? []
        const expanded = !collapsedElementGroups.has(group.uuid)
        return <section key={group.uuid} data-mf-element-group={group.uuid} className="min-w-0">
          <MediaElementGroupHeader group={group} expanded={expanded} editable={canMutate}
            onToggle={() => setCollapsedElementGroups((current) => { const next = new Set(current); if (expanded) next.add(group.uuid); else next.delete(group.uuid); return next })}
            onRename={(title) => { if (!canMutate) return false; const accepted = controller.updateGroup(group.uuid, { title }); redraw(); return accepted }} />
          <div id={`mf-element-group-${group.uuid}`} role="group" aria-label={`${group.title}中的节点`} hidden={!expanded}>
            {expanded ? <div className="mf-workspace__element-list mf-workspace__element-group-members">{members.length ? members.map(renderElement) : <p className="px-2 py-2 text-xs text-[#888]">暂无节点</p>}</div> : null}
          </div>
        </section>
      })}{ungroupedElements.length ? <div className="min-w-0">{visibleElementGroups.length ? <p className="px-2 pb-2 text-xs text-[#888]">未分组</p> : null}<div className="mf-workspace__element-list">{ungroupedElements.map(renderElement)}</div></div> : null}</div>
    : <p className="mf-workspace__empty-panel">{snapshot.panel === 'assets' ? '暂无可用资产' : '暂无画布历史'}</p>

  const selectedNode = snapshot.nodes.find((node) => node.id === primarySelectedNodeId)
  const dragSource = outputDragPreview
    ? snapshot.nodes.find((node) => node.id === outputDragPreview.sourceNodeId)
    : undefined
  return <section ref={workspaceRef} className="mf-workspace" style={{ '--mf-sidebar-width': `${sidebarWidth}px`, '--mf-workspace-width': `${viewportSize.width}px`, '--mf-workspace-height': `${viewportSize.height}px` } as CSSProperties} data-mf-sidebar-resizing={sidebarResizing} data-mf-sidebar-grid={sidebarWidth > MIN_SIDEBAR_WIDTH} data-mf-workspace={true} data-mf-sidebar-expanded={snapshot.panel !== 'none'} data-mf-read-only={readOnly} data-mf-reduced-motion={reducedMotion} data-mf-viewport-width={viewportSize.width} data-mf-viewport-height={viewportSize.height}>
    <WorkspaceStyles />
    <MediaNodeStyles />
    <MediaCanvasGroupStyles />
    <a className="mf-workspace__skip" href="#mf-project-tools">跳到项目工具</a>
    <a className="mf-workspace__skip" href={selectedNode ? '#mf-selected-node' : '#mf-media-canvas'}>跳到选中节点</a>
    <div
      ref={canvasRef}
      id="mf-media-canvas"
      className="mf-canvas"
      data-mf-canvas={true}
      data-mf-reference-selecting={Boolean(referenceTarget)}
      data-mf-interaction-mode={preferences.interactionMode}
      data-mf-zoom-tier={zoomTier}
      data-mf-grid-visible={gridVisible && snapshot.viewport.zoom > 0.2}
      data-mf-connections-visible={connectionsVisible}
      data-mf-panning={panning}
      data-mf-space-pan={spacePanActive}
      data-mf-selecting={Boolean(selectionBox)}
      data-mf-selection-chrome-hidden={selectionChromeHidden}
      data-mf-output-dragging={Boolean(outputDragPreview || pendingOutputDrag.current)}
      style={gridStyle}
      role="application"
      aria-label="AI媒体流无限画布"
      tabIndex={0}
      onClickCapture={(event) => {
        if ((event.target as Element).closest?.('[data-mf-reference-strip]')) return
        if (!referenceTarget || spacePanReady.current || !(event.target as Element).closest?.('[data-mf-workspace-node]')) return
        event.preventDefault()
        event.stopPropagation()
        const nodeId = (event.target as Element).closest('[data-mf-workspace-node]')?.getAttribute('data-mf-workspace-node')
        if (nodeId) pickNodeReference(nodeId)
      }}
      onDoubleClickCapture={(event) => {
        if (referenceTarget && !(event.target as Element).closest?.('[data-mf-reference-strip]')) { event.preventDefault(); event.stopPropagation() }
      }}
      onPointerDownCapture={(event) => {
        if ((event.target as Element).closest?.('[data-mf-reference-strip]')) return
        if (referenceTarget && event.button === 0 && !spacePanReady.current && (event.target as Element).closest?.('[data-mf-workspace-node]')) {
          event.preventDefault()
          event.stopPropagation()
          return
        }
        if (!(event.target as Element).closest?.('[data-mf-edge-hit]')) clearEdgeSelection()
        // Prompt panels stop bubbling to protect editing; hand-mode drags must start before that boundary.
        if (spacePanReady.current && event.button === 0 && !isCanvasShortcutProtectedTarget(event.target)) {
          handlePointerDown(event)
          event.stopPropagation()
        }
      }}
      onContextMenu={(event) => {
        if (suppressContextMenu.current || panGesture.current?.mode === 'right' && panGesture.current.moved) {
          event.preventDefault()
          suppressContextMenu.current = false
          return
        }
        const element = event.target as Element
        const nodeId = element.closest?.('[data-mf-workspace-node]')?.getAttribute('data-mf-workspace-node')
        // Media playback keeps keyboard protection, but node media uses the canvas context menu.
        const isNodeMedia = Boolean(nodeId) && ['video', 'audio'].includes(element.tagName?.toLowerCase())
        if (!isNodeMedia && isCanvasShortcutProtectedTarget(event.target)) return
        event.preventDefault()
        const groupId = element.closest?.('[data-mf-group]')?.getAttribute('data-mf-group')
        const current = controller.getSnapshot()
        if (nodeId && !current.selectedNodeIds.includes(nodeId)) controller.setObjectSelection([nodeId], [])
        else if (groupId && !current.selectedGroupIds.includes(groupId)) controller.setObjectSelection([], [groupId])
        const point = canvasPointFromPointer(event, current.viewport)
        lastPointerPoint.current = point
        setContextMenu({ point: screenPointFromPointer(event), canvasPoint: point, nodeId: nodeId ?? undefined })
        redraw()
      }}
      onPointerDown={handlePointerDown}
      onPointerEnter={(event) => { pointerOverCanvas.current = true; pointerOverBlankCanvas.current = event.target === event.currentTarget }}
      onPointerMove={handlePointerMove}
      onPointerLeave={() => { pointerOverCanvas.current = false; pointerOverBlankCanvas.current = false; if (!pendingOutputDrag.current) setPortHover(undefined) }}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onLostPointerCapture={handleLostPointerCapture}
      onDoubleClick={handleDoubleClick}
      onKeyDownCapture={(event) => {
        if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'g'
          || event.altKey || event.nativeEvent?.isComposing || event.defaultPrevented
          || !(event.target as Element).closest?.('[data-mf-context-menu]')) return
        // Menu buttons consume bubbling keys; handle the advertised group shortcut first.
        event.preventDefault()
        event.stopPropagation()
        if (!canMutate || event.repeat) return
        controller.handleKeyboardShortcut({ key: event.key, ctrlKey: event.ctrlKey, metaKey: event.metaKey, target: 'other', bounds: getNodeBounds() })
        setContextMenu(undefined)
        canvasRef.current?.focus?.({ preventScroll: true })
        redraw()
      }}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onBlur={handleBlur}
      onDragOver={(event) => {
        if (!Array.from(event.dataTransfer.types).includes('Files')) return
        event.preventDefault()
        event.dataTransfer.dropEffect = canMutate && onDropFiles ? 'copy' : 'none'
        setFileDropActive(Boolean(canMutate && onDropFiles))
      }}
      onDragLeave={(event) => {
        if (!event.relatedTarget || !event.currentTarget.contains(event.relatedTarget as Node)) { setFileDropActive(false); setFileDropTargetNodeId(undefined) }
      }}
      onDrop={(event) => {
        if (!Array.from(event.dataTransfer.types).includes('Files')) return
        event.preventDefault()
        event.stopPropagation()
        const capturedTargetNodeId = fileDropTargetNodeId
        const domTargetNodeId = (event.target as { closest?: (selector: string) => { getAttribute?: (name: string) => string | null } | null } | null | undefined)?.closest?.('[data-mf-workspace-node]')?.getAttribute?.('data-mf-workspace-node') ?? undefined
        setFileDropActive(false)
        setFileDropTargetNodeId(undefined)
        if (!canMutate || !onDropFiles) {
          showConnectionHint('当前无法上传文件，请确认编辑和上传权限')
          return
        }
        const files = Array.from(event.dataTransfer.files)
        const accepted = files.filter(isSupportedDropFile)
        if (accepted.length !== files.length || accepted.length === 0) showConnectionHint(DROP_FILE_HINT)
        if (accepted.length > 0) {
          // Node drag-over handlers stop propagation, and browsers may report
          // the canvas or a nested media element as the final drop target.
          // Keep the node captured during drag-over as the authoritative target.
          const targetNodeId = capturedTargetNodeId ?? domTargetNodeId
          onDropFiles(accepted, canvasPointFromPointer(event, controller.getSnapshot().viewport), targetNodeId)
        }
      }}
    >
      {contextMenu ? <MediaCanvasContextMenu point={contextMenu.point} viewportSize={viewportSize} minLeft={snapshot.panel === 'none' ? 8 : sidebarWidth + 8} onClose={() => { setContextMenu(undefined); canvasRef.current?.focus?.({ preventScroll: true }); redraw() }} items={[
        ...(contextAssetNode ? [{ label: '保存为资产', disabled: !canMutate || !onSaveNodeAsset, action: () => { onSaveNodeAsset?.(contextAssetNode.id) } }] : []),
        { label: '编组', shortcut: 'Ctrl/⌘ G', disabled: !canMutate || snapshot.selectedNodeIds.length + snapshot.selectedGroupIds.length < 2 || new Set([...snapshot.selectedNodeIds, ...snapshot.nodes.filter((node) => node.group_uuid && snapshot.selectedGroupIds.includes(node.group_uuid)).map((node) => node.id)]).size < 2, action: () => { controller.createGroup(getNodeBounds()) } },
        { label: '解散编组', disabled: !canMutate || snapshot.selectedGroupIds.length === 0, action: () => { controller.dissolveGroups(snapshot.selectedGroupIds) } },
        { label: '复制', shortcut: 'Ctrl/⌘ C', disabled: snapshot.selectedNodeIds.length + snapshot.selectedGroupIds.length === 0, action: () => { controller.copySelection() } },
        { label: '粘贴', shortcut: 'Ctrl/⌘ V', disabled: !canMutate, action: () => { controller.pasteSelection(contextMenu.canvasPoint) } },
        { label: '删除 / 解组', shortcut: 'Delete', disabled: !canMutate || snapshot.selectedNodeIds.length + snapshot.selectedGroupIds.length === 0, action: () => { controller.deleteSelection() } },
        { label: '撤销', shortcut: 'Ctrl/⌘ Z', disabled: !canMutate || controller.getHistoryInfo().undoDepth === 0, action: () => { controller.undo() } },
        { label: '重做', shortcut: 'Ctrl/⌘ Shift Z', disabled: !canMutate || controller.getHistoryInfo().redoDepth === 0, action: () => { controller.redo() } },
        { label: '全选', shortcut: 'Ctrl/⌘ A', disabled: snapshot.nodes.length + snapshot.groups.length === 0, action: () => { controller.setObjectSelection(snapshot.nodes.map((node) => node.id), snapshot.groups.map((group) => group.uuid)) } },
      ]} /> : null}
      {fileDropActive ? <div className="mf-canvas__drop-hint" role="status">松开鼠标，创建图片或音频节点</div> : null}
      <div className="mf-canvas__scene" style={{
        transform: `translate(${snapshot.viewport.x}px,${snapshot.viewport.y}px) scale(${snapshot.viewport.zoom})`,
      }}>
        {[...snapshot.groups].sort((a, b) => b.width * b.height - a.width * a.height).map((storedGroup) => {
          const group = groupResizePreview?.uuid === storedGroup.uuid ? groupResizePreview : groupDragPreview?.[storedGroup.uuid] ? { ...storedGroup, position: groupDragPreview[storedGroup.uuid] } : storedGroup
          return <MediaCanvasGroupFrame key={group.uuid} group={group} selected={snapshot.selectedGroupIds.includes(group.uuid)} editable={canMutate} zoom={snapshot.viewport.zoom}
            toolbarPosition={{ x: (Math.max(snapshot.panel === 'none' ? 8 : sidebarWidth + 8, Math.min(group.position.x * snapshot.viewport.zoom + snapshot.viewport.x, viewportSize.width - 280)) - snapshot.viewport.x) / snapshot.viewport.zoom - group.position.x,
              y: (Math.max(8, Math.min(group.position.y * snapshot.viewport.zoom + snapshot.viewport.y - (snapshot.viewport.zoom < 0.4 ? 70 : 48), viewportSize.height - (snapshot.viewport.zoom < 0.4 ? 326 : 304))) - snapshot.viewport.y) / snapshot.viewport.zoom - group.position.y }} cancellationKey={`${groupCancellation}:${preferences.interactionMode}`}
            onPointerDown={(event) => handleGroupPointerDown(event, storedGroup)}
            onSelectFocus={() => { if (!controller.getSnapshot().selectedGroupIds.includes(group.uuid)) { controller.setObjectSelection([], [group.uuid]); redraw() } }}
            onResizeStart={(event, handle) => handleGroupResizeStart(event, storedGroup, handle)}
            onResizeKeyboard={(handle, delta) => { if (canMutate) controller.updateGroup(group.uuid, resizeMediaCanvasGroup(storedGroup, handle, delta)); redraw() }}
            onRename={(title) => { if (canMutate) controller.updateGroup(group.uuid, { title }); redraw() }}
            onColor={(color) => { if (canMutate) controller.updateGroup(group.uuid, { color }); redraw() }}
            onLayout={(layout) => { if (canMutate) controller.layoutGroup(group.uuid, layout, getNodeBounds()); redraw() }}
            onDissolve={() => { if (canMutate) controller.dissolveGroups([group.uuid]); redraw() }} />
        })}
        {connectionsVisible ? <svg className="mf-workspace-edges" data-mf-edges={true} width="100%" height="100%" aria-hidden="true">
          {snapshot.edges.map((edge) => {
            const source = nodesById.get(edge.source_node_uuid)
            const target = nodesById.get(edge.target_node_uuid)
            if (!source || !target) return null
            const renderedSource = nodeDragPreview?.[source.id]
              ? { ...source, position: nodeDragPreview[source.id] }
              : source
            const renderedTarget = nodeDragPreview?.[target.id]
              ? { ...target, position: nodeDragPreview[target.id] }
              : target
            const sourceAnchor = getPortAnchor(renderedSource, 'output')
            const targetAnchor = getPortAnchor(renderedTarget, 'input')
            const path = mediaConnectionEdgePath(
              sourceAnchor ?? nodeBorderAnchor(renderedSource, 'output'),
              targetAnchor ?? nodeBorderAnchor(renderedTarget, 'input'),
            )
            const related = selectedIds.has(source.id) || selectedIds.has(target.id)
            const edgeInteractionDisabled = Boolean(outputDragPreview || pendingOutputDrag.current || panning || nodeDragPreview || selectionBox)
            return <g
              key={edge.id}
              className="mf-workspace-edge-group"
              data-mf-edge-hovered={canMutate && !edgeInteractionDisabled && hoveredEdgeId === edge.id}
              data-mf-edge-selected={canMutate && selectedEdgeId === edge.id}
              data-mf-edge-related={related}
            >
              <path className="mf-workspace-edge" data-mf-edge={edge.id} d={path} />
              {related && !reducedMotion ? <g
                data-mf-edge-meteor={edge.id}
                pointerEvents="none"
              >
                {/* Aligned strokes keep the fading tail on the curve, including bends and reversed edges. */}
                {Array.from({ length: 16 }, (_, index) => {
                  const progress = index / 15
                  const length = 10 * (1 - index / 16)
                  return <path
                    key={index}
                    className="mf-workspace-edge-meteor"
                    d={path}
                    stroke="#68B5FC"
                    strokeOpacity={0.2 + progress * 0.8}
                    strokeWidth={2 / snapshot.viewport.zoom}
                    strokeDasharray={`${length} ${36 - length}`}
                    strokeDashoffset={length}
                  />
                })}
              </g> : null}
              {canMutate ? <path
                className="mf-workspace-edge-hit"
                data-mf-edge-hit={edge.id}
                d={path}
                style={{
                  '--mf-workspace-cursor': selectedEdgeId === edge.id && !edgeInteractionDisabled ? EDGE_CUT_CURSOR : undefined,
                  cursor: selectedEdgeId === edge.id ? EDGE_CUT_CURSOR : 'pointer',
                  pointerEvents: edgeInteractionDisabled ? 'none' : undefined,
                } as CSSProperties}
                onPointerEnter={() => { if (!pendingOutputDrag.current && !panGesture.current) setHoveredEdgeId(edge.id) }}
                onPointerLeave={() => setHoveredEdgeId((current) => current === edge.id ? undefined : current)}
                onPointerDown={(event) => {
                  if (spacePanReady.current || pendingOutputDrag.current || edgeInteractionDisabled || event.button !== 0) return
                  event.preventDefault()
                  event.stopPropagation()
                  canvasRef.current?.focus({ preventScroll: true })
                }}
                onClick={(event) => {
                  if (spacePanReady.current || pendingOutputDrag.current || edgeInteractionDisabled || event.button !== 0) return
                  event.preventDefault()
                  event.stopPropagation()
                  if (selectedEdgeId === edge.id) deleteCanvasEdge(edge.id)
                  else setSelectedEdgeId(edge.id)
                }}
                onDoubleClick={(event) => { event.stopPropagation() }}
              /> : null}
            </g>
          })}
        </svg> : null}
        {outputDragPreview && dragSource ? <svg className="mf-workspace-edges" data-mf-connection-preview-layer={true} width="100%" height="100%" aria-hidden="true">
          <path
            className="mf-workspace-edge mf-workspace-edge--pending"
            data-mf-connection-preview={true}
            d={outputDragPreview.sourcePort === 'input'
              ? mediaConnectionEdgePath(outputDragPreview.point, getPortAnchor(dragSource, 'input') ?? nodeBorderAnchor(dragSource, 'input'))
              : mediaConnectionEdgePath(getPortAnchor(dragSource, 'output') ?? nodeBorderAnchor(dragSource, 'output'), outputDragPreview.point)}
          />
        </svg> : null}
        {snapshot.nodes.map((storedNode, nodeIndex) => {
          const node = nodeResize.preview?.id === storedNode.id ? { ...storedNode, ...nodeResize.preview } : storedNode
          const runtime = controller.getRuntime(node.id)
          const presentation: MediaCanvasNodePresentation = {
            component: componentSurfaces.displayOptions.find((option) => option.modality === node.modality),
            zoomTier,
            promptInputFixedScale: preferences.promptInputFixedScale,
            selectionChromeHidden,
            zoomAllowsInteraction,
            editable: canMutate,
            selected: selectedIds.has(node.id),
            editing: !selectionChromeHidden && snapshot.editingNodeId === node.id,
            fileDropHover: fileDropTargetNodeId === node.id,
            requestEdit: () => requestNodeEdit(node),
            closeEditor: () => closeNodeEditor(node.id),
            requestCenter: () => centerNode(node),
            locateReference: (sourceNodeId) => {
              const source = controller.getSnapshot().nodes.find((candidate) => candidate.id === sourceNodeId)
              if (!source) return
              controller.setEditingNode(undefined)
              onExitReferenceSelection?.()
              centerNode(source, controller.getSnapshot().panel === 'none' ? 0 : sidebarWidth)
              pendingNodeFocusId.current = source.id
            },
            ...(runtime ? { runtime } : {}),
          }
          const previewPosition = nodeDragPreview?.[node.id] ?? node.position
          const dragBounds = pendingOutputDrag.current?.cardBounds.get(node.id)
          const followsConnection = canMutate && !reducedMotion && outputDragPreview?.hoverNodeId === node.id && Boolean(dragBounds)
          const followAngle = (delta: number, size: number) => Math.max(-1, Math.min(1, delta / (size / 2))) * 18
          const nodeStyle: MediaCanvasNodeStyle = {
            '--mf-connect-rotate-x': `${followsConnection ? -followAngle(outputDragPreview!.point.y - dragBounds!.top - dragBounds!.height / 2, dragBounds!.height) : 0}deg`,
            '--mf-connect-rotate-y': `${followsConnection ? followAngle(outputDragPreview!.point.x - dragBounds!.left - dragBounds!.width / 2, dragBounds!.width) : 0}deg`,
            // Runtime card dimensions are CSS variables; static presentation stays in Tailwind.
            '--mf-resize-width': node.size ? `${node.size.width}px` : undefined,
            '--mf-resize-height': node.size ? `${node.size.height}px` : undefined,
            '--mf-node-wrapper-width': `${Math.max(NODE_WIDTH, node.size?.width ?? 0)}px`,
            width: Math.max(NODE_WIDTH, node.size?.width ?? 0),
            left: previewPosition.x,
            top: previewPosition.y,
            '--mf-node-content-width': `${nodeContentWidth(node)}px`,
            '--mf-node-port-y': `${nodePortY(node)}px`,
            '--mf-port-hit-size': `${PORT_SCREEN_HIT_SIZE / snapshot.viewport.zoom}px`,
            '--mf-canvas-inverse-zoom': 1 / snapshot.viewport.zoom,
            '--mf-canvas-layout-zoom': layoutZoom,
            '--mf-canvas-inverse-layout-zoom': 1 / layoutZoom,
            '--mf-canvas-inverse-transform-zoom': 1 / transformZoom,
          }
          // React 18 treats `inert` as a string-valued DOM attribute. An empty
          // value preserves native inert semantics without emitting a runtime
          // warning for the boolean form.
          const inertAttributes = zoomAllowsInteraction ? {} : { inert: '' as unknown as boolean }
          const connectAvailable = Boolean(outputDragPreview && dragSource && node.id !== dragSource.id
            && controller.validatePendingConnection(dragEndpoints(outputDragPreview!, node.id)).ok)
          const connectHover = connectAvailable && outputDragPreview?.hoverNodeId === node.id
          const cursorPort = connectHover ? (outputDragPreview?.sourcePort === 'input' ? 'output' : 'input') : undefined
          const portStyle = (port: 'input' | 'output'): CSSProperties => cursorPort === port && outputDragPreview
            ? {
              left: outputDragPreview.point.x - previewPosition.x - 10,
              right: 'auto',
              top: outputDragPreview.point.y - previewPosition.y,
            }
            : {
              transform: portHover?.nodeId === node.id && portHover.port === port ? `translate(${portHover.x}px, ${portHover.y}px)` : undefined,
              '--mf-port-offset-x': `${portHover?.nodeId === node.id && portHover.port === port ? portHover.x : 0}px`,
              '--mf-port-offset-y': `${portHover?.nodeId === node.id && portHover.port === port ? portHover.y : 0}px`,
            } as CSSProperties
          const interactionDescription = !zoomAllowsInteraction
            ? '当前缩放不可交互'
            : readOnly
              ? '只读，可查看结果'
              : '可编辑'
          return <article
            key={node.id}
            ref={(element) => {
              if (element) nodeRefs.current.set(node.id, element)
              else nodeRefs.current.delete(node.id)
            }}
            id={node.id === primarySelectedNodeId ? 'mf-selected-node' : undefined}
            className="mf-workspace-node [&_.mf-workspace-node__card]:h-[var(--mf-resize-height)] [&_.mf-workspace-node__card]:w-[var(--mf-resize-width)] [&_.mf-workspace-node__card]:mx-auto"
            data-mf-workspace-node={node.id}
            data-mf-node-modality={node.modality}
            data-mf-reference-candidate={referenceTarget ? controller.validatePendingConnection({ sourceNodeId: node.id, targetNodeId: referenceTarget.id }).ok : undefined}
            onDoubleClick={(event) => {
              if (node.modality !== 'text' || isCanvasShortcutProtectedTarget(event.target)) return
              event.stopPropagation()
              const shell = event.currentTarget.querySelector('[data-mf-node-shell="text"]')
              if (shell && !(event.target as Element).closest?.('.mf-node__content-card') && event.target !== event.currentTarget) return
              if (controller.getSnapshot().editingNodeId !== node.id) {
                if (shell?.getAttribute('data-mf-text-editable') === 'false') return
                requestNodeEdit(node)
              }
            }}
            data-mf-node-editable={canMutate}
            data-mf-node-mutable={canMutate}
            data-mf-node-zoom-interactive={zoomAllowsInteraction}
            data-mf-node-zoom-tier={zoomTier}
            data-mf-port-screen-hit={PORT_SCREEN_HIT_SIZE}
            data-mf-node-resizing={nodeResize.preview?.id === node.id || undefined}
            data-mf-node-dragging={Boolean(nodeDragPreview?.[node.id])}
            data-mf-node-editing={presentation.editing}
            data-mf-connect-follow={followsConnection}
            data-mf-connect-available={connectAvailable || undefined}
            data-mf-connect-hover={connectHover || undefined}
            data-selected={presentation.selected}
            role="group"
            tabIndex={0}
            aria-label={`${modalityLabel(node.modality)}节点，${node.name}，${runtimeStatusLabel(runtime)}，${presentation.selected ? '已选中' : '未选中'}，${interactionDescription}`}
            style={nodeStyle}
            onFocus={() => {
              if (referenceTarget) return
              if (!controller.getSnapshot().selectedNodeIds.includes(node.id)) {
                controller.setSelection([node.id])
                redraw()
              }
            }}
            onKeyDown={(event) => {
              if (referenceTarget && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault(); event.stopPropagation(); pickNodeReference(node.id)
              } else handleNodeKeyDown(event, node, nodeIndex)
            }}
            onPointerDown={(event) => handleNodePointerDown(event, node)}
            onPointerMove={handleNodePointerMove}
            onPointerUp={(event) => { finishNodeDrag(event, true) }}
            onPointerCancel={(event) => { finishNodeDrag(event, false) }}
            onLostPointerCapture={handleNodeLostPointerCapture}
            onDragEnter={(event) => {
              if (!Array.from(event.dataTransfer.types).includes('Files')) return
              const type = event.dataTransfer.items?.[0]?.type || event.dataTransfer.files?.[0]?.type || ''
              const kind = type.startsWith('video/') ? 'video' : type.startsWith('image/') ? 'image' : undefined
              if (kind === node.modality) { event.preventDefault(); event.stopPropagation(); setFileDropTargetNodeId(node.id) }
            }}
            onDragOver={(event) => {
              if (!Array.from(event.dataTransfer.types).includes('Files')) return
              const type = event.dataTransfer.items?.[0]?.type || event.dataTransfer.files?.[0]?.type || ''
              const kind = type.startsWith('video/') ? 'video' : type.startsWith('image/') ? 'image' : undefined
              if (kind === node.modality) { event.preventDefault(); event.stopPropagation(); setFileDropTargetNodeId(node.id) }
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget as Node)) setFileDropTargetNodeId((current) => current === node.id ? undefined : current)
            }}
          >
            {node.modality !== 'file' ? <button
              type="button"
              className="mf-workspace-port mf-workspace-port--input"
              data-mf-connect-cursor={cursorPort === 'input' || undefined}
              style={portStyle('input')}
              aria-label={`连接到${node.name}输入`}
              title={`连接到${node.name}输入`}
              aria-haspopup="dialog"
              disabled={!canMutate}
              onPointerDown={(event) => {
                if (!canMutate || spacePanReady.current || event.button !== 0) return
                event.preventDefault()
                event.stopPropagation()
                clearEdgeSelection()
                clearConnectionHint()
                inputDragMoved.current = false
                const captureTarget = event.currentTarget.closest('[data-mf-canvas]') as PointerCaptureTarget | null
                try {
                  captureTarget?.setPointerCapture?.(event.pointerId)
                } catch {
                  // Canvas pointerup also completes drags without pointer capture.
                }
                pendingOutputDrag.current = {
                  cardBounds: new Map(controller.getSnapshot().nodes.map((candidate) => [candidate.id, getCardBounds(candidate.id) ?? defaultNodeCardBounds(candidate)])),
                  sourceNodeId: node.id,
                  sourcePort: 'input',
                  pointerId: event.pointerId,
                  ...(captureTarget ? { captureTarget } : {}),
                }
              }}
              onClick={(event) => {
                if (inputDragMoved.current) { inputDragMoved.current = false; return }
                returnFocusRef.current = event.currentTarget
                setConnectTargetNodeId(node.id)
              }}
            ><ConnectionPortIcon /></button> : null}
            <div
              ref={(element) => {
                if (element) nodeEditorRefs.current.set(node.id, element)
                else nodeEditorRefs.current.delete(node.id)
              }}
              data-mf-node-interactive-content={node.id}
              aria-disabled={!zoomAllowsInteraction}
              aria-readonly={readOnly}
              {...inertAttributes}
            >
              {onSendToAgent ? <button type="button" className="mf-workspace-node__agent" aria-label={`发送${node.name}到 agents`} title="发送到 agents" onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); onSendToAgent(node.id) }}>发送到 agents</button> : null}
              {renderNode ? renderNode(node, presentation) : <DefaultWorkspaceNode node={node} presentation={presentation} onEdit={() => requestNodeEdit(node)} />}
            </div>
            <button
              type="button"
              className="mf-workspace-port mf-workspace-port--output"
              data-mf-connect-cursor={cursorPort === 'output' || undefined}
              style={portStyle('output')}
              aria-label={`从${node.name}输出拖拽连线`}
              title={`从${node.name}输出拖拽连线`}
              disabled={!canMutate}
              onPointerDown={(event) => {
                if (!canMutate || spacePanReady.current || event.button !== 0) return
                event.preventDefault()
                event.stopPropagation()
                clearEdgeSelection()
                clearConnectionHint()
                const captureTarget = event.currentTarget.closest('[data-mf-canvas]') as PointerCaptureTarget | null
                try {
                  captureTarget?.setPointerCapture?.(event.pointerId)
                } catch {
                  // Pointerup on the canvas still completes the gesture.
                }
                pendingOutputDrag.current = {
                  cardBounds: new Map(controller.getSnapshot().nodes.map((candidate) => [candidate.id, getCardBounds(candidate.id) ?? defaultNodeCardBounds(candidate)])),
                  sourceNodeId: node.id,
                  sourcePort: 'output',
                  pointerId: event.pointerId,
                  ...(captureTarget ? { captureTarget } : {}),
                }
              }}
            ><ConnectionPortIcon /></button>
            {canMutate && !referenceTarget && !selectionChromeHidden ? <MediaNodeResizeHandle name={node.name} zoom={snapshot.viewport.zoom} onPointerDown={(event) => {
              if (spacePanReady.current || panGesture.current || nodeDragGesture.current || groupResizeGesture.current || pendingOutputDrag.current || selectionBoxGesture.current) return
              const element = nodeRefs.current.get(node.id)
              if (element?.querySelector('[data-mf-image-edit]')) return
              cancelWheelFrame.current?.(); setPortHover(undefined)
              nodeResize.start(event, storedNode, getCardBounds(node.id) ?? defaultNodeCardBounds(node))
            }} /> : null}
            {!zoomAllowsInteraction ? <MediaButton type="button" className="mf-workspace-node__focus" aria-label={`聚焦${node.name}`} onClick={() => focusNode(node)}>聚焦并放大</MediaButton> : null}
          </article>
        })}
      </div>

      {multipleObjectsSelected && selectionBounds && !selectionBox ? <div
        className="mf-selection-frame"
        data-mf-selection-frame={true}
        data-mf-selection-dragging={Boolean(nodeDragPreview)}
        style={selectionBounds}
        role="group"
        aria-label={`已选择 ${snapshot.selectedNodeIds.length} 个节点、${snapshot.selectedGroupIds.length} 个编组，可拖动或右键编组`}
        tabIndex={0}
        onPointerDown={handleSelectionFramePointerDown}
        onDoubleClick={(event) => event.stopPropagation()}
      >
        {(['n', 'e', 's', 'w'] as const).map((side) => <span key={side} className={`mf-selection-frame__edge mf-selection-frame__edge--${side}`} />)}
        {(['nw', 'ne', 'se', 'sw'] as const).map((corner) => <span key={corner} className={`mf-selection-frame__corner mf-selection-frame__corner--${corner}`} />)}
      </div> : null}
      {selectionBox ? <div
        className="mf-selection-box"
        data-mf-selection-box={true}
        aria-hidden="true"
        style={selectionBox}
      /> : null}

      {snapshot.nodes.length === 0 ? <div className="mf-empty-state" data-mf-empty-state={true}>
        <p className="mf-empty-state__gesture"><span className="mf-empty-state__gesture-key"><CanvasToolIcon name="pointer" />双击</span>双击画布 自由生成节点</p>
        <div className="mf-empty-state__actions">{componentSurfaces.creatableOptions.map((option) => <MediaButton
          key={option.modality}
          type="button"
          className="mf-empty-state__action"
          data-mf-empty-create={true}
          data-mf-modality={option.modality}
          aria-label={`创建${option.label}节点`}
          disabled={readOnly && !onRestoreEditing}
          onClick={() => createAtCenter(option.modality)}
          title={option.description}
        ><MediaComponentIcon icon={option.icon} size={15} /><span>{option.label}</span></MediaButton>)}</div>
      </div> : null}

      <input
        ref={selectorUploadRef}
        type="file"
        hidden
        multiple
        accept={MEDIA_UPLOAD_ACCEPT}
        onChange={(event) => {
          const files = Array.from(event.currentTarget.files ?? [])
          const point = selectorUploadPoint.current
          event.currentTarget.value = ''
          selectorUploadPoint.current = undefined
          if (!point || files.length === 0 || readOnly || !onDropFiles) return
          const supported = files.filter(isSupportedDropFile)
          if (supported.length !== files.length) showConnectionHint(DROP_FILE_HINT)
          if (supported.length > 0) onDropFiles(supported, point)
        }}
      />
      {createIntent ? <MediaDropdown
        open
        onOpenChange={(open) => {
          if (!open) {
            setCreateIntent(undefined)
            returnFocusRef.current = undefined
          }
        }}
        placement="bottomLeft"
        destroyOnHidden
        popup={<div
        ref={selectorRef}
        className="mf-workspace-menu mf-node-selector"
        data-mf-node-selector={true}
        data-mf-intent-kind={createIntent.kind}
        data-mf-anchor-x={createIntent.point.x}
        data-mf-anchor-y={createIntent.point.y}
        data-mf-source-node-id={createIntent.kind === 'output-drag' || createIntent.kind === 'input-drag' ? createIntent.sourceNodeId : undefined}
        style={{ width: selectorWidth, maxHeight: Math.max(0, viewportSize.height - 32) }}
        role="menu"
        aria-label="选择节点类型"
        tabIndex={-1}
        onPointerDown={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
        onContextMenu={(event) => { event.preventDefault(); event.stopPropagation() }}
        onKeyDown={(event) => {
          event.stopPropagation()
          if (event.key === 'Tab') { closeCreateIntent(); return }
          handleSelectorKey(event)
        }}
      >
        <div className="mf-node-selector__heading" role="presentation">添加节点</div>
        <div className="mf-node-selector__group" role="group" aria-label="添加节点">
          {componentSurfaces.selectorOptions.map((option, index) => {
            const compatible = selectorOptionIsCompatible(option)
            return <button
              key={option.modality}
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="mf-workspace-menu__item mf-node-selector__option"
              data-mf-selector-index={index}
              data-selected={selectorIndex === index}
              aria-label={`选择${option.label}节点`}
              disabled={!compatible}
              title={compatible ? undefined : '不支持该参考类型'}
              onMouseEnter={() => { if (compatible) setSelectorIndex(index) }}
              onMouseLeave={() => setSelectorIndex(-1)}
              onFocus={() => setSelectorIndex(index)}
              onBlur={() => setSelectorIndex(-1)}
              onClick={() => createFromSelector(option.modality)}
            >
              <span className="mf-node-selector__icon"><MediaComponentIcon icon={option.icon} /></span>
              <span className="mf-node-selector__copy"><strong>{option.label}</strong><small>{option.description}</small></span>
            </button>
          })}
        </div>
        <div className="mf-node-selector__heading" role="presentation">添加资源</div>
        <div className="mf-node-selector__group" role="group" aria-label="添加资源">
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className="mf-workspace-menu__item mf-node-selector__option"
            data-mf-selector-index={selectorUploadIndex}
            data-selected={selectorIndex === selectorUploadIndex}
            disabled={!selectorUploadEnabled}
            title={selectorUploadEnabled ? undefined : '当前无法上传资源'}
            onMouseEnter={() => { if (selectorUploadEnabled) setSelectorIndex(selectorUploadIndex) }}
            onMouseLeave={() => setSelectorIndex(-1)}
            onFocus={() => setSelectorIndex(selectorUploadIndex)}
            onBlur={() => setSelectorIndex(-1)}
            onClick={openSelectorUpload}
          >
            <span className="mf-node-selector__icon"><MediaCreationIcon kind="upload" /></span>
            <span className="mf-node-selector__copy"><strong>上传</strong><small>上传图片、视频、音频或 PDF</small></span>
          </button>
        </div>
      </div>}
      >
        <span className="mf-node-selector__anchor" aria-hidden="true" style={{ left: selectorLeft, top: selectorTop }} />
      </MediaDropdown> : null}

      {referenceTarget ? <div className="mf-reference-picker" data-mf-reference-picker role="region" aria-label="选择参考素材" onPointerDown={(event) => event.stopPropagation()}>
        <span className="mf-reference-picker__icon" aria-hidden="true"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="m21 3-7 18-4-7-7-4 18-7Zm0 0L10 14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
        <span role="status">从画布或资产管理选择参考</span>
        <button type="button" onClick={() => { onExitReferenceSelection?.(); focusNode(referenceTarget); if (referenceTarget.modality !== 'text') requestNodeEdit(referenceTarget) }}>返回节点</button>
        <button type="button" aria-label="退出参考选择" onClick={onExitReferenceSelection}>×</button>
      </div> : null}
      {connectionHint ? <output
        className="mf-workspace__connection-hint"
        data-mf-connection-hint={true}
        aria-live="polite"
      >{connectionHint}</output> : null}
    </div>

    {connectTarget ? <MediaModal
      open
      title={`连接到“${connectTarget.name}”`}
      focusable={{ trap: true, focusTriggerAfterClose: false }}
      onCancel={closeConnectionDialog}
      width={480}
      footer={null}
      styles={{ body: { maxHeight: 'min(620px, calc(100dvh - 180px))', overflowY: 'auto' } }}
    >
      <div className="mf-connect-dialog" data-mf-connect-dialog={true}>
        <p>选择一个上游节点作为有序参考；尚无结果版本的节点会以待生成参考连接，生成后自动绑定。</p>
        <div className="mf-connect-dialog__section">
          <h3>可连接节点</h3>
          {connectCandidates.length > 0 ? connectCandidates.map(({ node, output }, index) => <MediaButton
            key={node.id}
            type="button"
            className="mf-connect-dialog__action"
            aria-label={`将${node.name}连接到${connectTarget.name}`}
            autoFocus={index === 0}
            onClick={() => connectNodes(node, output)}
          >{modalityLabel(node.modality)} · {node.name} · {output ? `版本 ${output.output_version}` : '待生成'}</MediaButton>) : <p>暂无兼容的上游节点。</p>}
        </div>
        {incomingConnections.length > 0 ? <div className="mf-connect-dialog__section">
          <h3>已有连接</h3>
          {incomingConnections.map((edge) => {
            const source = snapshot.nodes.find((node) => node.id === edge.source_node_uuid)
            if (!source) return null
            return <MediaButton
              key={edge.id}
              type="button"
              className="mf-connect-dialog__action mf-connect-dialog__action--delete"
              aria-label={`删除${source.name}到${connectTarget.name}的连接`}
              onClick={() => {
                controller.deleteEdge(edge.id)
                redraw()
              }}
            >删除 {source.name} → {connectTarget.name}</MediaButton>
          })}
        </div> : null}
        <div className="mf-connect-dialog__footer"><MediaButton type="button" autoFocus={connectCandidates.length === 0} onClick={closeConnectionDialog}>关闭</MediaButton></div>
      </div>
    </MediaModal> : null}

    <MediaWorkspaceTitle key={projectSwitcher?.currentProjectUuid} name={projectName} readOnly={readOnly} onRename={onRenameProject} onHome={projectSwitcher?.onReturnToHome} projectSwitcher={projectSwitcher} />
    {saveStatus ? <output className="mf-workspace__save-status" aria-label="保存状态" aria-live="polite">{saveStatus}</output> : null}

    {snapshot.panel !== 'none' ? <aside className="mf-workspace__sidebar" aria-label="工作区侧栏">
      <nav className="mf-workspace__sidebar-tabs" role="tablist" aria-label="画布、资产与历史">{PANEL_OPTIONS.map((option) => <MediaButton key={option.panel} type="button" variant="text" role="tab" aria-label={`${option.label}面板`} aria-selected={snapshot.panel === option.panel} aria-pressed={snapshot.panel === option.panel} onClick={() => { lastPanel.current = option.panel; controller.setPanel(option.panel); redraw() }}>{option.label}</MediaButton>)}</nav>
      <div id={`mf-workspace-panel-${snapshot.panel}`} className="mf-workspace__drawer" data-mf-workspace-drawer={true} aria-label={`${PANEL_OPTIONS.find((option) => option.panel === snapshot.panel)?.label}面板`} onKeyDown={(event) => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); togglePanel(snapshot.panel as Exclude<MediaCanvasPanel, 'none'>) } }}>
        <div className="mf-workspace__drawer-content">
        {snapshot.panel === 'elements' ? <>
          <header className="mf-workspace__element-toolbar">
            <h2>画布元素</h2>
            <MediaDropdown open={elementFilterOpen} onOpenChange={setElementFilterOpen} placement="bottomRight" destroyOnHidden menu={{
              'aria-label': '筛选画布元素', selectable: true, selectedKeys: [elementFilter],
              items: [{ key: 'all', label: '全部' }, ...NODE_OPTIONS.map((option) => ({ key: option.modality, label: option.label }))],
              onClick: ({ key }) => {
                if (key === 'all' || NODE_OPTIONS.some((option) => option.modality === key)) setElementFilter(key as MediaModality | 'all')
              },
            }}>
              <MediaButton type="button" variant="text" className="mf-workspace__element-filter" aria-label="筛选画布元素" aria-haspopup="menu" aria-expanded={elementFilterOpen}>
                {elementFilter === 'all' ? '全部' : NODE_OPTIONS.find((option) => option.modality === elementFilter)?.label}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="m2 3.5 3 3 3-3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </MediaButton>
            </MediaDropdown>
          </header>
        </> : null}
        {panelContent?.[snapshot.panel] ?? defaultPanelContent}
        </div>
      </div>
      <footer className="mf-workspace__sidebar-footer"><div className='mt-2'>共{snapshot.nodes.length}个节点</div></footer>
    </aside> : null}
    {snapshot.panel !== 'none' ? <div
      className="mf-workspace__sidebar-resize"
      role="separator"
      aria-label="调整侧栏宽度"
      aria-orientation="vertical"
      aria-controls={`mf-workspace-panel-${snapshot.panel}`}
      aria-valuemin={MIN_SIDEBAR_WIDTH}
      aria-valuemax={maxSidebarWidth}
      aria-valuenow={sidebarWidth}
      aria-valuetext={`${Math.round(sidebarWidth)}像素`}
      title="左右拖拽调整侧栏宽度"
      tabIndex={0}
      onPointerDown={handleSidebarResizeStart}
      onPointerMove={handleSidebarResizeMove}
      onPointerUp={(event) => {
        handleSidebarResizeMove(event)
        handleSidebarResizeEnd(event)
      }}
      onPointerCancel={handleSidebarResizeEnd}
      onLostPointerCapture={handleSidebarResizeEnd}
      onKeyDown={handleSidebarResizeKey}
    /> : null}
    {snapshot.panel !== 'none' ? <MediaButton ref={drawerCloseRef} type="button" variant="text" className="mf-workspace__sidebar-toggle" aria-label="收起资源面板" title="收起资源面板" aria-expanded={true} onClick={() => togglePanel(snapshot.panel as Exclude<MediaCanvasPanel, 'none'>)}>
      <SidebarIcon collapsed={false} />收起
    </MediaButton> : null}

    <nav id="mf-project-tools" className="mf-workspace__toolbar" aria-label="画布工具" tabIndex={-1}>
      {snapshot.panel === 'none' ? <CanvasToolTooltip label="元素、资产与历史"><MediaButton
        ref={resourcePanelTriggerRef}
        type="button"
        className="mf-workspace__tool"
        aria-label="打开资源面板"
        aria-controls={`mf-workspace-panel-${lastPanel.current}`}
        aria-expanded={false}
        onClick={() => togglePanel(lastPanel.current)}
      ><SidebarIcon collapsed={true} /></MediaButton></CanvasToolTooltip> : null}
      <span className="mf-workspace__divider" aria-hidden="true" />
      <CanvasToolTooltip label="显示网格"><MediaButton type="button" className="mf-workspace__tool" aria-label="显示网格" aria-pressed={gridVisible} onClick={() => setGridVisible((visible) => !visible)}><CanvasToolIcon name="grid" /></MediaButton></CanvasToolTooltip>
      <CanvasToolTooltip label="显示连线"><MediaButton type="button" className="mf-workspace__tool" aria-label="显示连线" aria-pressed={connectionsVisible} onClick={() => setConnectionsVisible((visible) => !visible)}><CanvasToolIcon name="connections" /></MediaButton></CanvasToolTooltip>
      <CanvasToolTooltip label="适配全部节点"><MediaButton type="button" className="mf-workspace__tool" aria-label="适配全部节点" onClick={() => { controller.setViewport(fitMediaCanvasViewport(snapshot.nodes, viewportSize, snapshot.groups)); redraw() }}><CanvasToolIcon name="fit" /></MediaButton></CanvasToolTooltip>
      <span className="mf-workspace__divider" aria-hidden="true" />
      <CanvasToolTooltip label="显示小地图"><MediaButton type="button" className="mf-workspace__tool" aria-label="显示小地图" aria-pressed={minimapVisible} onClick={() => setMinimapVisible((visible) => !visible)}><CanvasToolIcon name="minimap" /></MediaButton></CanvasToolTooltip>
      <CanvasToolTooltip label={preferences.interactionMode === 'infinite-canvas' ? '无限画布模式' : 'ComfyUI模式'}><MediaButton type="button" className="mf-workspace__tool mf-workspace__mode"
        data-mf-mode-toggle={true}
        aria-label={`当前${preferences.interactionMode === 'infinite-canvas' ? '无限画布' : 'ComfyUI'}模式，切换为${preferences.interactionMode === 'infinite-canvas' ? 'ComfyUI' : '无限画布'}模式`}
        onClick={() => {
          cancelGestures()
          setPreferences({ ...preferences, interactionMode: preferences.interactionMode === 'infinite-canvas' ? 'comfyui' : 'infinite-canvas' })
        }}>
        <CanvasToolIcon name={preferences.interactionMode} />
      </MediaButton></CanvasToolTooltip>
      <CanvasToolTooltip label="画布操作方式"><MediaButton type="button" className="mf-workspace__tool" aria-label="画布操作方式" aria-expanded={helpOpen} onClick={() => setHelpOpen(true)}>?</MediaButton></CanvasToolTooltip>
      <div className="mf-workspace__zoom-control">
        <input className="mf-workspace__zoom-slider" type="range" min={MIN_ZOOM} max={MAX_ZOOM} step={0.01} value={snapshot.viewport.zoom} aria-label="调整画布缩放" aria-valuetext={`${Math.round(snapshot.viewport.zoom * 100)}%`} onChange={(event) => setZoom(Number(event.currentTarget.value))} />
        <output className="mf-workspace__zoom-value" aria-label="当前缩放比例" aria-live="polite">{Math.round(snapshot.viewport.zoom * 100)}%</output>
      </div>
    </nav>

    {helpOpen ? <MediaModal open title="画布操作方式" width={560} styles={{ body: { maxHeight: 'min(620px, calc(100dvh - 180px))', overflowY: 'auto' } }} onCancel={() => setHelpOpen(false)} footer={<MediaButton type="button" onClick={() => setHelpOpen(false)}>关闭</MediaButton>}>
      <div className="mf-canvas-help" data-mf-canvas-help={true} onKeyDown={(event) => {
        if (event.key === 'Escape') { event.stopPropagation(); setHelpOpen(false) }
        else if (event.key !== 'Tab') event.stopPropagation()
      }}>
        <div className="mf-canvas-help__modes">
          <section className="mf-canvas-help__mode" data-active={preferences.interactionMode === 'infinite-canvas'}><h3>无限画布</h3><p>左键框选，滚轮平移，适合自由整理内容。</p></section>
          <section className="mf-canvas-help__mode" data-active={preferences.interactionMode === 'comfyui'}><h3>ComfyUI</h3><p>左键平移，滚轮缩放，适合节点流程操作。</p></section>
        </div>
        <div className="mf-canvas-help__guides">
          <section><h3>导航与选择</h3><ul>
            <li>空白处左键拖动：{preferences.interactionMode === 'infinite-canvas' ? '框选节点' : '平移画布'}</li>
            <li>Shift / Ctrl + 左键拖动：框选节点</li>
            <li>普通滚轮：{preferences.interactionMode === 'infinite-canvas' ? '平移画布' : '以光标为中心缩放'}</li>
            <li>Shift + 滚轮：横移；Ctrl / ⌘ + 滚轮或捏合：缩放</li>
            <li>中键、空格 + 左键、空白处右键拖动：平移</li>
            <li>Ctrl + 点击：增减选择；Ctrl / ⌘ + A：全选</li>
          </ul></section>
          <section><h3>节点与编组</h3><ul>
            <li>拖动选中节点：一起移动；Ctrl / ⌘ + G：编组</li>
            <li>拖动节点右下角：调整大小；图片和视频保持比例；Esc 取消</li>
            <li>拖动组框：移动成员；双击组框：重命名</li>
            <li>组框手柄：调整边界；组工具栏：配色、布局、解组</li>
            <li>Ctrl / ⌘ + C / V：复制 / 粘贴；Ctrl / ⌘ + Z：撤销；加 Shift：重做</li>
            <li>Delete / Backspace：删除节点、解散组框</li>
            <li>右键：编辑菜单；双击空白处：添加节点；缩放 10%–500%</li>
          </ul></section>
        </div>
        <label><input type="checkbox" checked={preferences.promptInputFixedScale} onChange={(event) => setPreferences({ ...preferences, promptInputFixedScale: event.target.checked })} />提示词面板保持固定大小</label>
      </div>
    </MediaModal> : null}

    {minimapVisible ? <MediaCanvasMiniMap
      nodes={snapshot.nodes.map((node) => nodeResize.preview?.id === node.id ? { ...node, ...nodeResize.preview } : nodeDragPreview?.[node.id] ? { ...node, position: nodeDragPreview[node.id] } : node)}
      groups={snapshot.groups.map((group) => groupResizePreview?.uuid === group.uuid ? groupResizePreview : groupDragPreview?.[group.uuid] ? { ...group, position: groupDragPreview[group.uuid] } : group)}
      viewport={snapshot.viewport}
      viewportSize={viewportSize}
      nodeElements={nodeRefs.current}
      fallbackBounds={defaultNodeCardBounds}
      onViewportChange={(viewport) => { controller.setViewport(viewport); redraw() }}
    /> : null}
  </section>
}
